import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { Spinner, Loading, fmtDate, fmtMoney, Badge, PaymentBadge, CountrySelect, useToast, StatusMultiSelect, RushBadge, LazyImage, Empty } from '../components/UI';
import { printPackingSlip, openSlipWindow } from '../utils/packingSlip';
import OperationsTab from './OperationsTab';
import PSPCompaniesTab from './PSPCompaniesTab'; // Phase 12
import APIIntegrationsTab from './APIIntegrationsTab'; // Phase 12g
import ModelViewer from './ModelViewer';

// periodFrom/periodTo on invoices are DATEONLY fields — plain 'YYYY-MM-DD'
// strings with no time or timezone. The shared fmtDate() does
// new Date(d).toLocaleDateString(), which for a date-only string parses per
// spec as UTC midnight, then converts to the browser's local timezone —
// in any timezone behind UTC, that shifts the displayed date back by one
// day. This builds the Date with the local-time constructor instead
// (new Date(year, month, day) is unambiguous, no UTC round-trip), so it
// always displays exactly the date that was entered.
const fmtDateOnly = (d) => {
  if (!d) return '';
  const [y, m, day] = String(d).split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// ── Rich text editor using contentEditable ───────────────────────────────────
function RichEditor({ value, onChange }) {
  const editorRef = useRef(null);
  const isComposing = useRef(false);

  // Set initial value only once on mount
  useEffect(() => {
    if (editorRef.current && value) {
      editorRef.current.innerHTML = value;
    }
  }, []);

  const exec = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
  };

  const handleInput = () => {
    if (!isComposing.current) onChange(editorRef.current?.innerHTML || '');
  };

  const tools = [
    { label: 'B',  title: 'Bold',          cmd: 'bold',           style: { fontWeight:'700' } },
    { label: 'I',  title: 'Italic',        cmd: 'italic',         style: { fontStyle:'italic' } },
    { label: 'U',  title: 'Underline',     cmd: 'underline',      style: { textDecoration:'underline' } },
    { label: 'H1', title: 'Heading 1',     cmd: 'formatBlock',    val: 'h2' },
    { label: 'H2', title: 'Heading 2',     cmd: 'formatBlock',    val: 'h3' },
    { label: '• ', title: 'Bullet list',   cmd: 'insertUnorderedList' },
    { label: '1.', title: 'Numbered list', cmd: 'insertOrderedList' },
    { label: '⇤',  title: 'Outdent',       cmd: 'outdent' },
    { label: '⇥',  title: 'Indent',        cmd: 'indent' },
    { label: '✕',  title: 'Clear format',  cmd: 'removeFormat' },
  ];

  return (
    <div style={{ border:'1px solid var(--border2)', borderRadius:'var(--radius)', overflow:'hidden' }}>
      {/* Toolbar */}
      <div style={{ display:'flex', gap:2, padding:'6px 8px', background:'var(--bg)', borderBottom:'1px solid var(--border2)', flexWrap:'wrap' }}>
        {tools.map(t => (
          <button key={t.cmd+t.label} type="button" title={t.title}
            onMouseDown={e => { e.preventDefault(); exec(t.cmd, t.val || null); }}
            style={{ padding:'3px 8px', border:'1px solid var(--border2)', borderRadius:4, background:'var(--surface)', cursor:'pointer', fontSize:12, fontWeight:500, ...t.style }}>
            {t.label}
          </button>
        ))}
      </div>
      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onCompositionStart={() => { isComposing.current = true; }}
        onCompositionEnd={() => { isComposing.current = false; handleInput(); }}
        style={{ minHeight:220, padding:'12px 14px', fontSize:13, lineHeight:1.8, outline:'none', color:'var(--text)' }}
      />
    </div>
  );
}
import {
  getAdminCompanies, createCompany, updateCompany, deleteCompany,
  getAdminGroups, createGroup, updateGroup, deleteGroup,
  getAdminProducts, createProduct, updateProduct, deleteProduct,
  getAdminStats, getRevenueTrend, getAdminOrders, getAdminOrder, adminLogin, adminUserMe, adminUserLogout,
  uploadProductImage, uploadLogo, uploadTaxExemptCert, updateOrderStatus, replacePhoto,
  cancelOrder, uncancelOrder, cancelOrderAdmin, refundOrder, dismissTaxFallback, dismissAllTaxFallback,
} from '../api';
import api from '../api';

// ── Admin login ───────────────────────────────────────────────────────────────
function AdminLogin({ onLogin }) {
  // Reset brand colors synchronously — prevents bleed from photographer portal
  document.documentElement.style.setProperty('--brand',        '#0077B6');
  document.documentElement.style.setProperty('--brand-dark',   '#005F92');
  document.documentElement.style.setProperty('--brand-light',  'rgba(0,119,182,0.1)');
  document.documentElement.style.setProperty('--brand-border', 'rgba(0,119,182,0.3)');

  const [step,     setStep]     = useState('login'); // login | twofa | forgot | forgot-sent | reset
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [code,     setCode]     = useState('');
  const [newPass,  setNewPass]  = useState('');
  const [newPass2, setNewPass2] = useState('');

  // Reset brand colors to admin defaults on mount
  useEffect(() => {
    document.documentElement.style.setProperty('--brand',        '#0077B6');
    document.documentElement.style.setProperty('--brand-dark',   '#005F92');
    document.documentElement.style.setProperty('--brand-light',  'rgba(0,119,182,0.1)');
    document.documentElement.style.setProperty('--brand-border', 'rgba(0,119,182,0.3)');
  }, []);
  const [method,   setMethod]   = useState(''); // totp | sms
  const [smsHint,  setSmsHint]  = useState('');
  const [error,    setError]    = useState('');
  const [info,     setInfo]     = useState('');
  const [loading,  setLoading]  = useState(false);

  // Read reset token from URL if present
  const resetToken = new URLSearchParams(window.location.search).get('token');
  useState(() => { if (resetToken) setStep('reset'); }, []);

  // Legacy seed login (ADMIN_PASSWORD) — hidden automatically once admin users exist
  const [showLegacy,    setShowLegacy]    = useState(false);
  const [legacyPw,      setLegacyPw]     = useState('');
  const [legacyAllowed, setLegacyAllowed] = useState(true); // assume allowed until checked

  useEffect(() => {
    import('../api').then(a => a.adminHasUsers()).then(({ data }) => {
      setLegacyAllowed(!data.hasUsers);
    }).catch(() => setLegacyAllowed(true));
  }, []);

  const handleLogin = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await import('../api').then(a => a.adminUserLogin(email, password));
      if (data.twoFactor) {
        setMethod(data.method);
        setSmsHint(data.hint || '');
        setStep('twofa');
      } else {
        onLogin();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid email or password');
    } finally { setLoading(false); }
  };

  const handle2FA = async () => {
    setLoading(true); setError('');
    try {
      await import('../api').then(a => a.adminUserVerify2FA(code));
      onLogin();
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid verification code');
    } finally { setLoading(false); }
  };

  const handleForgot = async () => {
    setLoading(true); setError('');
    try {
      await import('../api').then(a => a.adminForgotPassword(email));
      setStep('forgot-sent');
    } catch { setStep('forgot-sent'); }
    finally { setLoading(false); }
  };

  const handleReset = async () => {
    if (newPass !== newPass2) return setError('Passwords do not match');
    if (newPass.length < 8) return setError('Password must be at least 8 characters');
    setLoading(true); setError('');
    try {
      await import('../api').then(a => a.adminResetPassword(resetToken, newPass));
      setInfo('Password reset! You can now sign in.');
      setStep('login');
      window.history.replaceState({}, '', '/admin');
    } catch (err) {
      setError(err.response?.data?.error || 'Reset failed. Link may have expired.');
    } finally { setLoading(false); }
  };

  const handleLegacy = async () => {
    setLoading(true); setError('');
    try {
      await import('../api').then(a => a.adminLogin(legacyPw));
      onLogin();
    } catch { setError('Invalid legacy password'); }
    finally { setLoading(false); }
  };

  const card = (content) => (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--admin-bg)' }}>
      <div className="card" style={{ width:380, padding:32 }}>
        <div style={{ textAlign:'center', marginBottom:24 }}>
          <div style={{ fontSize:22, fontWeight:700, color:'var(--admin-nav)' }}>Admin Access</div>
          <div style={{ fontSize:13, color:'var(--text2)', marginTop:4 }}>Solidiphy Management Panel</div>
        </div>
        {info && <div style={{ background:'#d4edda', color:'#155724', padding:'10px 14px', borderRadius:6, fontSize:13, marginBottom:16 }}>{info}</div>}
        {error && <div style={{ color:'var(--danger)', fontSize:13, marginBottom:12, textAlign:'center' }}>{error}</div>}
        {content}
      </div>
    </div>
  );

  if (step === 'reset') return card(
    <>
      <div style={{ fontSize:15, fontWeight:600, marginBottom:16 }}>Set new password</div>
      <div className="field"><input className="input" type="password" placeholder="New password (min 8 chars)" value={newPass} onChange={e => { setNewPass(e.target.value); setError(''); }} /></div>
      <div className="field"><input className="input" type="password" placeholder="Confirm new password" value={newPass2} onChange={e => { setNewPass2(e.target.value); setError(''); }} /></div>
      <button className="btn btn-primary btn-full" disabled={loading} onClick={handleReset}>{loading ? <Spinner size={16}/> : 'Reset password'}</button>
    </>
  );

  if (step === 'forgot-sent') return card(
    <>
      <div style={{ textAlign:'center', fontSize:15, marginBottom:20 }}>✉️ If that email address has an account, a reset link has been sent. Check your inbox.</div>
      <button className="btn btn-full" onClick={() => setStep('login')}>Back to sign in</button>
    </>
  );

  if (step === 'forgot') return card(
    <>
      <div style={{ fontSize:15, fontWeight:600, marginBottom:16 }}>Reset password</div>
      <div className="field"><input className="input" type="email" placeholder="Your admin email" value={email} onChange={e => { setEmail(e.target.value); setError(''); }} /></div>
      <button className="btn btn-primary btn-full" disabled={loading} onClick={handleForgot}>{loading ? <Spinner size={16}/> : 'Send reset link'}</button>
      <button className="btn btn-full" style={{ marginTop:8 }} onClick={() => setStep('login')}>Back to sign in</button>
    </>
  );

  if (step === 'twofa') return card(
    <>
      <div style={{ fontSize:15, fontWeight:600, marginBottom:8 }}>Two-factor verification</div>
      <div style={{ fontSize:13, color:'var(--text2)', marginBottom:16 }}>
        {method === 'sms'
          ? `Enter the 6-digit code sent to the number ending in ${smsHint}`
          : 'Enter the 6-digit code from your authenticator app'}
      </div>
      <div className="field">
        <input className="input" type="text" inputMode="numeric" maxLength={6} placeholder="000000" value={code}
          onChange={e => { setCode(e.target.value.replace(/\D/,'')); setError(''); }}
          onKeyDown={e => e.key === 'Enter' && handle2FA()} autoFocus />
      </div>
      <button className="btn btn-primary btn-full" disabled={loading || code.length < 6} onClick={handle2FA}>{loading ? <Spinner size={16}/> : 'Verify'}</button>
      <button className="btn btn-full" style={{ marginTop:8 }} onClick={() => { setStep('login'); setCode(''); setError(''); }}>Back</button>
    </>
  );

  return card(
    <>
      <div className="field"><label className="label">Email</label>
        <input className="input" type="email" placeholder="admin@example.com" value={email}
          onChange={e => { setEmail(e.target.value); setError(''); setInfo(''); }}
          onKeyDown={e => e.key === 'Enter' && handleLogin()} />
      </div>
      <div className="field"><label className="label">Password</label>
        <input className="input" type="password" placeholder="••••••••" value={password}
          onChange={e => { setPassword(e.target.value); setError(''); setInfo(''); }}
          onKeyDown={e => e.key === 'Enter' && handleLogin()} />
      </div>
      <button className="btn btn-primary btn-full" disabled={loading} onClick={handleLogin}>{loading ? <Spinner size={16}/> : 'Sign in'}</button>
      <div style={{ textAlign:'center', marginTop:12 }}>
        <button className="btn-link" style={{ fontSize:13, color:'var(--text2)', background:'none', border:'none', cursor:'pointer', textDecoration:'underline' }}
          onClick={() => { setStep('forgot'); setError(''); }}>Forgot password?</button>
      </div>
      {legacyAllowed && (
        <div style={{ borderTop:'1px solid var(--border)', marginTop:20, paddingTop:16, textAlign:'center' }}>
          {!showLegacy
            ? <button className="btn-link" style={{ fontSize:12, color:'var(--text3)', background:'none', border:'none', cursor:'pointer' }}
                onClick={() => setShowLegacy(true)}>Use legacy admin password</button>
            : <>
                <div style={{ fontSize:12, color:'var(--text2)', marginBottom:8 }}>Legacy access — create an admin account after signing in</div>
                <input className="input" type="password" placeholder="Legacy password" value={legacyPw}
                  onChange={e => { setLegacyPw(e.target.value); setError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleLegacy()}
                  style={{ marginBottom:8 }} />
                <button className="btn btn-full" disabled={loading} onClick={handleLegacy}>{loading ? <Spinner size={16}/> : 'Sign in with legacy password'}</button>
              </>
          }
        </div>
      )}
    </>
  );
}

// ── Coupons Tab ──────────────────────────────────────────────────────────────
function CouponsTab({ companies, coupons, onRefresh, toast }) {
  const emptyCoupon = { code:'', type:'free_shipping', discountPercent:'', minimumOrder:'', effectiveDate:'', expirationDate:'', active:true, description:'', domesticOnly:false, companyIds:[] };
  const [form,    setForm]    = useState(emptyCoupon);
  const [editId,  setEditId]  = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [showForm,setShowForm]= useState(false);
  const [companiesListModal, setCompaniesListModal] = useState(null);

  const openCreate = () => { setEditId(null); setForm(emptyCoupon); setShowForm(true); };
  const openEdit   = (c) => {
    setEditId(c.id);
    setForm({
      code: c.code, type: c.type,
      discountPercent: c.discountPercent || '',
      minimumOrder:    c.minimumOrder    || '',
      effectiveDate:   c.effectiveDate   || '',
      expirationDate:  c.expirationDate  || '',
      active:          c.active,
      description:     c.description    || '',
      domesticOnly:    c.domesticOnly   || false,
      companyIds:      (c.companies || []).map(co => co.id),
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.code || !form.effectiveDate || !form.expirationDate) return alert('Code, effective date and expiry date are required');
    if (form.type === 'percent_discount' && (!form.discountPercent || form.discountPercent <= 0)) return alert('Discount % required for percent discount');
    setSaving(true);
    try {
      const apiModule = await import('../api');
      if (editId) await apiModule.updateCoupon(editId, form);
      else        await apiModule.createCoupon(form);
      setShowForm(false); setEditId(null); setForm(emptyCoupon);
      onRefresh();
    } catch (err) { alert(err.response?.data?.error || 'Error saving coupon'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this coupon?')) return;
    try { const apiModule = await import('../api'); await apiModule.deleteCoupon(id); onRefresh(); }
    catch (err) { alert('Failed to delete'); }
  };

  const today = new Date().toISOString().split('T')[0];

  const couponStatus = (c) => {
    if (!c.active) return { label:'Inactive', color:'var(--text3)' };
    if (c.expirationDate < today) return { label:'Expired', color:'var(--danger)' };
    if (c.effectiveDate > today) return { label:'Upcoming', color:'#F59E0B' };
    return { label:'Active', color:'var(--success)' };
  };

  return (
    <div style={{ maxWidth:1300 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <div style={{ fontSize:18, fontWeight:700 }}>Coupons</div>
          <div style={{ fontSize:13, color:'var(--text2)' }}>Manage discount and free shipping coupon codes</div>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>+ Create Coupon</button>
      </div>

      <div className="card" style={{ padding:0, overflowX:'auto' }}>
        {coupons.length === 0
          ? <div style={{ padding:32, textAlign:'center', color:'var(--text3)' }}>No coupons yet. Create one above.</div>
          : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Code</th><th>Type</th><th>Discount</th><th>Min Order</th>
                  <th>Effective</th><th>Expires</th><th>Geography</th><th>Companies</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {coupons.map(c => {
                  const st = couponStatus(c);
                  return (
                    <tr key={c.id} onClick={() => openEdit(c)} style={{ cursor:'pointer' }}>
                      <td style={{ fontWeight:600, fontFamily:'monospace' }}>{c.code}</td>
                      <td style={{ fontSize:12 }}>{c.type === 'free_shipping' ? '🚚 Free Shipping' : `💰 ${c.discountPercent}% Off`}</td>
                      <td style={{ fontSize:12 }}>{c.type === 'percent_discount' ? `${c.discountPercent}%` : 'Free shipping'}</td>
                      <td style={{ fontSize:12 }}>{parseFloat(c.minimumOrder) > 0 ? `$${parseFloat(c.minimumOrder).toFixed(2)}` : '—'}</td>
                      <td style={{ fontSize:12 }}>{c.effectiveDate}</td>
                      <td style={{ fontSize:12 }}>{c.expirationDate}</td>
                      <td style={{ fontSize:11 }}>
                        {c.domesticOnly
                          ? <span style={{ background:'#FEF3C7', color:'#92400E', padding:'2px 7px', borderRadius:10, fontSize:11, fontWeight:500 }}>🇺🇸🇨🇦 US & CA only</span>
                          : <span style={{ color:'var(--text3)' }}>Global</span>
                        }
                      </td>
                      <td style={{ fontSize:11 }}>
                        {!(c.companies?.length)
                          ? <span style={{ color:'var(--text3)' }}>None</span>
                          : <button className="btn btn-sm" style={{ fontSize:11 }}
                              onClick={() => setCompaniesListModal({ title: c.code, companies: c.companies })}>
                              {c.companies.length} {c.companies.length === 1 ? 'company' : 'companies'}
                            </button>
                        }
                      </td>
                      <td><span style={{ fontSize:11, color: st.color, fontWeight:600 }}>{st.label}</span></td>
                      <td>
                        <div style={{ display:'flex', gap:6 }}>
                          <button className="btn btn-sm" onClick={() => openEdit(c)}>Edit</button>
                          <button className="btn btn-danger btn-sm" onClick={() => handleDelete(c.id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )
        }
      </div>

      {/* Create / Edit modal */}
      {showForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div className="card" style={{ width:480, padding:28, maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ fontSize:17, fontWeight:700, marginBottom:20 }}>{editId ? 'Edit Coupon' : 'New Coupon'}</div>

            <div className="field"><label className="label">Code * (uppercase, no spaces)</label>
              <input className="input" value={form.code} style={{ textTransform:'uppercase' }}
                onChange={e => setForm(p => ({ ...p, code: e.target.value.toUpperCase().replace(/\s/g,'') }))} placeholder="e.g. SUMMER20" />
            </div>

            <div className="field"><label className="label">Type *</label>
              <select className="select" value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}>
                <option value="free_shipping">🚚 Free Shipping</option>
                <option value="percent_discount">💰 Percentage Discount</option>
              </select>
            </div>

            {form.type === 'percent_discount' && (
              <div className="field"><label className="label">Discount % *</label>
                <input className="input" type="number" min="1" max="100" step="0.01" value={form.discountPercent}
                  onChange={e => setForm(p => ({ ...p, discountPercent: e.target.value }))} placeholder="e.g. 20" />
              </div>
            )}

            <div className="field"><label className="label">Minimum Order Amount ($) — optional</label>
              <input className="input" type="number" min="0" step="0.01" value={form.minimumOrder}
                onChange={e => setForm(p => ({ ...p, minimumOrder: e.target.value }))} placeholder="0 = no minimum" />
            </div>

            <div className="grid2">
              <div className="field"><label className="label">Effective Date *</label>
                <input className="input" type="date" value={form.effectiveDate}
                  onChange={e => setForm(p => ({ ...p, effectiveDate: e.target.value }))} />
              </div>
              <div className="field"><label className="label">Expiration Date *</label>
                <input className="input" type="date" value={form.expirationDate}
                  onChange={e => setForm(p => ({ ...p, expirationDate: e.target.value }))} />
              </div>
            </div>

            <div className="field"><label className="label">Description (optional)</label>
              <input className="input" value={form.description}
                onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Internal note about this coupon" />
            </div>

            <div className="field">
              <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer' }}>
                <input type="checkbox" checked={form.domesticOnly}
                  onChange={e => setForm(p => ({ ...p, domesticOnly: e.target.checked }))} />
                <span style={{ fontSize:13 }}>
                  <strong>Domestic only</strong>
                  <span style={{ color:'var(--text2)', marginLeft:6, fontWeight:400 }}>Restrict to US and Canada orders only</span>
                </span>
              </label>
            </div>

            <div className="field">
              <label className="label">Assign to Companies</label>
              <div style={{ border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:8, maxHeight:150, overflowY:'auto' }}>
                {companies.map(c => (
                  <label key={c.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'4px 0', cursor:'pointer' }}>
                    <input type="checkbox" checked={form.companyIds.includes(c.id)}
                      onChange={e => setForm(p => ({
                        ...p,
                        companyIds: e.target.checked
                          ? [...p.companyIds, c.id]
                          : p.companyIds.filter(id => id !== c.id)
                      }))} />
                    <span style={{ fontSize:13 }}>{c.name}</span>
                  </label>
                ))}
              </div>
              <div style={{ fontSize:11, color:'var(--text3)', marginTop:4 }}>Leave unchecked to apply to no companies (inactive state)</div>
            </div>

            <div className="field" style={{ display:'flex', alignItems:'center', gap:8 }}>
              <input type="checkbox" id="coupon-active" checked={form.active} onChange={e => setForm(p => ({ ...p, active: e.target.checked }))} />
              <label htmlFor="coupon-active" style={{ fontSize:13, cursor:'pointer' }}>Active</label>
            </div>

            <div style={{ display:'flex', gap:10, marginTop:16 }}>
              <button className="btn btn-primary" disabled={saving} onClick={handleSave}>{saving ? <Spinner size={14}/> : (editId ? 'Save changes' : 'Create coupon')}</button>
              <button className="btn" onClick={() => { setShowForm(false); setEditId(null); setForm(emptyCoupon); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {/* Companies list modal */}
      {companiesListModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}
          onClick={() => setCompaniesListModal(null)}>
          <div className="card" style={{ width:380, padding:24, maxHeight:'70vh', display:'flex', flexDirection:'column' }} onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
              <div>
                <div style={{ fontWeight:700, fontSize:16 }}>Coupon: {companiesListModal.title}</div>
                <div style={{ fontSize:12, color:'var(--text2)' }}>{companiesListModal.companies.length} {companiesListModal.companies.length === 1 ? 'company' : 'companies'} assigned</div>
              </div>
              <button className="btn btn-sm" onClick={() => setCompaniesListModal(null)}>✕</button>
            </div>
            <div style={{ overflowY:'auto', flex:1 }}>
              {companiesListModal.companies.map(co => (
                <div key={co.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'1px solid var(--border)' }}>
                  <span style={{ fontSize:13 }}>{co.name}</span>
                  <button className="btn btn-danger btn-sm" style={{ fontSize:11 }} onClick={async () => {
                    if (!window.confirm(`Remove coupon "${companiesListModal.title}" from ${co.name}?`)) return;
                    try {
                      const apiModule = await import('../api');
                      const coupon = coupons.find(c => c.code === companiesListModal.title);
                      if (!coupon) return;
                      const newCompanyIds = coupon.companies.filter(c => c.id !== co.id).map(c => c.id);
                      await apiModule.updateCoupon(coupon.id, { companyIds: newCompanyIds });
                      setCompaniesListModal(p => ({ ...p, companies: p.companies.filter(c => c.id !== co.id) }));
                      onRefresh();
                      toast(`Removed from ${co.name}`, 'success');
                    } catch { toast('Failed to remove', 'error'); }
                  }}>Remove</button>
                </div>
              ))}
            </div>
            <button className="btn" style={{ marginTop:16 }} onClick={() => setCompaniesListModal(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
// ── Audit Trail Component ─────────────────────────────────────────────────────
function AuditTrail({ orderId, items = [] }) {
  const [logs,    setLogs]    = useState([]);
  const [open,    setOpen]    = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!open) return;
    setLoading(true);
    try {
      const { data } = await import('../api').then(a => a.getOrderAuditLog(orderId));
      setLogs(data);
    } catch { }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [open, orderId]);

  // Phase 9 — resolve itemId to a human-readable line item label instead of
  // showing the raw numeric ID (which tells you nothing about which item it is)
  const itemMap = Object.fromEntries(items.map(i => [i.id, i]));
  const itemLabel = (itemId) => {
    const item = itemMap[itemId];
    if (!item) return `Item #${itemId}`;
    return item.lineItemDescription || `${item.productName} (Qty ${item.quantity})`;
  };

  // Strip the raw numeric ID out of field names like "item_42_status" so the
  // resolved item label (shown separately) carries that information instead
  const formatField = (f) => f.replace(/^item_\d+_/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  return (
    <div className="card" style={{ marginBottom:16 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', cursor:'pointer' }}
        onClick={() => setOpen(o => !o)}>
        <div style={{ fontWeight:600 }}>Audit Trail</div>
        <span style={{ fontSize:12, color:'var(--text2)' }}>{open ? '▲ Hide' : '▼ Show'}</span>
      </div>
      {open && (
        <div style={{ marginTop:12 }}>
          {loading && <div style={{ fontSize:13, color:'var(--text2)' }}>Loading...</div>}
          {!loading && logs.length === 0 && <div style={{ fontSize:13, color:'var(--text3)' }}>No changes recorded yet.</div>}
          {!loading && logs.map(log => (
            <div key={log.id} style={{ borderBottom:'1px solid var(--border)', paddingBottom:8, marginBottom:8, fontSize:12 }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:2 }}>
                <span style={{ fontWeight:600 }}>
                  {formatField(log.field)}
                  {log.itemId != null && (
                    <span style={{ fontWeight:400, color:'var(--brand)', marginLeft:6, fontFamily:'monospace', fontSize:11 }}>
                      · {itemLabel(log.itemId)}
                    </span>
                  )}
                </span>
                <span style={{ color:'var(--text3)' }}>{new Date(log.changedAt).toLocaleString()} · {log.adminName}</span>
              </div>
              <div style={{ color:'var(--text2)' }}>
                <span style={{ textDecoration:'line-through', color:'var(--danger)' }}>{log.oldValue || '—'}</span>
                <span style={{ margin:'0 6px' }}>→</span>
                <span style={{ color:'var(--success)' }}>{log.newValue || '—'}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Line Items Tab ────────────────────────────────────────────────────────────
function LineItemsTab({ companies, groups, onOrderStatusChange }) {
  const [items,      setItems]      = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [filterCo,      setFilterCo]      = useState('');
  const [filterGrp,     setFilterGrp]     = useState('');
  const [filterSku,     setFilterSku]     = useState('');
  const [filterItemSt,  setFilterItemSt]  = useState('');
  const [filterOrderSt, setFilterOrderSt] = useState('');
  const [search,        setSearch]        = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await import('../api').then(a => a.getAdminLineItems({
        companyId: filterCo, groupId: filterGrp, sku: filterSku,
        itemStatus: filterItemSt, orderStatus: filterOrderSt, search,
      }));
      setItems(data);
    } catch { }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filterCo, filterGrp, filterSku, filterItemSt, filterOrderSt, search]);

  const filteredGroups = filterCo ? groups.filter(g => g.companyId === parseInt(filterCo)) : groups;

  // Get unique SKUs for filter dropdown
  const skus = [...new Set(items.map(i => i.sku).filter(Boolean))].sort();

  return (
    <div>
      <div style={{ fontSize:18, fontWeight:700, marginBottom:4 }}>Line Items</div>
      <div style={{ fontSize:13, color:'var(--text2)', marginBottom:16 }}>Summary of all line items across orders</div>

      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:16 }}>
        <input className="input" style={{ flex:1, minWidth:200 }} placeholder="Search SKU, ref #, description…"
          value={search} onChange={e => setSearch(e.target.value)} />
        <select className="select" style={{ width:160 }} value={filterCo} onChange={e => { setFilterCo(e.target.value); setFilterGrp(''); }}>
          <option value="">All companies</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className="select" style={{ width:160 }} value={filterGrp} onChange={e => setFilterGrp(e.target.value)}>
          <option value="">All groups</option>
          {filteredGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
        <select className="select" style={{ width:140 }} value={filterSku} onChange={e => setFilterSku(e.target.value)}>
          <option value="">All SKUs</option>
          {skus.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="select" style={{ width:160 }} value={filterItemSt} onChange={e => setFilterItemSt(e.target.value)}>
          <option value="">All item statuses</option>
          <option value="awaiting_photo">Awaiting Photo</option>
          <option value="in_process">In Process</option>
          <option value="mqc">Quality</option>
          <option value="ready_to_model">Ready to Model</option>
          <option value="sculpting">Sculpting</option>
          <option value="ready_to_print">Ready to Print</option>
          <option value="printing">Printing</option>
          <option value="finishing">Finishing</option>
          <option value="qc">QC</option>
          <option value="packing">Packing</option>
          <option value="shipped_to_solidiphy">Shipped to Solidiphy</option>
          <option value="received_accepted">Received &amp; Accepted</option>
          <option value="received_rejected">Received &amp; Rejected</option>
          <option value="ready_to_ship">Ready to Ship</option>
          <option value="shipped_to_customer">Shipped to Customer</option>
        </select>
        <select className="select" style={{ width:160 }} value={filterOrderSt} onChange={e => setFilterOrderSt(e.target.value)}>
          <option value="">All order statuses</option>
          <option value="awaiting_photo">Awaiting Photo</option>
          <option value="in_process">In Process</option>
          <option value="mqc">Quality</option>
          <option value="ready_to_model">Ready to Model</option>
          <option value="sculpting">Sculpting</option>
          <option value="ready_to_print">Ready to Print</option>
          <option value="printing">Printing</option>
          <option value="finishing">Finishing</option>
          <option value="qc">QC</option>
          <option value="packing">Packing</option>
          <option value="shipped_to_solidiphy">Shipped to Solidiphy</option>
          <option value="received_accepted">Received &amp; Accepted</option>
          <option value="received_rejected">Received &amp; Rejected</option>
          <option value="ready_to_ship">Ready to Ship</option>
          <option value="shipped_to_customer">Shipped to Customer</option>
        </select>
      </div>

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {loading
          ? <div style={{ padding:32, textAlign:'center', color:'var(--text3)' }}>Loading...</div>
          : items.length === 0
            ? <div style={{ padding:32, textAlign:'center', color:'var(--text3)' }}>No line items found</div>
            : (
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Line Item ID</th>
                    <th>Company / Group</th>
                    <th>Room #</th>
                    <th>SKU</th>
                    <th>Front Ref</th>
                    <th>Back Ref</th>
                    <th>Qty</th>
                    <th>Item Status</th>
                    <th>Order Status</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <tr key={item.id}>
                      <td style={{ fontWeight:500, fontSize:12 }}>{item.lineItemDescription || '—'}</td>
                      <td>
                        <div style={{ fontSize:12 }}>{item.order?.companyName || '—'}</div>
                        <div style={{ fontSize:11, color:'var(--text3)' }}>{item.order?.groupName || '—'}</div>
                      </td>
                      <td style={{ fontSize:12 }}>{item.order?.roomNumber || '—'}</td>
                      <td style={{ fontSize:12 }}>{item.sku || '—'}</td>
                      <td style={{ fontSize:12 }}>{item.referenceNumber || '—'}</td>
                      <td style={{ fontSize:12, color:'var(--text3)' }}>{item.referenceNumber2 || '—'}</td>
                      <td style={{ fontSize:12, textAlign:'center' }}>{item.quantity}</td>
                      <td>
                        <select className="select" style={{ fontSize:11, padding:'3px 6px', width:140 }}
                          value={item.itemStatus || 'awaiting_photo'}
                          onChange={async (e) => {
                            const newStatus = e.target.value;
                            // Phase 12f — reject on receipt needs a note (sent to the PSP)
                            let note;
                            if (newStatus === 'received_rejected') {
                              note = window.prompt('Reason for rejecting on receipt — this note is sent to the PSP:');
                              if (!note || !note.trim()) return;
                            }
                            const shown = newStatus === 'received_accepted' ? 'ready_to_ship' : newStatus; // accept → Ready to Ship
                            try {
                              const { updateItemStatus } = await import('../api');
                              const { data } = await updateItemStatus(item.orderId, item.id, newStatus, note);
                              const newOrderStatus = data.newOrderStatus;
                              // Update item status + order status badge for ALL items in same order
                              setItems(prev => prev.map(i => {
                                if (i.orderId !== item.orderId) return i;
                                return {
                                  ...i,
                                  itemStatus: i.id === item.id ? shown : i.itemStatus,
                                  order: newOrderStatus ? { ...i.order, status: newOrderStatus } : i.order,
                                };
                              }));
                              // Silently refresh orders list in background
                              if (newOrderStatus && onOrderStatusChange) onOrderStatusChange();
                            } catch (err) { alert('Failed to update: ' + (err.response?.data?.error || err.message)); }
                          }}>
                          <option value="awaiting_photo">Awaiting Photo</option>
                          <option value="in_process">In Process</option>
                          <option value="mqc">Quality</option>
                          <option value="ready_to_model">Ready to Model</option>
                          <option value="sculpting">Sculpting</option>
                          <option value="ready_to_print">Ready to Print</option>
                          <option value="printing">Printing</option>
                          <option value="finishing">Finishing</option>
                          <option value="qc">QC</option>
                          <option value="packing">Packing</option>
                          <option value="shipped_to_solidiphy">Shipped to Solidiphy</option>
                          <option value="received_accepted">Received &amp; Accepted</option>
                          <option value="received_rejected">Received &amp; Rejected</option>
                          <option value="ready_to_ship">Ready to Ship</option>
                          <option value="shipped_to_customer">Shipped to Customer</option>
                        </select>
                      </td>
                      <td><Badge status={item.order?.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
        }
      </div>
      <div style={{ fontSize:12, color:'var(--text3)', marginTop:8 }}>{items.length} line item{items.length !== 1 ? 's' : ''}</div>
    </div>
  );
}

function AdminUsersTab() {
  const [users,    setUsers]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [form,     setForm]     = useState({ name:'', email:'', password:'', twoFactorMethod:'none', phone:'' });
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');
  // 2FA setup
  const [show2FA,  setShow2FA]  = useState(false);
  const [tfaStep,  setTfaStep]  = useState('choose'); // choose | totp-qr | totp-verify | sms-send | sms-verify | done
  const [qrCode,   setQrCode]   = useState('');
  const [tfaCode,  setTfaCode]  = useState('');
  const [smsPhone, setSmsPhone] = useState('');
  const [tfaError, setTfaError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await import('../api').then(a => a.getAdminUsers());
      setUsers(data);
    } catch { }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name:'', email:'', password:'', twoFactorMethod:'none', phone:'' });
    setError('');
    setShowForm(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, password:'', twoFactorMethod: u.twoFactorMethod, phone: u.phone || '' });
    setError('');
    setShowForm(true);
  };

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      const api = await import('../api');
      if (editing) {
        const payload = { name: form.name, email: form.email, twoFactorMethod: form.twoFactorMethod, phone: form.phone };
        if (form.password) payload.password = form.password;
        await api.updateAdminUser(editing.id, payload);
      } else {
        await api.createAdminUser(form);
      }
      setShowForm(false);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleToggle = async (u) => {
    try {
      await import('../api').then(a => a.toggleAdminUser(u.id));
      await load();
    } catch { alert('Failed to update user'); }
  };

  // 2FA setup flow
  const start2FASetup = async (method) => {
    setTfaError(''); setTfaCode('');
    if (method === 'totp') {
      try {
        const { data } = await import('../api').then(a => a.getTotpSetup());
        setQrCode(data.qrCode);
        setTfaStep('totp-qr');
      } catch { setTfaError('Failed to generate QR code'); }
    } else {
      setTfaStep('sms-send');
    }
  };

  const verifyTotp = async () => {
    try {
      await import('../api').then(a => a.verifyTotpSetup(tfaCode));
      setTfaStep('done');
      await load();
    } catch { setTfaError('Invalid code — please try again'); }
  };

  const sendSmsSetup = async () => {
    try {
      await import('../api').then(a => a.setupSms2FA(smsPhone));
      setTfaStep('sms-verify');
    } catch (err) { setTfaError(err.response?.data?.error || 'Failed to send SMS'); }
  };

  const verifySms = async () => {
    try {
      await import('../api').then(a => a.verifySms2FASetup(tfaCode));
      setTfaStep('done');
      await load();
    } catch { setTfaError('Invalid or expired code'); }
  };

  const disable2FA = async () => {
    if (!window.confirm('Disable two-factor authentication?')) return;
    try {
      await import('../api').then(a => a.disable2FA());
      await load();
    } catch { alert('Failed to disable 2FA'); }
  };

  const methodBadge = (m) => {
    if (m === 'totp') return <span style={{ background:'#d4edda', color:'#155724', padding:'2px 8px', borderRadius:12, fontSize:11 }}>Authenticator App</span>;
    if (m === 'sms')  return <span style={{ background:'#cce5ff', color:'#004085', padding:'2px 8px', borderRadius:12, fontSize:11 }}>SMS</span>;
    return <span style={{ background:'#f8d7da', color:'#721c24', padding:'2px 8px', borderRadius:12, fontSize:11 }}>None</span>;
  };

  if (loading) return <div style={{ padding:32 }}><div className="spinner" /></div>;

  return (
    <div style={{ maxWidth:800 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <div style={{ fontSize:18, fontWeight:700 }}>Admin Users</div>
          <div style={{ fontSize:13, color:'var(--text2)' }}>Manage admin accounts and two-factor authentication</div>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>+ Add Admin User</button>
      </div>

      {/* User list */}
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr style={{ background:'var(--admin-bg)', borderBottom:'1px solid var(--border)' }}>
              <th style={{ padding:'10px 16px', textAlign:'left', fontSize:12, color:'var(--text2)', fontWeight:600 }}>NAME / EMAIL</th>
              <th style={{ padding:'10px 16px', textAlign:'left', fontSize:12, color:'var(--text2)', fontWeight:600 }}>2FA</th>
              <th style={{ padding:'10px 16px', textAlign:'left', fontSize:12, color:'var(--text2)', fontWeight:600 }}>STATUS</th>
              <th style={{ padding:'10px 16px', textAlign:'left', fontSize:12, color:'var(--text2)', fontWeight:600 }}>LAST LOGIN</th>
              <th style={{ padding:'10px 16px', textAlign:'left', fontSize:12, color:'var(--text2)', fontWeight:600 }}>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr><td colSpan={5} style={{ padding:24, textAlign:'center', color:'var(--text2)', fontSize:13 }}>No admin users yet. Add one above.</td></tr>
            )}
            {users.map((u, i) => (
              <tr key={u.id} style={{ borderBottom: i < users.length-1 ? '1px solid var(--border)' : 'none' }}>
                <td style={{ padding:'12px 16px' }}>
                  <div style={{ fontWeight:600, fontSize:14 }}>{u.name}</div>
                  <div style={{ fontSize:12, color:'var(--text2)' }}>{u.email}</div>
                </td>
                <td style={{ padding:'12px 16px' }}>{methodBadge(u.twoFactorMethod)}</td>
                <td style={{ padding:'12px 16px' }}>
                  <span style={{ background: u.isActive ? '#d4edda' : '#f8d7da', color: u.isActive ? '#155724' : '#721c24', padding:'2px 8px', borderRadius:12, fontSize:11 }}>
                    {u.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={{ padding:'12px 16px', fontSize:12, color:'var(--text2)' }}>
                  {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : 'Never'}
                </td>
                <td style={{ padding:'12px 16px' }}>
                  <div style={{ display:'flex', gap:6 }}>
                    <button className="btn btn-sm" onClick={() => openEdit(u)}>Edit</button>
                    <button className="btn btn-sm" onClick={() => handleToggle(u)}
                      style={{ color: u.isActive ? 'var(--danger)' : 'green' }}>
                      {u.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create / Edit form modal */}
      {showForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div className="card" style={{ width:440, padding:28, maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ fontSize:17, fontWeight:700, marginBottom:20 }}>{editing ? 'Edit Admin User' : 'New Admin User'}</div>
            {error && <div style={{ color:'var(--danger)', fontSize:13, marginBottom:12 }}>{error}</div>}
            <div className="field"><label className="label">Full Name *</label>
              <input className="input" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="Jane Smith" />
            </div>
            <div className="field"><label className="label">Email *</label>
              <input className="input" type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} placeholder="jane@example.com" />
            </div>
            <div className="field"><label className="label">{editing ? 'New Password (leave blank to keep current)' : 'Password *'}</label>
              <input className="input" type="password" value={form.password} onChange={e => setForm(p => ({...p, password: e.target.value}))} placeholder="Min 8 characters" />
            </div>
            <div style={{ borderTop:'1px solid var(--border)', marginTop:8, paddingTop:16, display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button className="btn" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={saving} onClick={handleSave}>{saving ? <Spinner size={14}/> : (editing ? 'Save changes' : 'Create user')}</button>
            </div>
          </div>
        </div>
      )}

      {/* 2FA Setup section — only for current logged-in user */}
      <div className="card" style={{ marginTop:24 }}>
        <div style={{ fontSize:15, fontWeight:700, marginBottom:4 }}>Your Two-Factor Authentication</div>
        <div style={{ fontSize:13, color:'var(--text2)', marginBottom:16 }}>Set up 2FA for your own account. You must be signed in to configure this.</div>

        {!show2FA ? (
          <div style={{ display:'flex', gap:10 }}>
            <button className="btn btn-primary" onClick={() => { setShow2FA(true); setTfaStep('choose'); setTfaError(''); }}>Set up 2FA</button>
            <button className="btn" style={{ color:'var(--danger)' }} onClick={disable2FA}>Disable 2FA</button>
          </div>
        ) : (
          <div style={{ maxWidth:380 }}>
            {tfaError && <div style={{ color:'var(--danger)', fontSize:13, marginBottom:12 }}>{tfaError}</div>}

            {tfaStep === 'choose' && (
              <>
                <div style={{ fontSize:14, marginBottom:14 }}>Choose your 2FA method:</div>
                <div style={{ display:'flex', gap:10 }}>
                  <button className="btn btn-primary" onClick={() => start2FASetup('totp')}>📱 Authenticator App</button>
                  <button className="btn" onClick={() => start2FASetup('sms')}>💬 SMS / Text Message</button>
                </div>
                <button className="btn" style={{ marginTop:10 }} onClick={() => setShow2FA(false)}>Cancel</button>
              </>
            )}

            {tfaStep === 'totp-qr' && (
              <>
                <div style={{ fontSize:14, marginBottom:12 }}>Scan this QR code with Google Authenticator, Authy, or any authenticator app:</div>
                {qrCode && <img src={qrCode} alt="QR Code" style={{ width:200, height:200, marginBottom:16 }} />}
                <div style={{ fontSize:13, color:'var(--text2)', marginBottom:12 }}>Then enter the 6-digit code from the app to confirm:</div>
                <input className="input" type="text" inputMode="numeric" maxLength={6} placeholder="000000" value={tfaCode}
                  onChange={e => { setTfaCode(e.target.value.replace(/\D/,'')); setTfaError(''); }} style={{ marginBottom:10 }} />
                <div style={{ display:'flex', gap:8 }}>
                  <button className="btn btn-primary" disabled={tfaCode.length < 6} onClick={verifyTotp}>Confirm</button>
                  <button className="btn" onClick={() => setTfaStep('choose')}>Back</button>
                </div>
              </>
            )}

            {tfaStep === 'sms-send' && (
              <>
                <div style={{ fontSize:14, marginBottom:12 }}>Enter your mobile number to receive verification codes:</div>
                <input className="input" type="tel" placeholder="+1 555 000 0000" value={smsPhone}
                  onChange={e => { setSmsPhone(e.target.value); setTfaError(''); }} style={{ marginBottom:10 }} />
                <div style={{ display:'flex', gap:8 }}>
                  <button className="btn btn-primary" disabled={!smsPhone} onClick={sendSmsSetup}>Send test code</button>
                  <button className="btn" onClick={() => setTfaStep('choose')}>Back</button>
                </div>
              </>
            )}

            {tfaStep === 'sms-verify' && (
              <>
                <div style={{ fontSize:14, marginBottom:12 }}>Enter the 6-digit code sent to {smsPhone}:</div>
                <input className="input" type="text" inputMode="numeric" maxLength={6} placeholder="000000" value={tfaCode}
                  onChange={e => { setTfaCode(e.target.value.replace(/\D/,'')); setTfaError(''); }} style={{ marginBottom:10 }} />
                <div style={{ display:'flex', gap:8 }}>
                  <button className="btn btn-primary" disabled={tfaCode.length < 6} onClick={verifySms}>Verify</button>
                  <button className="btn" onClick={() => setTfaStep('sms-send')}>Resend</button>
                </div>
              </>
            )}

            {tfaStep === 'done' && (
              <div style={{ color:'#155724', background:'#d4edda', padding:'12px 16px', borderRadius:8, fontSize:14 }}>
                ✅ Two-factor authentication is now active on your account.
                <button className="btn" style={{ marginLeft:12 }} onClick={() => setShow2FA(false)}>Done</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Sidebar Nav Item ──────────────────────────────────────────────────────────
function NavItem({ id, icon, label, tab, setTab, setEditingId, setSelOrder, setAssigningProduct }) {
  return (
    <button className={`admin-nav-item ${tab === id ? 'active' : ''}`}
      onClick={() => { setTab(id); sessionStorage.setItem('admin_tab', id); setEditingId(null); setSelOrder(null); setAssigningProduct(undefined); }}>
      <span>{icon}</span>
      <span>{label}</span>
    </button>
  );
}

// ── PSP Users Tab ─────────────────────────────────────────────────────────────
function PSPUsersTab({ toast }) {
  const emptyForm = { name:'', email:'', password:'', pspCompanyId:'' };
  const [users,    setUsers]    = useState([]);
  const [pspCompanies, setPspCompanies] = useState([]); // Phase 12
  const [loading,  setLoading]  = useState(true);
  const [form,     setForm]     = useState(emptyForm);
  const [editId,   setEditId]   = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [pwErrors, setPwErrors] = useState([]);

  const validatePw = (pw) => {
    const errs = [];
    if (!pw) return errs;
    if (pw.length < 8)                  errs.push('At least 8 characters');
    if (!/[A-Z]/.test(pw))              errs.push('At least one uppercase letter');
    if (!/[a-z]/.test(pw))              errs.push('At least one lowercase letter');
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pw)) errs.push('At least one special character');
    return errs;
  };

  const load = async () => {
    setLoading(true);
    try { const { data } = await import('../api').then(a => a.getPSPUsers()); setUsers(data); }
    catch { toast('Failed to load PSP users', 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    import('../api').then(a => a.getPSPCompanies()).then(r => setPspCompanies(r.data)).catch(() => {});
  }, []);

  const openCreate = () => { setEditId(null); setForm(emptyForm); setPwErrors([]); setShowForm(true); };
  const openEdit   = (u) => { setEditId(u.id); setForm({ name: u.name, email: u.email, password:'', pspCompanyId: u.pspCompanyId || '' }); setPwErrors([]); setShowForm(true); };

  const handleSave = async () => {
    if (!form.name || !form.email) return toast('Name and email required', 'error');
    if (!form.pspCompanyId) return toast('PSP company required — users without one see no items', 'error');
    if (!editId && !form.password) return toast('Password required for new user', 'error');
    if (form.password) {
      const errs = validatePw(form.password);
      if (errs.length) { setPwErrors(errs); return; }
    }
    setSaving(true);
    try {
      const apiModule = await import('../api');
      if (editId) await apiModule.updatePSPUser(editId, form);
      else        await apiModule.createPSPUser(form);
      toast(editId ? 'PSP user updated' : 'PSP user created', 'success');
      setShowForm(false); load();
    } catch (err) { toast(err.response?.data?.error || 'Save failed', 'error'); }
    finally { setSaving(false); }
  };

  const handleToggle = async (u) => {
    try {
      await import('../api').then(a => a.togglePSPUser(u.id));
      toast(`User ${u.isActive ? 'deactivated' : 'activated'}`, 'success');
      load();
    } catch { toast('Failed', 'error'); }
  };

  if (loading) return <div style={{ padding:40, textAlign:'center' }}><Spinner /></div>;

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
        <div style={{ fontSize:18, fontWeight:600, color:'var(--text)' }}>PSP Users</div>
        <button className="btn btn-primary btn-sm" onClick={openCreate}>+ Add User</button>
      </div>

      {showForm && (
        <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:10, padding:20, marginBottom:20 }}>
          <div style={{ fontSize:15, fontWeight:500, marginBottom:16 }}>{editId ? 'Edit PSP User' : 'New PSP User'}</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div className="field">
              <label className="label">Full name</label>
              <input className="input" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="Jane Smith" />
            </div>
            <div className="field">
              <label className="label">Email</label>
              <input className="input" type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} placeholder="jane@psp.com" />
            </div>
            <div className="field" style={{ gridColumn:'1/-1' }}>
              <label className="label">PSP company</label>
              <select className="input" value={form.pspCompanyId} onChange={e => setForm(p => ({...p, pspCompanyId: e.target.value}))}>
                <option value="">Select a PSP company…</option>
                {pspCompanies.map(c => <option key={c.id} value={c.id}>{c.name}{c.isActive ? '' : ' (inactive)'}</option>)}
              </select>
            </div>
            <div className="field" style={{ gridColumn:'1/-1' }}>
              <label className="label">{editId ? 'New password (leave blank to keep current)' : 'Password'}</label>
              <input className="input" type="password" value={form.password} onChange={e => { setForm(p => ({...p, password: e.target.value})); setPwErrors(validatePw(e.target.value)); }} placeholder="Min 8 chars, upper, lower, special" />
              {pwErrors.length > 0 && (
                <ul style={{ margin:'6px 0 0 16px', padding:0, fontSize:12, color:'var(--danger)' }}>
                  {pwErrors.map((e,i) => <li key={i}>{e}</li>)}
                </ul>
              )}
            </div>
          </div>
          <div style={{ display:'flex', gap:10, marginTop:16 }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            <button className="btn" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:10, overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr style={{ background:'var(--bg)' }}>
              <th style={{ padding:'10px 14px', textAlign:'left', fontWeight:500, color:'var(--text2)', fontSize:12, borderBottom:'1px solid var(--border)' }}>Name</th>
              <th style={{ padding:'10px 14px', textAlign:'left', fontWeight:500, color:'var(--text2)', fontSize:12, borderBottom:'1px solid var(--border)' }}>Email</th>
              <th style={{ padding:'10px 14px', textAlign:'left', fontWeight:500, color:'var(--text2)', fontSize:12, borderBottom:'1px solid var(--border)' }}>PSP company</th>
              <th style={{ padding:'10px 14px', textAlign:'left', fontWeight:500, color:'var(--text2)', fontSize:12, borderBottom:'1px solid var(--border)' }}>Last login</th>
              <th style={{ padding:'10px 14px', textAlign:'left', fontWeight:500, color:'var(--text2)', fontSize:12, borderBottom:'1px solid var(--border)' }}>Status</th>
              <th style={{ padding:'10px 14px', borderBottom:'1px solid var(--border)' }}></th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr><td colSpan={6} style={{ padding:32, textAlign:'center', color:'var(--text3)' }}>No PSP users yet</td></tr>
            ) : users.map(u => (
              <tr key={u.id} style={{ borderBottom:'1px solid var(--border)' }}>
                <td style={{ padding:'10px 14px', fontWeight:500, color:'var(--text)' }}>{u.name}</td>
                <td style={{ padding:'10px 14px', color:'var(--text2)' }}>{u.email}</td>
                <td style={{ padding:'10px 14px', color: u.pspCompany ? 'var(--text2)' : 'var(--danger)' }}>{u.pspCompany?.name || 'None'}</td>
                <td style={{ padding:'10px 14px', color:'var(--text2)' }}>{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : '—'}</td>
                <td style={{ padding:'10px 14px' }}>
                  <span style={{ padding:'2px 8px', borderRadius:10, fontSize:11, fontWeight:500,
                    background: u.isActive ? '#D1FAE5' : '#FEE2E2',
                    color: u.isActive ? '#065F46' : '#991B1B' }}>
                    {u.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={{ padding:'10px 14px', textAlign:'right' }}>
                  <div style={{ display:'flex', gap:6, justifyContent:'flex-end' }}>
                    <button className="btn btn-sm" onClick={() => openEdit(u)}>Edit</button>
                    <button className="btn btn-sm" onClick={() => handleToggle(u)}
                      style={{ background: u.isActive ? '#FEE2E2' : '#D1FAE5', color: u.isActive ? '#991B1B' : '#065F46', border:'none' }}>
                      {u.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Packing Slips Tab ─────────────────────────────────────────────────────────
function PackingSlipsTab({ toast }) {
  const [slips, setSlips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const load = () => {
    setLoading(true);
    import('../api').then(a => a.getAdminPackingSlips()).then(({ data }) => setSlips(data))
      .catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleDiscard = async (slipId) => {
    if (!window.confirm(`Discard packing slip ${slipId}? This will release all items so they can be re-assigned.`)) return;
    try {
      await import('../api').then(a => a.discardAdminPackingSlip(slipId));
      toast(`Packing slip ${slipId} discarded`, 'success');
      load();
    } catch (err) { toast(err.response?.data?.error || 'Failed', 'error'); }
  };

  const STATUS_COLOR = { printed: '#2E3A59', discarded: '#999' };

  if (loading) return <div style={{ padding:40, textAlign:'center' }}><Spinner /></div>;

  return (
    <div>
      <div style={{ fontSize:20, fontWeight:700, marginBottom:4 }}>Packing Slips</div>
      <div style={{ fontSize:13, color:'var(--text2)', marginBottom:20 }}>
        Slips printed by PSP portal users, and shipment slips created automatically when an API partner ships (one per shipment group).
      </div>
      {slips.length === 0 ? (
        <div style={{ padding:48, textAlign:'center', color:'var(--text3)' }}>No packing slips yet</div>
      ) : (
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background:'var(--bg)', borderBottom:'1px solid var(--border)' }}>
                <th style={{ padding:'10px 14px', textAlign:'left', fontSize:12, fontWeight:600, color:'var(--text2)' }}>Slip ID</th>
                <th style={{ padding:'10px 14px', textAlign:'left', fontSize:12, fontWeight:600, color:'var(--text2)' }}>Date</th>
                <th style={{ padding:'10px 14px', textAlign:'left', fontSize:12, fontWeight:600, color:'var(--text2)' }}>PSP</th>
                <th style={{ padding:'10px 14px', textAlign:'left', fontSize:12, fontWeight:600, color:'var(--text2)' }}>Shipment group</th>
                <th style={{ padding:'10px 14px', textAlign:'left', fontSize:12, fontWeight:600, color:'var(--text2)' }}>Source</th>
                <th style={{ padding:'10px 14px', textAlign:'center', fontSize:12, fontWeight:600, color:'var(--text2)' }}>Items</th>
                <th style={{ padding:'10px 14px', textAlign:'left', fontSize:12, fontWeight:600, color:'var(--text2)' }}>Status</th>
                <th style={{ padding:'10px 14px', textAlign:'right', fontSize:12, fontWeight:600, color:'var(--text2)' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {slips.map(slip => (
                <>
                  <tr key={slip.slipId} style={{ borderBottom:'1px solid var(--border)', cursor:'pointer' }}
                    onClick={() => setExpanded(p => ({ ...p, [slip.slipId]: !p[slip.slipId] }))}>
                    <td style={{ padding:'10px 14px', fontFamily:'monospace', fontWeight:600, fontSize:13 }}>
                      {expanded[slip.slipId] ? '▼' : '▶'} {slip.slipId}
                    </td>
                    <td style={{ padding:'10px 14px', fontSize:13 }}>{new Date(slip.createdAt).toLocaleDateString()}</td>
                    <td style={{ padding:'10px 14px', fontSize:13 }}>{slip.pspName || '—'}</td>
                    <td style={{ padding:'10px 14px', fontSize:13, fontFamily:'monospace' }}>{slip.shipmentGroupId || '—'}</td>
                    <td style={{ padding:'10px 14px', fontSize:12, color:'var(--text2)' }}>{slip.source === 'api' ? 'API shipment' : 'PSP portal'}</td>
                    <td style={{ padding:'10px 14px', fontSize:13, textAlign:'center' }}>{slip.itemCount}</td>
                    <td style={{ padding:'10px 14px' }}>
                      <span style={{ fontSize:11, fontWeight:600, color:'#fff', background: STATUS_COLOR[slip.status] || '#999', padding:'2px 8px', borderRadius:10 }}>
                        {slip.status}
                      </span>
                    </td>
                    <td style={{ padding:'10px 14px', textAlign:'right' }}>
                      {slip.status === 'printed' && slip.items?.length > 0 && (
                        <button className="btn btn-sm" style={{ marginRight:6 }}
                          onClick={e => {
                            e.stopPropagation();
                            // Phase 12e — fetch full print data (PSP shipment details) for a consistent slip
                            const win = openSlipWindow();
                            import('../api').then(a => a.getAdminSlipPrintData(slip.slipId))
                              .then(r => printPackingSlip(r.data.items, slip.slipId, slip.createdAt, { orderInfo: r.data.orderInfo, reprint: true, win }))
                              .catch(() => printPackingSlip(slip.items, slip.slipId, slip.createdAt, { reprint: true, win }));
                          }}>
                          <i className="ti ti-printer" style={{ marginRight:4 }} aria-hidden="true"></i>View/Print
                        </button>
                      )}
                      {slip.status === 'printed' && slip.source !== 'api' && (
                        <button className="btn btn-sm btn-danger" onClick={e => { e.stopPropagation(); handleDiscard(slip.slipId); }}>
                          Discard
                        </button>
                      )}
                    </td>
                  </tr>
                  {expanded[slip.slipId] && slip.items?.length > 0 && (
                    <tr key={`${slip.slipId}-items`}>
                      <td colSpan={8} style={{ padding:0, background:'var(--bg)' }}>
                        <table style={{ width:'100%', borderCollapse:'collapse' }}>
                          <thead>
                            <tr style={{ borderBottom:'1px solid var(--border)' }}>
                              <th style={{ padding:'6px 28px', textAlign:'left', fontSize:11, color:'var(--text2)' }}>Order #</th>
                              <th style={{ padding:'6px 14px', textAlign:'left', fontSize:11, color:'var(--text2)' }}>Item ID</th>
                              <th style={{ padding:'6px 14px', textAlign:'left', fontSize:11, color:'var(--text2)' }}>Product</th>
                              <th style={{ padding:'6px 14px', textAlign:'left', fontSize:11, color:'var(--text2)' }}>SKU</th>
                              <th style={{ padding:'6px 14px', textAlign:'center', fontSize:11, color:'var(--text2)' }}>Qty</th>
                              <th style={{ padding:'6px 14px', textAlign:'left', fontSize:11, color:'var(--text2)' }}>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {slip.items.map(item => (
                              <tr key={item.id} style={{ borderBottom:'0.5px solid var(--border)' }}>
                                <td style={{ padding:'6px 28px', fontSize:12, fontFamily:'monospace' }}>{item.order?.orderNumber}</td>
                                <td style={{ padding:'6px 14px', fontSize:12 }}>{item.lineItemDescription}</td>
                                <td style={{ padding:'6px 14px', fontSize:12 }}>{item.productName}</td>
                                <td style={{ padding:'6px 14px', fontSize:12 }}>{item.sku}</td>
                                <td style={{ padding:'6px 14px', fontSize:12, textAlign:'center' }}>{item.quantity}</td>
                                <td style={{ padding:'6px 14px', fontSize:12 }}>{item.itemStatus}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Meshy Tasks Tab (Phase 5) ─────────────────────────────────────────────────
function MeshyTasksTab({ toast }) {
  const [tasks,        setTasks]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [filter,       setFilter]       = useState('');
  const [retryTask,    setRetryTask]    = useState(null);
  const [retryType,    setRetryType]    = useState('scratch');
  const [retrySettings, setRetrySettings] = useState(null);
  const [retrying,     setRetrying]     = useState(false);
  const [viewerFiles,  setViewerFiles]  = useState(null); // files for 3D viewer
  const [viewerLoading, setViewerLoading] = useState(null); // taskId being loaded

  const STATUS_LABELS = {
    queued:      { label: 'Queued',      color: '#6B7280' },
    submitted:   { label: 'Submitted',   color: '#2563EB' },
    analyzing:   { label: 'Analyzing',   color: '#7C3AED' },
    resizing:    { label: 'Resizing',    color: '#0891B2' },
    retexturing: { label: 'Retexturing', color: '#DB2777' },
    succeeded:   { label: 'Succeeded',   color: '#059669' },
    failed:      { label: 'Failed',      color: '#DC2626' },
  };

  const DEFAULT_SETTINGS = {
    imageEnhancement: false, removeLighting: false, poseMode: '',
    useBackPhoto: true, figureSize: 0, hdTexture: false,
  };

  const load = () => {
    setLoading(true);
    import('../api').then(a => a.getAdminMeshyTasks(filter || undefined))
      .then(({ data }) => { setTasks(data.tasks || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(load, [filter]);

  const openRetry = (task) => {
    setRetryTask(task);
    setRetryType('scratch');
    setRetrySettings({
      imageEnhancement: task.settingImageEnhancement || false,
      removeLighting:   task.settingRemoveLighting   || false,
      poseMode:         task.settingPoseMode          || '',
      useBackPhoto:     task.settingUseBackPhoto      !== false,
      figureSize:       task.settingFigureSize        || 0,
      hdTexture:        task.settingHdTexture         || false,
    });
  };

  const submitRetry = async () => {
    if (!retryTask) return;
    setRetrying(true);
    try {
      await import('../api').then(a => a.retryAdminMeshyTask(retryTask.id, { type: retryType, ...retrySettings }));
      toast('Retry submitted', 'success');
      setRetryTask(null);
      load();
    } catch (err) {
      toast('Retry failed: ' + (err.response?.data?.error || err.message), 'error');
    } finally { setRetrying(false); }
  };

  const thS = { padding:'8px 10px', textAlign:'left', fontWeight:600, fontSize:12, color:'var(--text2)' };
  const tdS = { padding:'8px 10px', verticalAlign:'top', fontSize:13 };

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
        <div style={{ fontSize:20, fontWeight:700 }}>3D Tasks</div>
        <select value={filter} onChange={e => setFilter(e.target.value)}
          style={{ padding:'4px 8px', borderRadius:6, border:'1px solid var(--border)', fontSize:13 }}>
          <option value=''>All statuses</option>
          {Object.entries(STATUS_LABELS).map(([v, { label }]) => (
            <option key={v} value={v}>{label}</option>
          ))}
        </select>
        <button className='btn btn-sm' onClick={load}>Refresh</button>
      </div>

      {loading ? <div>Loading…</div> : tasks.length === 0 ? (
        <div style={{ color:'var(--text2)', padding:24 }}>No tasks found.</div>
      ) : (
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr style={{ background:'var(--admin-bg)', borderBottom:'2px solid var(--border)' }}>
              <th style={thS}>ID</th>
              <th style={thS}>Order #</th>
              <th style={thS}>Line Item</th>
              <th style={thS}>Att.</th>
              <th style={thS}>Status</th>
              <th style={thS}>Settings</th>
              <th style={thS}>Watertight</th>
              <th style={thS}>Submitted</th>
              <th style={thS}>Completed</th>
              <th style={thS}>Error / Note</th>
              <th style={thS}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map(t => {
              const cfg       = STATUS_LABELS[t.status] || { label: t.status, color: '#6B7280' };
              const isWarning = t.orderItem?.meshyWarning;
              return (
                <tr key={t.id} style={{ borderBottom:'1px solid var(--border)', background: isWarning ? '#FFFBEB' : undefined }}>
                  <td style={tdS}>{t.id}</td>
                  <td style={tdS}><span style={{ fontWeight:600 }}>{t.order?.orderNumber || '—'}</span></td>
                  <td style={tdS}>
                    <div style={{ fontSize:12 }}>{t.orderItem?.lineItemDescription || '—'}</div>
                    <div style={{ fontSize:11, color:'var(--text2)' }}>{t.orderItem?.productName}</div>
                  </td>
                  <td style={{ ...tdS, textAlign:'center' }}>{t.attemptNumber}</td>
                  <td style={tdS}>
                    <span style={{ background: cfg.color + '22', color: cfg.color, padding:'2px 8px', borderRadius:10, fontWeight:600, fontSize:11 }}>
                      {cfg.label}
                    </span>
                  </td>
                  <td style={{ ...tdS, fontSize:11, color:'var(--text2)' }}>
                    <div>{t.settingImageEnhancement ? '✓ Enhance' : '✗ Enhance'}</div>
                    <div>{t.settingRemoveLighting   ? '✓ Remove light' : '✗ Remove light'}</div>
                    <div>{t.settingHdTexture        ? '✓ HD texture'   : '✗ HD texture'}</div>
                    <div>{t.settingPoseMode ? `Pose: ${t.settingPoseMode}` : 'No pose'}</div>
                    <div>{t.settingUseBackPhoto && t.photoOneDrivePath2 ? '✓ Back photo' : '✗ Back photo'}</div>
                    <div>Size: {t.settingFigureSize > 0 ? `${t.settingFigureSize}"` : `${(t.orderItem?.sku || '').slice(-1) || '?'}"`}</div>
                  </td>
                  <td style={{ ...tdS, textAlign:'center' }}>
                    {t.watertight === null || t.watertight === undefined
                      ? <span style={{ color:'var(--text3)' }}>—</span>
                      : t.watertight
                        ? <span style={{ color:'#059669', fontWeight:700 }}>✓</span>
                        : <span style={{ color:'#DC2626', fontWeight:700 }}>✗{isWarning ? ' ⚠️' : ''}</span>
                    }
                  </td>
                  <td style={{ ...tdS, fontSize:11 }}>{t.submittedAt ? new Date(t.submittedAt).toLocaleString() : '—'}</td>
                  <td style={{ ...tdS, fontSize:11 }}>{t.completedAt ? new Date(t.completedAt).toLocaleString() : '—'}</td>
                  <td style={{ ...tdS, maxWidth:180, wordBreak:'break-word', fontSize:11, color: t.errorMessage ? 'var(--danger)' : 'inherit' }}>
                    {t.errorMessage || ''}
                  </td>
                  <td style={tdS}>
                    {['failed','succeeded'].includes(t.status) && (
                      <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <button className='btn btn-sm' onClick={() => openRetry(t)}>Retry</button>
                        {t.status === 'succeeded' && (
                          <button className='btn btn-sm'
                            disabled={viewerLoading === t.id}
                            onClick={async () => {
                              setViewerLoading(t.id);
                              try {
                                const r = await fetch(`/api/admin/orders/${t.orderId}/items/${t.orderItemId}/files`, { credentials:'include' });
                                const d = await r.json();
                                if (d.original?.length) setViewerFiles(d.original);
                                else toast('No model files found', 'error');
                              } catch { toast('Failed to load files', 'error'); }
                              finally { setViewerLoading(null); }
                            }}>
                            {viewerLoading === t.id ? '…' : '🔄 View 3D'}
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* ── 3D Viewer ── */}
      {viewerFiles && ReactDOM.createPortal(
        <ModelViewer files={viewerFiles} onClose={() => setViewerFiles(null)} />,
        document.body
      )}

      {/* ── Retry Modal ── */}
      {retryTask && retrySettings && (() => {
        // Determine what will run based on changed settings
        const genChanged = (
          retrySettings.imageEnhancement !== (retryTask.settingImageEnhancement || false) ||
          retrySettings.poseMode         !== (retryTask.settingPoseMode          || '')    ||
          retrySettings.useBackPhoto     !== (retryTask.settingUseBackPhoto      !== false)
        );
        const textureChanged = (
          retrySettings.hdTexture      !== (retryTask.settingHdTexture      || false) ||
          retrySettings.removeLighting !== (retryTask.settingRemoveLighting || false)
        );
        const sizeChanged = retrySettings.figureSize !== (retryTask.settingFigureSize || 0);

        const runType = !retryTask.meshyTaskId ? 'rerun'
          : genChanged ? 'rerun'
          : textureChanged && sizeChanged ? 'resize+retexture'
          : textureChanged ? 'retexture'
          : sizeChanged ? 'resize'
          : 'rerun';

        const RUN_LABELS = {
          'rerun':            { label: 'Rerun',             color: '#2563EB' },
          'retexture':        { label: 'Retexture',          color: '#DB2777' },
          'resize':           { label: 'Resize',             color: '#0891B2' },
          'resize+retexture': { label: 'Resize + Retexture', color: '#7C3AED' },
        };
        const cfg    = RUN_LABELS[runType];
        const apiType = runType === 'rerun' ? 'scratch' : runType;
        const set     = (key, val) => setRetrySettings(s => ({ ...s, [key]: val }));

        return (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
            <div style={{ background:'var(--surface)', borderRadius:12, padding:28, width:400, boxShadow:'0 8px 32px rgba(0,0,0,0.2)', maxHeight:'90vh', overflowY:'auto' }}>
              <div style={{ fontSize:17, fontWeight:700, marginBottom:4 }}>Retry Task #{retryTask.id}</div>
              <div style={{ fontSize:13, color:'var(--text2)', marginBottom:20 }}>
                {retryTask.order?.orderNumber} — {retryTask.orderItem?.lineItemDescription}
              </div>

              {/* Generation */}
              <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Generation</div>
              <label style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10, cursor:'pointer' }}>
                <input type='checkbox' checked={retrySettings.imageEnhancement} onChange={e => set('imageEnhancement', e.target.checked)} />
                <span style={{ fontSize:13 }}>Image Enhancement</span>
              </label>
              <label style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10,
                cursor: retryTask.photoOneDrivePath2 ? 'pointer' : 'not-allowed', opacity: retryTask.photoOneDrivePath2 ? 1 : 0.4 }}>
                <input type='checkbox' checked={retrySettings.useBackPhoto} disabled={!retryTask.photoOneDrivePath2}
                  onChange={e => set('useBackPhoto', e.target.checked)} />
                <span style={{ fontSize:13 }}>Use Back Photo {!retryTask.photoOneDrivePath2 ? '(not available)' : ''}</span>
              </label>
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:13, marginBottom:6 }}>Pose</div>
                <div style={{ display:'flex', gap:8 }}>
                  {['', 'a-pose', 't-pose'].map(p => (
                    <button key={p} onClick={() => set('poseMode', p)}
                      style={{ padding:'4px 12px', borderRadius:6, fontSize:12, cursor:'pointer', fontWeight:500,
                        background: retrySettings.poseMode === p ? 'var(--brand)' : 'var(--admin-bg)',
                        color:      retrySettings.poseMode === p ? '#fff' : 'var(--text)',
                        border:     `1px solid ${retrySettings.poseMode === p ? 'var(--brand)' : 'var(--border)'}` }}>
                      {p === '' ? 'None' : p}
                    </button>
                  ))}
                </div>
              </div>

              {/* Texture */}
              <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Texture</div>
              <label style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10, cursor:'pointer' }}>
                <input type='checkbox' checked={retrySettings.hdTexture} onChange={e => set('hdTexture', e.target.checked)} />
                <span style={{ fontSize:13 }}>HD Texture (4K)</span>
              </label>
              <label style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14, cursor:'pointer' }}>
                <input type='checkbox' checked={retrySettings.removeLighting} onChange={e => set('removeLighting', e.target.checked)} />
                <span style={{ fontSize:13 }}>Remove Lighting</span>
              </label>

              {/* Size */}
              <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Figure Size</div>
              <select value={retrySettings.figureSize} onChange={e => set('figureSize', parseInt(e.target.value))}
                style={{ padding:'6px 10px', borderRadius:6, border:'1px solid var(--border)', fontSize:13, width:'100%', marginBottom:20 }}>
                <option value={0}>SKU default</option>
                {[2,3,4,5,6,7].map(n => <option key={n} value={n}>{n}"</option>)}
              </select>

              {/* Reset + actions */}
              <button onClick={() => setRetrySettings({
                imageEnhancement: false, removeLighting: false, poseMode: '',
                useBackPhoto: true, figureSize: 0, hdTexture: false,
              })} style={{ fontSize:12, color:'var(--text2)', background:'none', border:'none', cursor:'pointer', textDecoration:'underline', marginBottom:16, padding:0, display:'block' }}>
                Reset to defaults
              </button>

              <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
                <button className='btn' onClick={() => setRetryTask(null)} disabled={retrying}>Cancel</button>
                <button className='btn btn-primary' disabled={retrying} onClick={async () => {
                  setRetrying(true);
                  try {
                    await import('../api').then(a => a.retryAdminMeshyTask(retryTask.id, { type: apiType, ...retrySettings }));
                    toast('Retry submitted', 'success');
                    setRetryTask(null);
                    load();
                  } catch (err) {
                    toast('Retry failed: ' + (err.response?.data?.error || err.message), 'error');
                  } finally { setRetrying(false); }
                }}>
                  {retrying ? 'Submitting…' : `Submit — ${cfg.label}`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ── Global Settings Tab (Terms & Privacy) ─────────────────────────────────────
function SettingsTab({ toast }) {
  const [termsText,   setTermsText]   = useState('');
  const [privacyText, setPrivacyText] = useState('');
  const [supportEmail,        setSupportEmail]        = useState('');
  const [defaultBrandColor,   setDefaultBrandColor]   = useState('');
  const [defaultBrandLogoUrl, setDefaultBrandLogoUrl] = useState('');
  const [companyLegalName, setCompanyLegalName] = useState('');
  const [companyAddress1,  setCompanyAddress1]  = useState('');
  const [companyAddress2,  setCompanyAddress2]  = useState('');
  const [companyCity,      setCompanyCity]      = useState('');
  const [companyState,     setCompanyState]     = useState('');
  const [companyZip,       setCompanyZip]       = useState('');
  const [companyCountry,   setCompanyCountry]   = useState('US');
  const [companyPhone,     setCompanyPhone]     = useState('');
  // Remit To bank details (line-item invoices) — loaded from an admin-only endpoint
  const [remitBankName,      setRemitBankName]      = useState('');
  const [remitRoutingNumber, setRemitRoutingNumber] = useState('');
  const [remitAccountNumber, setRemitAccountNumber] = useState('');
  const [remitAccountName,   setRemitAccountName]   = useState('');
  const [standardDomesticShippingRate,      setStandardDomesticShippingRate]      = useState('9.95');
  const [standardInternationalShippingRate, setStandardInternationalShippingRate] = useState('24.99');
  const [freeShippingInternationalRate,     setFreeShippingInternationalRate]     = useState('15.99');
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);

  useEffect(() => {
    import('../api').then(a => a.getGlobalSettings()).then(({ data }) => {
      setTermsText(data.termsText || '');
      setPrivacyText(data.privacyText || '');
      setSupportEmail(data.supportEmail || '');
      setDefaultBrandColor(data.defaultBrandColor || '');
      setDefaultBrandLogoUrl(data.defaultBrandLogoUrl || '');
      setCompanyLegalName(data.companyLegalName || '');
      setCompanyAddress1(data.companyAddress1 || '');
      setCompanyAddress2(data.companyAddress2 || '');
      setCompanyCity(data.companyCity || '');
      setCompanyState(data.companyState || '');
      setCompanyZip(data.companyZip || '');
      setCompanyCountry(data.companyCountry || 'US');
      setCompanyPhone(data.companyPhone || '');
      setStandardDomesticShippingRate(String(data.standardDomesticShippingRate ?? '9.95'));
      setStandardInternationalShippingRate(String(data.standardInternationalShippingRate ?? '24.99'));
      setFreeShippingInternationalRate(String(data.freeShippingInternationalRate ?? '15.99'));
    })
      .then(() => import('../api').then(a => a.getRemitSettings()))
      .then(({ data }) => {
        setRemitBankName(data.remitBankName || '');
        setRemitRoutingNumber(data.remitRoutingNumber || '');
        setRemitAccountNumber(data.remitAccountNumber || '');
        setRemitAccountName(data.remitAccountName || '');
      })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await import('../api').then(a => a.updateGlobalSettings({
        termsText, privacyText,
        supportEmail, defaultBrandColor, defaultBrandLogoUrl,
        companyLegalName, companyAddress1, companyAddress2, companyCity, companyState, companyZip, companyCountry, companyPhone,
        remitBankName, remitRoutingNumber, remitAccountNumber, remitAccountName,
        standardDomesticShippingRate, standardInternationalShippingRate, freeShippingInternationalRate,
      }));
      toast('Settings saved', 'success');
    } catch (err) {
      toast(err.response?.data?.error || 'Failed to save settings', 'error');
    } finally { setSaving(false); }
  };

  if (loading) return <div style={{ padding:40, textAlign:'center' }}><Spinner /></div>;

  return (
    <div>
      <div style={{ fontSize:20, fontWeight:700, marginBottom:4 }}>Settings</div>
      <div style={{ fontSize:13, color:'var(--text2)', marginBottom:20 }}>
        Global Terms &amp; Conditions and Privacy Statement, shown at <code>/terms</code> and <code>/privacy</code> for all companies.
      </div>
      <div className="card" style={{ marginBottom:16 }}>
        <div style={{ fontWeight:600, marginBottom:12 }}>General</div>
        <div className="field">
          <label className="label">Alert email</label>
          <div style={{ fontSize:11, color:'var(--text3)', marginBottom:6 }}>Single destination for all system alerts — 3D modeling failures, tax-exempt certificate expiry, and anything else that needs a human to look at it.</div>
          <input className="input" type="email" value={supportEmail} onChange={e => setSupportEmail(e.target.value)} placeholder="support@solidiphy.com" />
        </div>
        <div style={{ fontWeight:600, margin:'16px 0 8px' }}>Remit-to Address</div>
        <div style={{ fontSize:11, color:'var(--text3)', marginBottom:6 }}>Printed at the top of every partner invoice.</div>
        <div className="field"><label className="label">Legal entity name</label><input className="input" value={companyLegalName} onChange={e => setCompanyLegalName(e.target.value)} placeholder="Solidiphy Inc." /></div>
        <div className="field"><label className="label">Address line 1</label><input className="input" value={companyAddress1} onChange={e => setCompanyAddress1(e.target.value)} /></div>
        <div className="field"><label className="label">Address line 2</label><input className="input" value={companyAddress2} onChange={e => setCompanyAddress2(e.target.value)} /></div>
        <div className="grid2">
          <div className="field"><label className="label">City</label><input className="input" value={companyCity} onChange={e => setCompanyCity(e.target.value)} /></div>
          <div className="field"><label className="label">State</label><input className="input" value={companyState} onChange={e => setCompanyState(e.target.value)} /></div>
        </div>
        <div className="grid2">
          <div className="field"><label className="label">ZIP</label><input className="input" value={companyZip} onChange={e => setCompanyZip(e.target.value)} /></div>
          <div className="field"><label className="label">Country</label><input className="input" value={companyCountry} onChange={e => setCompanyCountry(e.target.value)} /></div>
        </div>
        <div className="field"><label className="label">Phone</label><input className="input" value={companyPhone} onChange={e => setCompanyPhone(e.target.value)} placeholder="Shown as Phone: on line-item invoices" /></div>
        <div style={{ fontWeight:600, fontSize:13, margin:'14px 0 4px' }}>Remit To — bank details</div>
        <div style={{ fontSize:11, color:'var(--text3)', marginBottom:8 }}>Printed in the Remit To box of line-item invoices. If left blank, the address above is shown instead.</div>
        <div className="grid2">
          <div className="field"><label className="label">Bank name</label><input className="input" value={remitBankName} onChange={e => setRemitBankName(e.target.value)} /></div>
          <div className="field"><label className="label">Account name</label><input className="input" value={remitAccountName} onChange={e => setRemitAccountName(e.target.value)} placeholder="e.g. Solidiphy Inc." /></div>
        </div>
        <div className="grid2">
          <div className="field"><label className="label">Routing #</label><input className="input" value={remitRoutingNumber} onChange={e => setRemitRoutingNumber(e.target.value)} /></div>
          <div className="field"><label className="label">Account #</label><input className="input" value={remitAccountNumber} onChange={e => setRemitAccountNumber(e.target.value)} /></div>
        </div>
        <div className="grid2">
          <div className="field">
            <label className="label">Solidiphy brand color</label>
            <div style={{ fontSize:11, color:'var(--text3)', marginBottom:6 }}>Shown as the fallback theme before any company-specific brand is loaded.</div>
            <input type="color" value={defaultBrandColor || '#0077B6'} onChange={e => setDefaultBrandColor(e.target.value)} style={{ width:60, height:40, padding:2, border:'1px solid var(--border)', borderRadius:'var(--radius)', cursor:'pointer' }} />
          </div>
          <div className="field">
            <label className="label">Solidiphy logo URL</label>
            <input className="input" value={defaultBrandLogoUrl} onChange={e => setDefaultBrandLogoUrl(e.target.value)} placeholder="https://..." />
          </div>
        </div>
      </div>
      <div className="card" style={{ marginBottom:16 }}>
        <div style={{ fontWeight:600, marginBottom:4 }}>Shipping Rates</div>
        <div style={{ fontSize:11, color:'var(--text3)', marginBottom:12 }}>
          Used by the photographer portal checkout screen. Companies with "Free shipping" checked (Companies tab) show $0.00 for domestic and use the free-shipping international rate below instead of the standard one.
        </div>
        <div className="grid2">
          <div className="field">
            <label className="label">Standard domestic (US/CA)</label>
            <input className="input" type="number" step="0.01" min="0" value={standardDomesticShippingRate} onChange={e => setStandardDomesticShippingRate(e.target.value)} />
          </div>
          <div className="field">
            <label className="label">Standard international</label>
            <input className="input" type="number" step="0.01" min="0" value={standardInternationalShippingRate} onChange={e => setStandardInternationalShippingRate(e.target.value)} />
          </div>
        </div>
        <div className="field">
          <label className="label">Free-shipping international rate</label>
          <div style={{ fontSize:11, color:'var(--text3)', marginBottom:6 }}>Charged instead of the standard international rate for companies with "Free shipping" enabled. Domestic is always $0.00 for these companies.</div>
          <input className="input" type="number" step="0.01" min="0" value={freeShippingInternationalRate} onChange={e => setFreeShippingInternationalRate(e.target.value)} />
        </div>
      </div>
      <div className="card" style={{ marginBottom:16 }}>
        <div className="field">
          <label className="label">Terms &amp; Conditions</label>
          <div style={{ fontSize:11, color:'var(--text3)', marginBottom:6 }}>Supports bold, italic, bullet points, headings — paste from Word or Google Docs.</div>
          <RichEditor value={termsText} onChange={setTermsText} />
        </div>
      </div>
      <div className="card" style={{ marginBottom:16 }}>
        <div className="field">
          <label className="label">Privacy Statement</label>
          <div style={{ fontSize:11, color:'var(--text3)', marginBottom:6 }}>Supports bold, italic, bullet points, headings — paste from Word or Google Docs.</div>
          <RichEditor value={privacyText} onChange={setPrivacyText} />
        </div>
      </div>
      <button className="btn btn-primary" disabled={saving} onClick={handleSave}>
        {saving ? <Spinner size={14} /> : 'Save Settings'}
      </button>
    </div>
  );
}

// ── Volume Discounts Tab (Phase 8) ────────────────────────────────────────────
// Precedence: companyId+sku > companyId+null ("apply to all SKUs") > global default.
// ── Partner Settlements Tab (Phase 8) ─────────────────────────────────────────
// Read-only — no editing here. Lets you actually see the revenue-share
// numbers that services/settlements.js computes, since nothing else in the
// app displays them. Can be revisited/expanded later (editing, marking
// paid, exporting, etc.) — this is just visibility for now.
function SettlementsTab({ companies, toast }) {
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companyFilter, setCompanyFilter] = useState('');
  const [payStatusFilter, setPayStatusFilter] = useState('');
  const [invoiceNumberFilter, setInvoiceNumberFilter] = useState('');
  const [orderFrom, setOrderFrom] = useState('');
  const [orderTo, setOrderTo] = useState('');
  const [shipFrom, setShipFrom] = useState('');
  const [shipTo, setShipTo] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [exporting, setExporting] = useState(false);

  const load = () => {
    setLoading(true);
    const params = {};
    if (companyFilter) params.companyId = companyFilter;
    if (payStatusFilter) params.payStatus = payStatusFilter;
    if (invoiceNumberFilter) params.invoiceNumber = invoiceNumberFilter;
    if (orderFrom) params.orderFrom = orderFrom;
    if (orderTo) params.orderTo = orderTo;
    if (shipFrom) params.shipFrom = shipFrom;
    if (shipTo) params.shipTo = shipTo;
    import('../api').then(a => a.getAdminSettlements(params))
      .then(({ data }) => { setSettlements(Array.isArray(data.settlements) ? data.settlements : []); setSelected(new Set()); })
      .catch(err => toast(err.response?.data?.error || 'Failed to load settlements', 'error'))
      .finally(() => setLoading(false));
  };

  // Debounce the invoice-number text search a bit so it doesn't fire on
  // every keystroke; the other filters (dropdowns/dates) apply immediately.
  useEffect(() => {
    const t = setTimeout(load, invoiceNumberFilter ? 350 : 0);
    return () => clearTimeout(t);
  }, [companyFilter, payStatusFilter, invoiceNumberFilter, orderFrom, orderTo, shipFrom, shipTo]);

  const toggleOne = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const selectAll   = () => setSelected(new Set(settlements.map(s => s.id)));
  const clearSelect = () => setSelected(new Set());
  const toggleAll   = () => (selected.size === settlements.length ? clearSelect() : selectAll());

  // Totals across whatever's currently checked — not the full list, since the
  // point is reconciling a specific batch someone's about to export/invoice,
  // not the grand total of every settlement matching the filters.
  const selectedTotals = settlements
    .filter(s => selected.has(s.id))
    .reduce((t, s) => ({
      subtotal:       t.subtotal       + parseFloat(s.order?.subtotal || 0),
      solidiphyProfit: t.solidiphyProfit + parseFloat(s.solidiphyProfit || 0),
      invoiceAmount:   t.invoiceAmount   + parseFloat(s.invoiceAmount || 0),
    }), { subtotal: 0, solidiphyProfit: 0, invoiceAmount: 0 });

  const clearFilters = () => {
    setCompanyFilter(''); setPayStatusFilter(''); setInvoiceNumberFilter('');
    setOrderFrom(''); setOrderTo(''); setShipFrom(''); setShipTo('');
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const api = await import('../api');
      const { data } = await api.exportSettlementsExcel(Array.from(selected));
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url; a.download = `Settlements-Export-${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast('Export downloaded', 'success');
    } catch (err) {
      toast(err.response?.data?.error || 'Export failed', 'error');
    } finally { setExporting(false); }
  };

  // Pending (not yet invoiced) / Invoiced (billed, not yet paid) / Paid
  // (invoice marked paid) — derived purely from invoiceId + the linked
  // invoice's own paidAt, never a separately-stored value on the settlement.
  const payStatusOf = (s) => {
    if (!s.currentInvoice) return { text: 'Pending', color: 'var(--text3)' };
    if (s.currentInvoice.paidAt) return { text: 'Paid', color: 'var(--success)' };
    return { text: 'Invoiced', color: '#D97706' };
  };

  return (
    <div style={{ maxWidth:1400 }}>
      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:18, fontWeight:700 }}>Partner Settlements</div>
        <div style={{ fontSize:13, color:'var(--text2)' }}>
          Read-only view of the revenue-share calculation for invoice_retail orders. A row appears here once an order reaches Shipped to Customer with a company that has a revenue share % set. <strong>Partner Share</strong> = kept by the partner (their cut of true product margin). <strong>Solidiphy Profit</strong> = Solidiphy's net profit on the order, after cost and after the partner's cut. <strong>Solidiphy Invoice</strong> = what Solidiphy actually bills the partner (cost recovery + Solidiphy Profit + shipping) — this is the revenue figure, Solidiphy Profit is not. To actually generate an invoice or mark one paid, use the Invoices tab — this tab is for ad-hoc Excel export and reconciliation only.
        </div>
      </div>

      <div style={{ display:'flex', gap:10, marginBottom:10, flexWrap:'wrap', alignItems:'center' }}>
        <select className="input" style={{ width:200 }} value={companyFilter} onChange={e => setCompanyFilter(e.target.value)}>
          <option value="">All companies</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className="input" style={{ width:160 }} value={payStatusFilter} onChange={e => setPayStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="invoiced">Invoiced</option>
          <option value="paid">Paid</option>
        </select>
        <input className="input" style={{ width:170 }} placeholder="Search invoice #" value={invoiceNumberFilter} onChange={e => setInvoiceNumberFilter(e.target.value)} />
        <button className="btn btn-sm" onClick={clearFilters}>Clear filters</button>
      </div>

      <div style={{ display:'flex', gap:16, marginBottom:16, flexWrap:'wrap', alignItems:'center', fontSize:12, color:'var(--text3)' }}>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <span>Order date</span>
          <input type="date" className="input" style={{ width:140 }} value={orderFrom} onChange={e => setOrderFrom(e.target.value)} />
          <span>to</span>
          <input type="date" className="input" style={{ width:140 }} value={orderTo} onChange={e => setOrderTo(e.target.value)} />
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <span>Ship date</span>
          <input type="date" className="input" style={{ width:140 }} value={shipFrom} onChange={e => setShipFrom(e.target.value)} />
          <span>to</span>
          <input type="date" className="input" style={{ width:140 }} value={shipTo} onChange={e => setShipTo(e.target.value)} />
        </div>
      </div>

      <div style={{ display:'flex', gap:12, marginBottom:16, alignItems:'center' }}>
        <button className="btn btn-sm" onClick={selectAll} disabled={settlements.length === 0}>Select All ({settlements.length})</button>
        {selected.size > 0 && (
          <div style={{ display:'flex', alignItems:'center', gap:10, background:'var(--bg)', padding:'6px 12px', borderRadius:'var(--radius)', flexWrap:'wrap' }}>
            <span style={{ fontSize:13 }}>{selected.size} selected</span>
            <span style={{ fontSize:12, color:'var(--text3)' }}>|</span>
            <span style={{ fontSize:12 }}>Subtotal: <strong>{fmtMoney(selectedTotals.subtotal)}</strong></span>
            <span style={{ fontSize:12 }}>Solidiphy Profit: <strong style={{ color:'var(--admin-nav)' }}>{fmtMoney(selectedTotals.solidiphyProfit)}</strong></span>
            <span style={{ fontSize:12 }}>Invoice Amount: <strong>{fmtMoney(selectedTotals.invoiceAmount)}</strong></span>
            <button className="btn btn-sm" onClick={handleExport} disabled={exporting}>{exporting ? 'Exporting…' : 'Export to Excel'}</button>
            <button className="btn btn-sm" onClick={clearSelect}>Clear</button>
          </div>
        )}
      </div>

      {/* Bounded-height scroll area: the horizontal scrollbar stays at the
          bottom of the screen instead of below the last row, and the header
          row stays visible while scrolling down. */}
      <div className="card settlements-scroll" style={{ padding:0, overflow:'auto', maxHeight:'calc(100vh - 240px)' }}>
        {loading ? <div style={{ padding:32, textAlign:'center' }}><Spinner /></div>
        : settlements.length === 0
          ? <div style={{ padding:32, textAlign:'center', color:'var(--text3)' }}>No settlements match these filters.</div>
          : (
            <table className="tbl" style={{ whiteSpace:'nowrap' }}>
              <thead style={{ position:'sticky', top:0, zIndex:1 }}>
                <tr>
                  <th style={{ width:32 }}><input type="checkbox" checked={selected.size === settlements.length && settlements.length > 0} onChange={toggleAll} /></th>
                  <th>Order #</th><th>Company</th><th>Rev %</th>
                  <th>Subtotal</th><th>Cost</th><th>Order Discount</th><th>Shipping Cost</th><th>Tax</th>
                  <th>Shipping Included</th><th>Partner Share</th><th>Solidiphy Profit</th><th>Solidiphy Invoice</th>
                  <th>Status</th><th>Order Date</th><th>Ship Date</th><th>Invoice #</th>
                </tr>
              </thead>
              <tbody>
                {settlements.map(s => (
                  <tr key={s.id} style={selected.has(s.id) ? { background:'var(--bg)' } : undefined}>
                    <td><input type="checkbox" checked={selected.has(s.id)} onChange={() => toggleOne(s.id)} /></td>
                    <td>{s.order?.orderNumber || '—'}</td>
                    <td>{s.company?.name || '—'}</td>
                    <td>{parseFloat(s.revenuePercent)}%</td>
                    <td>{fmtMoney(s.order?.subtotal || 0)}</td>
                    <td>{fmtMoney((s.order?.items || []).reduce((sum, i) => sum + parseFloat(i.cost || 0) * i.quantity, 0))}</td>
                    <td>{fmtMoney(s.order?.discountAmount || 0)}</td>
                    <td>{fmtMoney(s.order?.shippingCost || 0)}</td>
                    <td>{fmtMoney(s.order?.taxAmount || 0)}</td>
                    <td>{fmtMoney(s.shippingIncluded)}</td>
                    <td style={{ fontWeight:600, color:'var(--success)' }}>{fmtMoney(s.revenueAmount)}</td>
                    <td style={{ fontWeight:600, color:'var(--admin-nav)' }}>{fmtMoney(s.solidiphyProfit)}</td>
                    <td style={{ fontWeight:600 }}>{fmtMoney(s.invoiceAmount)}</td>
                    <td><span style={{ fontSize:11, fontWeight:600, color: payStatusOf(s).color }}>{payStatusOf(s).text}</span></td>
                    <td style={{ fontSize:12 }}>{fmtDate(s.order?.createdAt || s.createdAt)}</td>
                    <td style={{ fontSize:12 }}>{s.order?.revenueRecognizedAt ? fmtDate(s.order.revenueRecognizedAt) : '—'}</td>
                    <td style={{ fontSize:12 }}>{s.currentInvoice?.invoiceNumber || <span style={{ color:'var(--text3)' }}>—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        }
      </div>
    </div>
  );
}



// ── Invoices Tab (Phase 10) ─────────────────────────────────────────────────
// Home for the whole partner-invoicing workflow: list/filter, generate (pick
// company + range -> preview matching orders -> deselect -> confirm),
// download (Summary PDF / Backup PDF / Excel), and void (issues a Credit
// Memo, releases the underlying orders so they can be rebilled correctly).
function InvoicesTab({ companies, toast }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companyFilter, setCompanyFilter] = useState('');
  const [fromFilter, setFromFilter] = useState('');
  const [toFilter, setToFilter] = useState('');
  const [uninvoiced, setUninvoiced] = useState([]);
  const [showGenerate, setShowGenerate] = useState(false);
  const [voidTarget, setVoidTarget] = useState(null);
  const [detailInvoice, setDetailInvoice] = useState(null);

  const load = () => {
    setLoading(true);
    const params = {};
    if (companyFilter) params.companyId = companyFilter;
    if (fromFilter) params.from = fromFilter;
    if (toFilter) params.to = toFilter;
    import('../api').then(a => Promise.all([
      a.getAdminInvoices(params),
      a.getUninvoicedSummary(),
    ])).then(([invRes, uninvRes]) => {
      setInvoices(Array.isArray(invRes.data.invoices) ? invRes.data.invoices : []);
      setUninvoiced(Array.isArray(uninvRes.data.companies) ? uninvRes.data.companies : []);
    }).catch(err => toast(err.response?.data?.error || 'Failed to load invoices', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [companyFilter, fromFilter, toFilter]);

  const download = async (id, kind, filenameHint) => {
    const isPdf = kind === 'pdf';
    // Open the tab synchronously (before the await below) — browsers block
    // window.open() calls that happen after an async gap, treating them as
    // not a direct user gesture. Point it at the real blob once it's ready.
    const previewTab = isPdf ? window.open('', '_blank') : null;
    try {
      const api = await import('../api');
      const fn = kind === 'pdf' ? api.downloadInvoicePdf : api.downloadInvoiceExcel;
      const { data } = await fn(id);
      const url = window.URL.createObjectURL(new Blob([data], isPdf ? { type: 'application/pdf' } : undefined));
      if (isPdf) {
        if (previewTab) previewTab.location.href = url;
        else window.open(url, '_blank'); // popup was blocked despite the sync open — fall back
      } else {
        const a = document.createElement('a');
        a.href = url; a.download = filenameHint;
        document.body.appendChild(a); a.click(); a.remove();
      }
      // Don't revoke right away for PDFs — the new tab still needs the blob.
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      if (previewTab) previewTab.close();
      toast('Download failed', 'error');
    }
  };

  const statusLabel = (inv) => {
    if (inv.type === 'credit_memo') return { text: 'Credit Memo', color: 'var(--danger)' };
    if (inv.voidedAt) return { text: 'Voided', color: 'var(--text3)' };
    if (inv.paidAt) return { text: 'Paid', color: 'var(--success)' };
    return { text: 'Invoiced', color: '#D97706' };
  };

  const togglePaid = async (inv) => {
    try {
      const api = await import('../api');
      await api.markInvoicePaid(inv.id, !inv.paidAt);
      toast(inv.paidAt ? 'Marked unpaid' : 'Marked paid', 'success');
      load();
    } catch (err) {
      toast(err.response?.data?.error || 'Failed to update', 'error');
    }
  };

  return (
    <div style={{ maxWidth:1300 }}>
      <div style={{ marginBottom:20, display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <div style={{ fontSize:18, fontWeight:700 }}>Invoices</div>
          <div style={{ fontSize:13, color:'var(--text2)' }}>Generate, list, and reprint partner invoices. Once issued, an invoice is immutable — corrections go through a Credit Memo, not an edit.</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowGenerate(true)}>+ Generate Invoice</button>
      </div>

      {/* Shipped but not invoiced report */}
      {uninvoiced.length > 0 && (
        <div className="card" style={{ marginBottom:20, borderLeft:'3px solid var(--warning)' }}>
          <div style={{ fontWeight:600, marginBottom:10 }}>Shipped but not invoiced</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px,1fr))', gap:10 }}>
            {uninvoiced.map(c => (
              <div key={c.companyId} style={{ background:'var(--bg)', borderRadius:'var(--radius)', padding:'10px 14px' }}>
                <div style={{ fontWeight:500, fontSize:13, marginBottom:4 }}>{c.companyName}</div>
                <div style={{ fontSize:18, fontWeight:700, color:'var(--warning)' }}>{fmtMoney(c.totalInvoiceAmount)}</div>
                <div style={{ fontSize:11, color:'var(--text3)' }}>{c.orderCount} orders, oldest ship date {c.oldestShipDate ? fmtDate(c.oldestShipDate) : '—'}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display:'flex', gap:12, marginBottom:16 }}>
        <select className="input" style={{ width:220 }} value={companyFilter} onChange={e => setCompanyFilter(e.target.value)}>
          <option value="">All companies</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <input type="date" className="input" style={{ width:150 }} value={fromFilter} onChange={e => setFromFilter(e.target.value)} />
        <span style={{ alignSelf:'center', color:'var(--text3)' }}>to</span>
        <input type="date" className="input" style={{ width:150 }} value={toFilter} onChange={e => setToFilter(e.target.value)} />
      </div>

      <div className="card" style={{ padding:0, overflowX:'auto' }}>
        {loading ? <div style={{ padding:32, textAlign:'center' }}><Spinner /></div>
        : invoices.length === 0
          ? <Empty icon="📄" title="No invoices yet" subtitle="Generate one from a company's not-yet-invoiced shipped orders." />
          : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Invoice #</th><th>Type</th><th>Company</th><th>Period</th><th>Orders</th>
                  <th>Amount Due</th><th>Status</th><th>Generated</th><th style={{ width:280 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(inv => {
                  const s = statusLabel(inv);
                  return (
                    <tr key={inv.id}>
                      <td style={{ fontWeight:600, cursor:'pointer', color:'var(--admin-nav)' }} onClick={() => setDetailInvoice(inv)}>{inv.invoiceNumber}</td>
                      <td style={{ fontSize:12 }}>{inv.type === 'credit_memo' ? 'Credit Memo' : 'Invoice'}</td>
                      <td>{inv.companyName}</td>
                      <td style={{ fontSize:12 }}>{fmtDateOnly(inv.periodFrom)} – {fmtDateOnly(inv.periodTo)}</td>
                      <td>{inv.orderCount}</td>
                      <td style={{ fontWeight:600 }}>{fmtMoney(inv.totalAmountDue)}</td>
                      <td>
                        <span style={{ fontSize:11, fontWeight:600, color:s.color }}>{s.text}</span>
                        {inv.paidAt && <div style={{ fontSize:10, color:'var(--text3)' }}>{fmtDate(inv.paidAt)}</div>}
                      </td>
                      <td style={{ fontSize:12 }}>{fmtDate(inv.generatedAt)}</td>
                      <td>
                        <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                          <button className="btn btn-sm" onClick={() => download(inv.id, 'pdf', `${inv.invoiceNumber}.pdf`)}>View Invoice PDF</button>
                          <button className="btn btn-sm" onClick={() => download(inv.id, 'excel', `${inv.invoiceNumber}.xlsx`)}>Excel</button>
                          {inv.type === 'invoice' && !inv.voidedAt && (
                            <button className="btn btn-sm" style={inv.paidAt ? {} : { color:'var(--success)' }} onClick={() => togglePaid(inv)}>
                              {inv.paidAt ? 'Mark Unpaid' : 'Mark Paid'}
                            </button>
                          )}
                          {inv.type === 'invoice' && !inv.voidedAt && (
                            <button className="btn btn-sm" style={{ color:'var(--danger)' }} onClick={() => setVoidTarget(inv)}>Void</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )
        }
      </div>

      {showGenerate && (
        <GenerateInvoiceModal
          companies={companies}
          onClose={() => setShowGenerate(false)}
          onGenerated={() => { setShowGenerate(false); load(); }}
          toast={toast}
        />
      )}

      {voidTarget && (
        <VoidInvoiceModal
          invoice={voidTarget}
          onClose={() => setVoidTarget(null)}
          onVoided={() => { setVoidTarget(null); load(); }}
          toast={toast}
        />
      )}

      {detailInvoice && (
        <InvoiceDetailModal invoiceId={detailInvoice.id} onClose={() => setDetailInvoice(null)} toast={toast} />
      )}
    </div>
  );
}

// ── Generate Invoice modal: company + range -> preview -> deselect -> confirm
function GenerateInvoiceModal({ companies, onClose, onGenerated, toast }) {
  const [step, setStep] = useState('pick'); // 'pick' | 'preview'
  const [companyId, setCompanyId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [lines, setLines] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [memo, setMemo] = useState('');
  const [invoiceFormat, setInvoiceFormat] = useState('revenue_summary');

  const loadPreview = async () => {
    if (!companyId) { toast('Select a company', 'error'); return; }
    setLoading(true);
    try {
      const api = await import('../api');
      const { data } = await api.getInvoicePreview({ companyId, from, to });
      setLines(data.lines || []);
      setSelected(new Set((data.lines || []).map(l => l.settlementId)));
      setMemo(data.defaultMemo || '');
      setInvoiceFormat(data.invoiceFormat || 'revenue_summary');
      setStep('preview');
    } catch (err) {
      toast(err.response?.data?.error || 'Failed to load preview', 'error');
    } finally { setLoading(false); }
  };

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const totalSelected = lines.filter(l => selected.has(l.settlementId))
    .reduce((sum, l) => sum + l.amountDue, 0);

  const handleGenerate = async () => {
    if (selected.size === 0) { toast('Select at least one order', 'error'); return; }
    setGenerating(true);
    // Open synchronously — still inside this click's call stack — so the
    // preview tab isn't blocked as a popup once we get to the await below.
    const previewTab = window.open('', '_blank');
    try {
      const api = await import('../api');
      const { data } = await api.generateInvoice({ companyId, settlementIds: Array.from(selected), periodFrom: from || null, periodTo: to || null, memo });
      toast('Invoice generated', 'success');
      // Auto-preview the Summary so it can be reviewed before saving anywhere.
      try {
        const { data: pdfData } = await api.downloadInvoicePdf(data.invoice.id);
        const url = window.URL.createObjectURL(new Blob([pdfData], { type: 'application/pdf' }));
        if (previewTab) previewTab.location.href = url;
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      } catch { if (previewTab) previewTab.close(); }
      onGenerated();
    } catch (err) {
      if (previewTab) previewTab.close();
      toast(err.response?.data?.error || 'Failed to generate invoice', 'error');
    } finally { setGenerating(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
      <div className="card" style={{ width: step === 'preview' ? 780 : 420, padding:28, maxHeight:'88vh', overflowY:'auto' }}>
        <div style={{ fontSize:17, fontWeight:700, marginBottom:20 }}>Generate Invoice</div>

        {step === 'pick' && (
          <>
            <div className="field"><label className="label">Company *</label>
              <select className="input" value={companyId} onChange={e => setCompanyId(e.target.value)}>
                <option value="">Select a company…</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="field"><label className="label">Date range (ship date, optional)</label>
              <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                <input type="date" className="input" value={from} onChange={e => setFrom(e.target.value)} />
                <span style={{ color:'var(--text3)' }}>to</span>
                <input type="date" className="input" value={to} onChange={e => setTo(e.target.value)} />
              </div>
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:20 }}>
              <button className="btn" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={loadPreview} disabled={loading}>{loading ? 'Loading…' : 'Preview Orders'}</button>
            </div>
          </>
        )}

        {step === 'preview' && (
          <>
            {lines.length === 0 ? (
              <Empty icon="✅" title="Nothing to invoice" subtitle="No un-invoiced shipped orders in this range." />
            ) : (
              <>
                <div style={{ fontSize:13, color:'var(--text2)', marginBottom:12 }}>
                  {lines.length} order{lines.length !== 1 ? 's' : ''} found. Deselect any that shouldn't be included, then confirm.
                </div>
                <div style={{ maxHeight:340, overflowY:'auto', border:'1px solid var(--border)', borderRadius:'var(--radius)' }}>
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th style={{ width:32 }}><input type="checkbox" checked={selected.size === lines.length} onChange={() => setSelected(selected.size === lines.length ? new Set() : new Set(lines.map(l => l.settlementId)))} /></th>
                        <th>Ship Date</th><th>Order #</th><th>Total Collected</th><th>Amount Due</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map(l => (
                        <tr key={l.settlementId} style={selected.has(l.settlementId) ? undefined : { opacity:0.4 }}>
                          <td><input type="checkbox" checked={selected.has(l.settlementId)} onChange={() => toggle(l.settlementId)} /></td>
                          <td style={{ fontSize:12 }}>{fmtDate(l.shipmentDate)}</td>
                          <td>{l.orderNumber}</td>
                          <td>{fmtMoney(l.totalCollected)}</td>
                          <td style={{ fontWeight:600 }}>{fmtMoney(l.amountDue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:16 }}>
                  <div style={{ fontSize:14 }}>Selected: <strong>{selected.size}</strong> orders, total <strong>{fmtMoney(totalSelected)}</strong></div>
                </div>
                {invoiceFormat === 'line_item' && (
                  <div className="field" style={{ marginTop:16 }}>
                    <label className="label">Memo</label>
                    <input className="input" maxLength={255} value={memo} onChange={e => setMemo(e.target.value)} />
                  </div>
                )}
              </>
            )}
            <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:20 }}>
              <button className="btn" onClick={() => setStep('pick')}>Back</button>
              <button className="btn" onClick={onClose}>Cancel</button>
              {lines.length > 0 && (
                <button className="btn btn-primary" onClick={handleGenerate} disabled={generating || selected.size === 0}>
                  {generating ? 'Generating…' : `Generate Invoice (${selected.size})`}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Void modal — confirms + generates the Credit Memo ───────────────────────
function VoidInvoiceModal({ invoice, onClose, onVoided, toast }) {
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);

  const handleVoid = async () => {
    setConfirming(true);
    try {
      const api = await import('../api');
      await api.voidInvoice(invoice.id, reason);
      toast('Invoice voided — Credit Memo generated', 'success');
      onVoided();
    } catch (err) {
      toast(err.response?.data?.error || 'Failed to void invoice', 'error');
    } finally { setConfirming(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
      <div className="card" style={{ width:460, padding:28 }}>
        <div style={{ fontSize:17, fontWeight:700, marginBottom:10 }}>Void {invoice.invoiceNumber}?</div>
        <div style={{ fontSize:13, color:'var(--text2)', marginBottom:16 }}>
          This generates a Credit Memo for {fmtMoney(invoice.totalAmountDue)} referencing this invoice, and releases its {invoice.orderCount} order{invoice.orderCount !== 1 ? 's' : ''} so they can be picked up on a corrected invoice. The original invoice itself is never edited or deleted — this is permanent and cannot be undone.
        </div>
        <div className="field"><label className="label">Reason (optional, shown on the Credit Memo record)</label>
          <textarea className="input" rows={3} value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Order CAR-CEL-0000029 was double-counted" />
        </div>
        <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:20 }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn" style={{ background:'var(--danger)', color:'#fff', borderColor:'var(--danger)' }} onClick={handleVoid} disabled={confirming}>
            {confirming ? 'Voiding…' : 'Void & Generate Credit Memo'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Invoice detail modal — line items, read-only ─────────────────────────────
function InvoiceDetailModal({ invoiceId, onClose, toast }) {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    import('../api').then(a => a.getInvoiceDetail(invoiceId))
      .then(({ data }) => setInvoice(data.invoice))
      .catch(() => toast('Failed to load invoice detail', 'error'))
      .finally(() => setLoading(false));
  }, [invoiceId]);

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
      <div className="card" style={{ width:820, padding:28, maxHeight:'88vh', overflowY:'auto' }}>
        {loading || !invoice ? <div style={{ padding:32, textAlign:'center' }}><Spinner /></div> : (
          <>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
              <div>
                <div style={{ fontSize:17, fontWeight:700 }}>{invoice.invoiceNumber}</div>
                <div style={{ fontSize:13, color:'var(--text2)' }}>{invoice.companyName} · {fmtDateOnly(invoice.periodFrom)} – {fmtDateOnly(invoice.periodTo)}</div>
              </div>
              <button className="btn btn-sm" onClick={onClose}>Close</button>
            </div>
            {invoice.voidedAt && (
              <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:'var(--radius)', padding:'10px 14px', marginBottom:16, fontSize:13, color:'var(--danger)' }}>
                Voided {fmtDate(invoice.voidedAt)} by {invoice.voidedByName}{invoice.voidReason ? ` — ${invoice.voidReason}` : ''}
              </div>
            )}
            <div style={{ maxHeight:400, overflowY:'auto', border:'1px solid var(--border)', borderRadius:'var(--radius)' }}>
              <table className="tbl">
                <thead>
                  <tr><th>Date</th><th>Order #</th><th>Collected</th><th>Cost</th><th>Shipping</th><th>Tax</th><th>Net Rev</th><th>Partner Share</th><th>Amount Due</th></tr>
                </thead>
                <tbody>
                  {(invoice.items || []).slice().sort((a,b) => new Date(a.orderDate) - new Date(b.orderDate)).map(item => (
                    <tr key={item.id}>
                      <td style={{ fontSize:12 }}>{fmtDate(item.orderDate)}</td>
                      <td>{item.orderNumber}</td>
                      <td>{fmtMoney(item.totalCollected)}</td>
                      <td>{fmtMoney(item.cost)}</td>
                      <td>{fmtMoney(item.shipping)}</td>
                      <td>{fmtMoney(item.tax)}</td>
                      <td>{fmtMoney(item.netRevenue)}</td>
                      <td>{fmtMoney(item.cclShare)}</td>
                      <td style={{ fontWeight:600 }}>{fmtMoney(item.amountDue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ textAlign:'right', marginTop:14, fontSize:15, fontWeight:700 }}>
              Amount Due: {fmtMoney(invoice.totalAmountDue)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}


function VolumePromotionsTab({ companies, products, promotions, onRefresh, toast }) {
  const emptyPromotion = {
    name:'', mode:'all_skus', sku:'',
    effectiveDate:'', expirationDate:'', noExpiration:false, active:true,
    tiers:[{ minQty:'', discountPercent:'' }],
    companyIds:[],
  };
  const [form,     setForm]     = useState(emptyPromotion);
  const [editId,   setEditId]   = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [companiesListModal, setCompaniesListModal] = useState(null);

  const openCreate = () => { setEditId(null); setForm(emptyPromotion); setShowForm(true); };
  const openEdit   = (p) => {
    setEditId(p.id);
    setForm({
      name: p.name, mode: p.mode, sku: p.sku || '',
      effectiveDate:  p.effectiveDate  || '',
      expirationDate: p.expirationDate || '',
      noExpiration:   p.noExpiration   || false,
      active:         p.active,
      tiers: (p.tiers || []).map(t => ({ minQty: t.minQty, discountPercent: t.discountPercent })).sort((a,b) => a.minQty - b.minQty),
      companyIds: (p.companies || []).map(c => c.id),
    });
    setShowForm(true);
  };

  const addTierRow    = () => setForm(p => ({ ...p, tiers: [...p.tiers, { minQty:'', discountPercent:'' }] }));
  const removeTierRow = (idx) => setForm(p => ({ ...p, tiers: p.tiers.filter((_,i) => i !== idx) }));
  const updateTierRow = (idx, field, value) => setForm(p => ({ ...p, tiers: p.tiers.map((t,i) => i === idx ? { ...t, [field]: value } : t) }));

  const handleSave = async () => {
    if (!form.name) return alert('Name is required');
    if (form.mode === 'specific_sku' && !form.sku) return alert('SKU is required for a specific-SKU promotion');
    const validTiers = form.tiers.filter(t => t.minQty && t.discountPercent !== '');
    if (!validTiers.length) return alert('At least one tier (qty + %) is required');
    setSaving(true);
    try {
      const apiModule = await import('../api');
      const payload = { ...form, tiers: validTiers };
      if (editId) await apiModule.updateVolumePromotion(editId, payload);
      else        await apiModule.createVolumePromotion(payload);
      setShowForm(false); setEditId(null); setForm(emptyPromotion);
      onRefresh();
      toast(editId ? 'Promotion updated' : 'Promotion created', 'success');
    } catch (err) { toast(err.response?.data?.error || 'Error saving promotion', 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this promotion?')) return;
    try { const apiModule = await import('../api'); await apiModule.deleteVolumePromotion(id); onRefresh(); toast('Promotion deleted', 'success'); }
    catch (err) { toast('Failed to delete', 'error'); }
  };

  const today = new Date().toISOString().split('T')[0];
  const promoStatus = (p) => {
    if (!p.active) return { label:'Inactive', color:'var(--text3)' };
    if (!p.noExpiration && p.expirationDate && p.expirationDate < today) return { label:'Expired', color:'var(--danger)' };
    if (p.effectiveDate && p.effectiveDate > today) return { label:'Upcoming', color:'#F59E0B' };
    return { label:'Active', color:'var(--success)' };
  };
  const tiersLabel = (p) => (p.tiers || []).slice().sort((a,b) => a.minQty - b.minQty)
    .map(t => `${t.minQty}+: ${parseFloat(t.discountPercent)}%`).join(', ') || '—';

  return (
    <div style={{ maxWidth:1300 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <div style={{ fontSize:18, fontWeight:700 }}>Volume Discounts</div>
          <div style={{ fontSize:13, color:'var(--text2)' }}>Codeless, quantity-based promotions — no code for the photographer to enter, applies automatically at checkout.</div>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>+ Create Promotion</button>
      </div>

      <div className="card" style={{ padding:0, overflowX:'auto' }}>
        {promotions.length === 0
          ? <div style={{ padding:32, textAlign:'center', color:'var(--text3)' }}>No volume discount promotions yet. Create one above.</div>
          : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Name</th><th>Applies To</th><th>Tiers</th>
                  <th>Effective</th><th>Expires</th><th>Companies</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {promotions.map(p => {
                  const st = promoStatus(p);
                  return (
                    <tr key={p.id} onClick={() => openEdit(p)} style={{ cursor:'pointer' }}>
                      <td style={{ fontWeight:600 }}>{p.name}</td>
                      <td style={{ fontSize:12 }}>{p.mode === 'all_skus' ? 'All SKUs' : `SKU: ${p.sku}`}</td>
                      <td style={{ fontSize:12 }}>{tiersLabel(p)}</td>
                      <td style={{ fontSize:12 }}>{p.effectiveDate || '—'}</td>
                      <td style={{ fontSize:12 }}>{p.noExpiration ? 'No expiration' : (p.expirationDate || '—')}</td>
                      <td style={{ fontSize:11 }}>
                        {!(p.companies?.length)
                          ? <span style={{ color:'var(--text3)' }}>None</span>
                          : <button className="btn btn-sm" style={{ fontSize:11 }}
                              onClick={(e) => { e.stopPropagation(); setCompaniesListModal({ title: p.name, companies: p.companies }); }}>
                              {p.companies.length} {p.companies.length === 1 ? 'company' : 'companies'}
                            </button>
                        }
                      </td>
                      <td><span style={{ fontSize:11, color: st.color, fontWeight:600 }}>{st.label}</span></td>
                      <td>
                        <div style={{ display:'flex', gap:6 }} onClick={e => e.stopPropagation()}>
                          <button className="btn btn-sm" onClick={() => openEdit(p)}>Edit</button>
                          <button className="btn btn-danger btn-sm" onClick={() => handleDelete(p.id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )
        }
      </div>

      {/* Create / Edit modal */}
      {showForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div className="card" style={{ width:520, padding:28, maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ fontSize:17, fontWeight:700, marginBottom:20 }}>{editId ? 'Edit Promotion' : 'New Promotion'}</div>

            <div className="field"><label className="label">Name * (internal label only, not shown to customer)</label>
              <input className="input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Standard volume discount" />
            </div>

            <div className="field"><label className="label">Applies to *</label>
              <select className="select" value={form.mode} onChange={e => setForm(p => ({ ...p, mode: e.target.value, sku: e.target.value === 'all_skus' ? '' : p.sku }))}>
                <option value="all_skus">All SKUs — triggered by total cart quantity</option>
                <option value="specific_sku">One specific SKU — triggered by that SKU's quantity</option>
              </select>
            </div>

            {form.mode === 'specific_sku' && (
              <div className="field"><label className="label">SKU *</label>
                <select className="input" value={form.sku} onChange={e => setForm(p => ({ ...p, sku: e.target.value }))}>
                  <option value="">Select a product…</option>
                  {products.map(pr => <option key={pr.sku} value={pr.sku}>{pr.name} ({pr.sku})</option>)}
                </select>
              </div>
            )}

            <div className="field">
              <label className="label">Tiers * (qty threshold → discount %)</label>
              {form.tiers.map((t, idx) => (
                <div key={idx} style={{ display:'flex', gap:10, alignItems:'center', marginBottom:6 }}>
                  <input className="input" type="number" min="2" placeholder="Qty" style={{ width:90 }} value={t.minQty}
                    onChange={e => updateTierRow(idx, 'minQty', e.target.value)} />
                  <span style={{ fontSize:13, color:'var(--text3)' }}>+ units →</span>
                  <input className="input" type="number" step="0.01" min="0" max="100" placeholder="%" style={{ width:90 }} value={t.discountPercent}
                    onChange={e => updateTierRow(idx, 'discountPercent', e.target.value)} />
                  <span style={{ fontSize:13, color:'var(--text3)' }}>% off</span>
                  {form.tiers.length > 1 && <button className="btn btn-sm" style={{ color:'var(--danger)' }} onClick={() => removeTierRow(idx)}>Remove</button>}
                </div>
              ))}
              <button className="btn btn-sm" onClick={addTierRow}>+ Add Tier</button>
            </div>

            <div className="grid2">
              <div className="field"><label className="label">Effective Date</label>
                <input className="input" type="date" value={form.effectiveDate}
                  onChange={e => setForm(p => ({ ...p, effectiveDate: e.target.value }))} />
              </div>
              <div className="field">
                <label className="label">Expiration Date</label>
                <input className="input" type="date" value={form.expirationDate} disabled={form.noExpiration}
                  onChange={e => setForm(p => ({ ...p, expirationDate: e.target.value }))} />
              </div>
            </div>
            <div className="field">
              <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}>
                <input type="checkbox" checked={form.noExpiration}
                  onChange={e => setForm(p => ({ ...p, noExpiration: e.target.checked }))} />
                <span style={{ fontSize:13 }}>No expiration date</span>
              </label>
            </div>

            <div className="field">
              <label className="label">Assign to Companies</label>
              <div style={{ border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:8, maxHeight:150, overflowY:'auto' }}>
                {companies.map(c => (
                  <label key={c.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'4px 0', cursor:'pointer' }}>
                    <input type="checkbox" checked={form.companyIds.includes(c.id)}
                      onChange={e => setForm(p => ({
                        ...p,
                        companyIds: e.target.checked
                          ? [...p.companyIds, c.id]
                          : p.companyIds.filter(id => id !== c.id)
                      }))} />
                    <span style={{ fontSize:13 }}>{c.name}</span>
                  </label>
                ))}
              </div>
              <div style={{ fontSize:11, color:'var(--text3)', marginTop:4 }}>Leave unchecked to apply to no companies (inactive state)</div>
            </div>

            <div className="field" style={{ display:'flex', alignItems:'center', gap:8 }}>
              <input type="checkbox" id="promo-active" checked={form.active} onChange={e => setForm(p => ({ ...p, active: e.target.checked }))} />
              <label htmlFor="promo-active" style={{ fontSize:13, cursor:'pointer' }}>Active</label>
            </div>

            <div style={{ display:'flex', gap:10, marginTop:16 }}>
              <button className="btn btn-primary" disabled={saving} onClick={handleSave}>{saving ? <Spinner size={14}/> : (editId ? 'Save changes' : 'Create promotion')}</button>
              <button className="btn" onClick={() => { setShowForm(false); setEditId(null); setForm(emptyPromotion); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Companies list modal */}
      {companiesListModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}
          onClick={() => setCompaniesListModal(null)}>
          <div className="card" style={{ width:360, padding:24 }} onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight:700, marginBottom:12 }}>{companiesListModal.title} — Companies</div>
            {companiesListModal.companies.map(c => <div key={c.id} style={{ padding:'4px 0', fontSize:13 }}>{c.name}</div>)}
            <button className="btn" style={{ marginTop:16 }} onClick={() => setCompaniesListModal(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminScreen() {
  const { toast } = useToast();
  const [isAdmin,     setIsAdmin]     = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const checkAdminAuth = async () => {
    try {
      await adminUserMe();
      setIsAdmin(true);
    } catch {
      setIsAdmin(false);
    } finally {
      setAuthChecked(true);
    }
  };
  useEffect(() => { checkAdminAuth(); }, []);
  const handleAdminLogout = async () => {
    try { await adminUserLogout(); } catch {}
    setIsAdmin(false);
  };
  const [tab,        setTab]        = useState(() => sessionStorage.getItem('admin_tab') || 'dashboard');

  // Reset brand colors to admin defaults — prevents bleed from photographer portal
  useEffect(() => {
    document.title = 'Admin Portal — Solidiphy';
    document.documentElement.style.setProperty('--brand',        '#0077B6');
    document.documentElement.style.setProperty('--brand-dark',   '#005F92');
    document.documentElement.style.setProperty('--brand-light',  'rgba(0,119,182,0.1)');
    document.documentElement.style.setProperty('--brand-border', 'rgba(0,119,182,0.3)');
  }, []);
  const [companies,  setCompanies]  = useState([]);
  const [groups,     setGroups]     = useState([]);
  const [products,   setProducts]   = useState([]);
  const [orders,     setOrders]     = useState([]);
  const [stats,      setStats]      = useState(null);
  const [selCompany, setSelCompany] = useState(null);
  // Clicking a company row sets selCompany to drill into that company's
  // groups/products — but it was never cleared when switching tabs, so it
  // silently kept filtering the Groups and Products tabs to that one company
  // long after leaving the Companies screen. Reset it whenever we navigate
  // away from the Companies-related views.
  useEffect(() => {
    if (tab !== 'companies' && tab !== 'company-form') setSelCompany(null);
  }, [tab]);
  // Dashboard stats only refreshed on login + specific save actions before —
  // switching tabs to Dashboard never re-fetched, so a newly-created order
  // wouldn't show up in the alert cards without a manual Refresh click.
  useEffect(() => {
    if (tab === 'dashboard' && isAdmin) loadAll();
  }, [tab]);

  // Dashboard date range filter — 'all' | '7' | '30' | '60' | 'custom'
  const [dashRange, setDashRange] = useState('all');
  const [dashFrom,  setDashFrom]  = useState('');
  const [dashTo,    setDashTo]    = useState('');
  const dashParams = () => {
    const base = dashRange === 'custom' ? ((dashFrom || dashTo) ? { from: dashFrom, to: dashTo } : {})
      : dashRange !== 'all' ? { range: dashRange } : {};
    return base;
  };
  // Order Outcomes (Shipped to Customer / Cancelled) gets its own,
  // completely independent date range — deliberately NOT shared with
  // Revenue by Company's picker, since coupling status counts to the
  // revenue charts' range felt awkward (per Sharon).
  const [outcomeRange, setOutcomeRange] = useState('all');
  const [outcomeFrom,  setOutcomeFrom]  = useState('');
  const [outcomeTo,    setOutcomeTo]    = useState('');
  const outcomeParams = () => {
    const base = outcomeRange === 'custom' ? ((outcomeFrom || outcomeTo) ? { outcomeFrom, outcomeTo } : {})
      : outcomeRange !== 'all' ? { outcomeRange } : {};
    return base;
  };
  // Revenue chart controls — bar chart supports multi-select metrics
  // (stacks when >1 checked); pie is single-select since a pie can only
  // show one metric's breakdown at a time; line chart is company-select +
  // single-select metric + granularity (fetched separately since each
  // granularity needs its own time-bucketed query).
  const REVENUE_METRICS = [
    { key: 'salesRevenue', label: 'Sales Revenue', color: '#2E86C1' },
    { key: 'grossRevenue', label: 'Gross Revenue', color: '#8E44AD' },
    { key: 'netRevenue',   label: 'Net Revenue',   color: '#059669' },
  ];
  const [barMetrics, setBarMetrics] = useState(['salesRevenue', 'grossRevenue', 'netRevenue']);
  const PIE_COLORS = ['#2E86C1', '#8E44AD', '#059669', '#D97706', '#DC2626', '#0891B2', '#65A30D', '#DB2777', '#7C3AED', '#EA580C'];
  const [trendGranularity, setTrendGranularity] = useState('monthly');
  const [trendMetric, setTrendMetric] = useState('netRevenue');
  const [trendCompanies, setTrendCompanies] = useState([]); // selected companies; populated once trend data loads
  const [trendData, setTrendData] = useState(null);
  const loadTrend = async (granularity) => {
    try {
      const { data } = await getRevenueTrend({ granularity });
      setTrendData(data);
      setTrendCompanies(prev => prev.length ? prev : data.companies); // default: all companies checked on first load
    } catch (err) { console.error('Revenue trend load error:', err.message); }
  };
  useEffect(() => { if (tab === 'dashboard') loadTrend(trendGranularity); }, [trendGranularity, tab]);
  const loadStats = async () => {
    try {
      const { data } = await getAdminStats({ ...dashParams(), ...outcomeParams() });
      setStats(data);
    } catch (err) { console.error('Stats load error:', err.message); }
  };
  // Refetch whenever either independent range filter changes while sitting on the dashboard.
  useEffect(() => {
    if (tab === 'dashboard' && isAdmin) loadStats();
  }, [dashRange, dashFrom, dashTo, outcomeRange, outcomeFrom, outcomeTo]);
  const [saving,     setSaving]     = useState(false);
  const [editingId,  setEditingId]  = useState(null);
  const [selOrder,   setSelOrder]   = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(false);

  // Order filters
  const [orderSearch,  setOrderSearch]  = useState('');
  const [orderStatus,  setOrderStatus]  = useState([]); // Phase 9 — multi-select
  const [hideShippedCancelled, setHideShippedCancelled] = useState(true); // on by default — these are terminal and rarely need review
  const [orderCompany, setOrderCompany] = useState('');
  const [orderGroup,   setOrderGroup]   = useState('');
  const [orderRushOnly, setOrderRushOnly] = useState(false); // Phase 9
  const [orderDetailLightbox, setOrderDetailLightbox] = useState(null); // Phase 9 — front photo enlarge
  const [orderSort,    setOrderSort]    = useState('createdAt');
  const [orderSortDir, setOrderSortDir] = useState('DESC');

  // Forms
  const emptyCompany = { name:'', slug:'', brandName:'', brandColor:'#0077B6', brandLogo:'',
    onedriveFolder:'', invoiceEmail:'', contactName:'', contactTitle:'', contactPhone:'', contactEmail:'',
    addressCompanyName:'', address1:'', address2:'', city:'', state:'', zip:'', country:'US',
    billToCompanyName:'', billToAddress1:'', billToAddress2:'', billToCity:'', billToState:'', billToZip:'', billToCountry:'',
    shipToCompanyName:'', shipToAddress1:'', shipToAddress2:'', shipToCity:'', shipToState:'', shipToZip:'', shipToCountry:'',
    orderDisclaimer:'', billingModel:'invoice_retail', revenuePercent:'', freeShippingEnabled:false,
    invoiceFormat:'revenue_summary', invoicePoNumber:'N/A', invoiceTerms:'Net 30', invoicePaymentDue:'Upon receipt',
    invoiceVia:'USPS', invoiceCustomerId:'' };
  const emptyGroup   = { companyId:'', name:'', pin:'', description:'', location:'', taxEnabled:false, taxRate:'',
    taxExempt:false, taxExemptCertificateId:'', taxExemptCertificateUrl:'', taxExemptExpiresAt:'', invoiceEmail:'',
    showOnInvoiceSpreadsheet:true, hasPin:false };
  const emptyProduct = { name:'', sku:'', description:'', category:'', wholesalePrice:'', cost:'', active:true, sortOrder:0, invoiceDescription:'' };
  const emptyCpForm  = { companyId:'', productId:'', retailPrice:'', imageUrl:'', active:true, sortOrder:0 };
  const emptyCoupon  = { code:'', type:'free_shipping', discountPercent:'', minimumOrder:'', effectiveDate:'', expirationDate:'', active:true, description:'', domesticOnly:false, companyIds:[] };

  const [companyForm,  setCompanyForm]  = useState(emptyCompany);
  const [groupForm,    setGroupForm]    = useState(emptyGroup);
  const [productForm,  setProductForm]  = useState(emptyProduct);
  const [cpForm,       setCpForm]       = useState(emptyCpForm);
  const [couponForm,   setCouponForm]   = useState(emptyCoupon);
  const [editCouponId, setEditCouponId] = useState(null);
  const [coupons,      setCoupons]      = useState([]);
  const [volumePromotions, setVolumePromotions] = useState([]);
  // Cancel modal state
  const [cancelModalOrder, setCancelModalOrder] = useState(null);
  const [cancelReason,     setCancelReason]     = useState('');
  const [assigningProduct, setAssigningProduct] = useState(undefined); // undefined=hidden, null=shown no product, object=shown with product
  const [companiesModal,   setCompaniesModal]   = useState(null); // product whose companies to show
  const [assignReturnTo,   setAssignReturnTo]   = useState('products'); // where to go on back

  const loadAll = async () => {
    try {
      const [cRes, gRes, pRes, sRes, cpRes, vpRes] = await Promise.all([
        getAdminCompanies(), getAdminGroups(), getAdminProducts(), getAdminStats(dashParams()),
        import('../api').then(a => a.getAdminCoupons()),
        import('../api').then(a => a.getAdminVolumePromotions()),
      ]);
      setCompanies(cRes.data.companies);
      setGroups(gRes.data.groups);
      setProducts(pRes.data.products);
      setStats(sRes.data);
      setCoupons(cpRes.data.coupons);
      setVolumePromotions(vpRes.data.promotions);
    } catch (err) { console.error('Load error:', err.message); }
  };

  const loadOrders = async () => {
    try {
      const { data } = await getAdminOrders({
        search: orderSearch, status: orderStatus.join(','), companyId: orderCompany, groupId: orderGroup,
        rushOnly: orderRushOnly ? 'true' : '', sortBy: orderSort, sortDir: orderSortDir, limit: 50,
        hideShippedCancelled: hideShippedCancelled ? 'true' : '',
      });
      setOrders(data.orders);
    } catch {}
  };

  useEffect(() => { if (isAdmin) { loadAll(); loadOrders(); } }, [isAdmin]);
  useEffect(() => { if (isAdmin && tab === 'orders') loadOrders(); }, [tab, orderSearch, orderStatus, orderCompany, orderGroup, orderRushOnly, orderSort, orderSortDir, hideShippedCancelled]);

  // Handle browser back button — stay within admin app
  useEffect(() => {
    const handlePopState = (e) => {
      e.preventDefault();
      // If on a sub-tab, go back to parent tab
      if (tab === 'order-detail')  { setTab('orders');    setSelOrder(null);   return; }
      if (tab === 'company-form')  { setTab('companies'); setEditingId(null);  return; }
      if (tab === 'group-form')    { setTab('groups');    setEditingId(null);  return; }
      if (tab === 'product-form')  { setTab('products');  setEditingId(null);  return; }
      // On main tabs, go to dashboard
      if (tab !== 'dashboard') { setTab('dashboard'); return; }
    };
    window.addEventListener('popstate', handlePopState);
    // Push a state so we can intercept back
    window.history.pushState({ adminTab: tab }, '');
    return () => window.removeEventListener('popstate', handlePopState);
  }, [tab]);

  const handleAdminLogin = async () => { await checkAdminAuth(); loadAll(); loadOrders(); };

  if (!authChecked) return null; // avoid a flash of the login screen while the check is in flight
  if (!isAdmin) return <AdminLogin onLogin={handleAdminLogin} />;

  // ── Company CRUD ──────────────────────────────────────────────
  const saveCompany = async () => {
    setSaving(true);
    try {
      if (editingId) {
        await updateCompany(editingId, companyForm);
        await loadAll();
        setTab('companies'); setEditingId(null); setCompanyForm(emptyCompany);
      } else {
        const { data } = await createCompany(companyForm);
        // Stay on form with new ID so admin can immediately assign products
        setEditingId(data.company.id);
        await loadAll();
      }
    } catch (err) { alert(err.response?.data?.error || 'Error'); }
    finally { setSaving(false); }
  };

  const editCompany = (c) => {
    setEditingId(c.id);
    setCompanyForm({
      name:c.name, slug:c.slug, brandName:c.brandName||'', brandColor:c.brandColor||'#0077B6',
      brandLogo:c.brandLogo||'', onedriveFolder:c.onedriveFolder||'', invoiceEmail:c.invoiceEmail||'',
      contactName:c.contactName||'', contactTitle:c.contactTitle||'', contactPhone:c.contactPhone||'', contactEmail:c.contactEmail||'',
      addressCompanyName:c.addressCompanyName||'', address1:c.address1||'', address2:c.address2||'', city:c.city||'', state:c.state||'',
      zip:c.zip||'', country:c.country||'US',
      billToCompanyName:c.billToCompanyName||'', billToAddress1:c.billToAddress1||'', billToAddress2:c.billToAddress2||'',
      billToCity:c.billToCity||'', billToState:c.billToState||'', billToZip:c.billToZip||'', billToCountry:c.billToCountry||'',
      shipToCompanyName:c.shipToCompanyName||'', shipToAddress1:c.shipToAddress1||'', shipToAddress2:c.shipToAddress2||'',
      shipToCity:c.shipToCity||'', shipToState:c.shipToState||'', shipToZip:c.shipToZip||'', shipToCountry:c.shipToCountry||'',
      orderDisclaimer:c.orderDisclaimer||'',
      billingModel:c.billingModel||'invoice_retail',
      revenuePercent:c.revenuePercent||'',
      freeShippingEnabled:!!c.freeShippingEnabled,
      invoiceFormat:c.invoiceFormat||'revenue_summary',
      invoicePoNumber:c.invoicePoNumber ?? 'N/A',
      invoiceTerms:c.invoiceTerms ?? 'Net 30',
      invoicePaymentDue:c.invoicePaymentDue ?? 'Upon receipt',
      invoiceVia:c.invoiceVia ?? 'USPS',
      invoiceCustomerId:c.invoiceCustomerId ?? '',
    });
    setTab('company-form');
  };

  // ── Group CRUD ────────────────────────────────────────────────
  const saveGroup = async () => {
    setSaving(true);
    try {
      if (editingId) await updateGroup(editingId, groupForm);
      else           await createGroup(groupForm);
      setGroupForm(emptyGroup); setEditingId(null); setTab('groups'); await loadAll();
    } catch (err) { alert(err.response?.data?.error || 'Error'); }
    finally { setSaving(false); }
  };

  const editGroup = (g) => {
    setEditingId(g.id);
    // If somehow both flags are true (shouldn't happen going forward, but
    // could exist from before this UI made them mutually exclusive), exemption
    // wins — matches the backend's own precedence (isExemptionActive is
    // checked first, taxEnabled is irrelevant when exempt).
    const bothSet = (g.taxEnabled || false) && (g.taxExempt || false);
    setGroupForm({ companyId:g.companyId, name:g.name, pin:'', description:g.description||'', location:g.location||'',
      taxEnabled: bothSet ? false : (g.taxEnabled||false), taxRate:g.taxRate||'',
      taxExempt:g.taxExempt||false, taxExemptCertificateId:g.taxExemptCertificateId||'',
      taxExemptCertificateUrl:g.taxExemptCertificateUrl||'', taxExemptExpiresAt:g.taxExemptExpiresAt||'',
      invoiceEmail:g.invoiceEmail||'', showOnInvoiceSpreadsheet: g.showOnInvoiceSpreadsheet !== false, hasPin: !!g.hasPin });
    setTab('group-form');
  };

  // ── Product CRUD ──────────────────────────────────────────────
  const saveProduct = async () => {
    setSaving(true);
    try {
      if (editingId) await updateProduct(editingId, productForm);
      else           await createProduct(productForm);
      setProductForm(emptyProduct); setEditingId(null); setTab('products'); await loadAll();
    } catch (err) { alert(err.response?.data?.error || 'Error'); }
    finally { setSaving(false); }
  };

  const editProduct = (p) => {
    setEditingId(p.id);
    setProductForm({ name:p.name, sku:p.sku, description:p.description||'', category:p.category||'', wholesalePrice:p.wholesalePrice||'', cost:p.cost||'', active:p.active, sortOrder:p.sortOrder||0, invoiceDescription:p.invoiceDescription||'' });
    setTab('product-form');
  };

  // ── Company Product assignment CRUD ───────────────────────────
  const saveCpAssignment = async () => {
    setSaving(true);
    try {
      const api = await import('../api');
      if (cpForm.id) await api.updateCompanyProduct(cpForm.id, cpForm);
      else           await api.createCompanyProduct(cpForm);
      setCpForm(emptyCpForm); await loadAll();
    } catch (err) { alert(err.response?.data?.error || 'Error'); }
    finally { setSaving(false); }
  };

  // ── Coupon CRUD ───────────────────────────────────────────────
  const saveCoupon = async () => {
    setSaving(true);
    try {
      const api = await import('../api');
      if (editCouponId) await api.updateCoupon(editCouponId, couponForm);
      else              await api.createCoupon(couponForm);
      setCouponForm(emptyCoupon); setEditCouponId(null); await loadAll();
    } catch (err) { alert(err.response?.data?.error || 'Error'); }
    finally { setSaving(false); }
  };

  const editCoupon = (c) => {
    setEditCouponId(c.id);
    setCouponForm({
      code: c.code, type: c.type,
      discountPercent: c.discountPercent || '',
      minimumOrder:    c.minimumOrder    || '',
      effectiveDate:   c.effectiveDate   || '',
      expirationDate:  c.expirationDate  || '',
      active:          c.active,
      description:     c.description    || '',
      domesticOnly:    c.domesticOnly   || false,
      companyIds:      c.companies?.map(co => co.id) || [],
    });
  };

  // ── Image uploads ─────────────────────────────────────────────
  const handleProductImage = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append('image', file);
    try { const { data } = await uploadProductImage(fd); setProductForm(p => ({ ...p, imageUrl: data.imageUrl })); }
    catch (err) { alert('Image upload failed: ' + (err.response?.data?.error || err.message)); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append('logo', file);
    try { const { data } = await uploadLogo(fd); setCompanyForm(p => ({ ...p, brandLogo: data.logoUrl })); }
    catch (err) { alert('Logo upload failed: ' + err.message); }
  };

  const handleCertUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append('certificate', file);
    try {
      const { data } = await uploadTaxExemptCert(fd);
      setGroupForm(p => ({ ...p, taxExemptCertificateUrl: data.certUrl }));
      toast('Certificate uploaded', 'success');
    } catch (err) { alert('Certificate upload failed: ' + err.message); }
  };

  const handleTermsUpload = async (e) => {};   // no longer used
  const handlePrivacyUpload = async (e) => {}; // no longer used

  // ── Order detail ──────────────────────────────────────────────
  const openOrder = async (id) => {
    setLoadingOrder(true);
    setSelOrder(null);
    setTab('order-detail');
    try {
      const { data } = await getAdminOrder(id);
      setSelOrder(data.order);
    } catch (err) {
      alert('Failed to load order: ' + (err.response?.data?.error || err.message));
      setTab('orders');
    } finally {
      setLoadingOrder(false);
    }
  };

  const handleReplacePhoto = async (item, file, isBackPhoto = false) => {
    if (!file || !selOrder) return;
    const fd = new FormData();
    fd.append('photo', file);
    fd.append('orderId', selOrder.id);
    fd.append('itemId', item.id);
    fd.append('isBackPhoto', isBackPhoto ? 'true' : 'false');
    try {
      await replacePhoto(fd);
      const { data } = await getAdminOrder(selOrder.id);
      setSelOrder(data.order);
      toast(`${isBackPhoto ? 'Back' : 'Front'} photo replaced!`, 'success');
    } catch (err) { alert('Replace failed: ' + err.message); }
  };

  const filteredGroups   = selCompany ? groups.filter(g => g.companyId === selCompany.id) : groups;
  const filteredProducts = selCompany ? products.filter(p => p.companyId === selCompany.id) : products;

  const ALL_STATUSES = [
    { value:'awaiting_photo',       label:'Awaiting Photo',       short:'AWP'  },
    { value:'in_process',           label:'In Process',           short:'INP'  },
    { value:'mqc',                  label:'Quality',              short:'MQC'  },
    { value:'ready_to_model',       label:'Ready to Model',       short:'RTM'  },
    { value:'sculpting',            label:'Sculpting',            short:'SCP'  },
    { value:'ready_to_print',       label:'Ready to Print',       short:'RTP'  },
    { value:'printing',             label:'Printing',              short:'PRT'  },
    { value:'finishing',            label:'Finishing',            short:'FIN'  },
    { value:'qc',                   label:'QC',                   short:'QCI'  },
    { value:'packing',              label:'Packing',               short:'PKG'  },
    { value:'shipped_to_solidiphy', label:'Shipped to Solidiphy', short:'STS'  },
    { value:'received_accepted',    label:'Received & Accepted',  short:'RCVA' },
    { value:'received_rejected',    label:'Received & Rejected',  short:'RCVR' },
    { value:'ready_to_ship',        label:'Ready to Ship',        short:'RTS'  },
    { value:'shipped_to_customer',  label:'Shipped to Customer',  short:'STC'  },
    { value:'cancelled',            label:'Cancelled',            short:'CXL'  },
  ];

  return (
    <div className="admin-layout">
      {/* ── Sidebar ── */}
      <div className="admin-sidebar">
        <div className="admin-sidebar-logo">
          <div className="admin-sidebar-logo-text">Solidiphy</div>
          <div className="admin-sidebar-logo-sub">Admin Portal</div>
        </div>

          <div style={{ padding:'8px 0', flex:1 }}>
          <NavItem id="dashboard"  icon="📊" label="Dashboard"   tab={tab} setTab={setTab} setEditingId={setEditingId} setSelOrder={setSelOrder} setAssigningProduct={setAssigningProduct} />

          <div className="admin-sidebar-section">Operations</div>
          <NavItem id="orders"     icon="📋" label="Orders"      tab={tab} setTab={setTab} setEditingId={setEditingId} setSelOrder={setSelOrder} setAssigningProduct={setAssigningProduct} />
          <NavItem id="line-items" icon="🔩" label="Line Items"  tab={tab} setTab={setTab} setEditingId={setEditingId} setSelOrder={setSelOrder} setAssigningProduct={setAssigningProduct} />
          <NavItem id="meshy-tasks" icon="🤖" label="3D Tasks" tab={tab} setTab={setTab} setEditingId={setEditingId} setSelOrder={setSelOrder} setAssigningProduct={setAssigningProduct} />
          <NavItem id="operations" icon="⚙️" label="Operations" tab={tab} setTab={setTab} setEditingId={setEditingId} setSelOrder={setSelOrder} setAssigningProduct={setAssigningProduct} />

          <div className="admin-sidebar-section">Partners</div>
          <NavItem id="companies"  icon="🏢" label="Companies"   tab={tab} setTab={setTab} setEditingId={setEditingId} setSelOrder={setSelOrder} setAssigningProduct={setAssigningProduct} />
          <NavItem id="groups"     icon="👥" label="Groups"      tab={tab} setTab={setTab} setEditingId={setEditingId} setSelOrder={setSelOrder} setAssigningProduct={setAssigningProduct} />

          <div className="admin-sidebar-section">Catalog</div>
          <NavItem id="products"   icon="📦" label="Products"    tab={tab} setTab={setTab} setEditingId={setEditingId} setSelOrder={setSelOrder} setAssigningProduct={setAssigningProduct} />
          <NavItem id="coupons"    icon="🏷️" label="Coupons"     tab={tab} setTab={setTab} setEditingId={setEditingId} setSelOrder={setSelOrder} setAssigningProduct={setAssigningProduct} />

          <div className="admin-sidebar-section">Finance</div>
          <NavItem id="volume-discounts" icon="💰" label="Volume Discounts" tab={tab} setTab={setTab} setEditingId={setEditingId} setSelOrder={setSelOrder} setAssigningProduct={setAssigningProduct} />
          <NavItem id="settlements" icon="🧾" label="Partner Settlements" tab={tab} setTab={setTab} setEditingId={setEditingId} setSelOrder={setSelOrder} setAssigningProduct={setAssigningProduct} />
          <NavItem id="invoices"    icon="📄" label="Invoices"            tab={tab} setTab={setTab} setEditingId={setEditingId} setSelOrder={setSelOrder} setAssigningProduct={setAssigningProduct} />

          <div className="admin-sidebar-section">Admin</div>
          <NavItem id="admin-users" icon="🔐" label="Admin Users" tab={tab} setTab={setTab} setEditingId={setEditingId} setSelOrder={setSelOrder} setAssigningProduct={setAssigningProduct} />
          <NavItem id="psp-companies"  icon="🏭" label="PSP Companies"  tab={tab} setTab={setTab} setEditingId={setEditingId} setSelOrder={setSelOrder} setAssigningProduct={setAssigningProduct} />
          <NavItem id="psp-users"      icon="🖨️" label="PSP Users"      tab={tab} setTab={setTab} setEditingId={setEditingId} setSelOrder={setSelOrder} setAssigningProduct={setAssigningProduct} />
          <a className="admin-nav-item" href="/psp?admin=1" target="_blank" rel="noreferrer" style={{ textDecoration:'none' }}>
            <span>🔗</span><span>PSP Portal ↗</span>
          </a>
          <NavItem id="packing-slips"  icon="📦" label="Packing Slips"  tab={tab} setTab={setTab} setEditingId={setEditingId} setSelOrder={setSelOrder} setAssigningProduct={setAssigningProduct} />
          <NavItem id="settings"       icon="⚙️" label="Settings"       tab={tab} setTab={setTab} setEditingId={setEditingId} setSelOrder={setSelOrder} setAssigningProduct={setAssigningProduct} />
          <NavItem id="api-integrations" icon="🔌" label="API Integrations" tab={tab} setTab={setTab} setEditingId={setEditingId} setSelOrder={setSelOrder} setAssigningProduct={setAssigningProduct} />
        </div>
        <div style={{ padding:'12px 16px', borderTop:'1px solid rgba(255,255,255,0.1)' }}>
          <button className="btn" style={{ width:'100%' }} onClick={handleAdminLogout}>Sign Out</button>
        </div>
      </div>

      {/* ── Main Content ── */}
      <div className="admin-content">

        {/* Stats dashboard */}
        {tab === 'dashboard' && stats && (
          <div>
            {stats.expiredTaxExemptGroups?.length > 0 && (
              <div className="card" style={{
                marginBottom:16, borderLeft:'4px solid var(--danger)', background:'rgba(220,53,69,0.05)',
              }}>
                <div style={{ fontWeight:600, color:'var(--danger)', marginBottom:8 }}>
                  ⚠️ {stats.expiredTaxExemptGroups.length} tax exemption{stats.expiredTaxExemptGroups.length === 1 ? '' : 's'} expired
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                  {stats.expiredTaxExemptGroups.map(g => (
                    <div key={g.id} style={{ fontSize:13, display:'flex', justifyContent:'space-between' }}>
                      <span>{g.companyName} — {g.name}</span>
                      <span style={{ color:'var(--text2)' }}>expired {g.taxExemptExpiresAt}</span>
                    </div>
                  ))}
                </div>
                <button className="btn btn-sm" style={{ marginTop:10 }} onClick={() => setTab('groups')}>Review groups</button>
              </div>
            )}
            {stats.taxFallbackOrders?.length > 0 && (
              <div className="card" style={{
                marginBottom:16, borderLeft:'4px solid var(--danger)', background:'rgba(220,53,69,0.05)',
              }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                  <div style={{ fontWeight:600, color:'var(--danger)' }}>
                    ⚠️ {stats.taxFallbackOrders.length} order{stats.taxFallbackOrders.length === 1 ? '' : 's'} used the tax fallback rate (Stripe Tax failed)
                  </div>
                  <button className="btn btn-sm" onClick={async () => {
                    try {
                      const { data } = await dismissAllTaxFallback();
                      toast(`Dismissed ${data.dismissed} order${data.dismissed === 1 ? '' : 's'}`, 'success');
                      loadAll();
                    } catch (err) { toast('Failed to dismiss: ' + (err.response?.data?.error || err.message), 'error'); }
                  }}>Dismiss all</button>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                  {stats.taxFallbackOrders.map(o => (
                    <div key={o.id} style={{ fontSize:13, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <span>{o.orderNumber} — {o.companyName} / {o.groupName}</span>
                      <span style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <span style={{ color:'var(--text2)' }}>{fmtMoney(o.taxAmount)} · {fmtDate(o.createdAt)}</span>
                        <button className="btn btn-sm" onClick={async () => {
                          try {
                            await dismissTaxFallback(o.id);
                            loadAll();
                          } catch (err) { toast('Failed to dismiss: ' + (err.response?.data?.error || err.message), 'error'); }
                        }}>Dismiss</button>
                      </span>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize:11, color:'var(--text2)', marginTop:8 }}>Worth double-checking these tax amounts are reasonable for their shipping destination.</div>
              </div>
            )}
            {/* Condensed status + active-count strip — was a 12-card grid + separate
                3-card grid; folded into one wrapped row of small stat chips so this
                doesn't eat several screens of vertical space before you even get to
                revenue. */}
            <div className="card" style={{ marginBottom:12, padding:'10px 16px' }}>
              <div style={{ display:'flex', flexWrap:'wrap', gap:'6px 22px' }}>
                {[
                  { label:'Total active orders', value: stats.total,              color:'var(--admin-nav)' },
                  { label:'Awaiting photo',       value: stats.pending,           color:'var(--warning)' },
                  { label:'In Process',          value: stats.inProcess,          color:'#6A1B9A' },
                  { label:'Quality (MQC)',       value: stats.mqc,                color:'#8E44AD' },
                  { label:'Ready to model',      value: stats.readyToModel,       color:'#2E86C1' },
                  { label:'PSP production',      value: stats.pspProduction,      color:'#D97706' },
                  { label:'Shipped to Solidiphy',value: stats.shippedToSolidiphy, color:'#6A1B9A' },
                  { label:'Received & accepted', value: stats.receivedAccepted,   color:'var(--success)' },
                  { label:'Received & rejected', value: stats.receivedRejected,   color:'var(--danger)' },
                  { label:'Ready to ship',       value: stats.readyToShip,        color:'#059669' },
                  { label:'Active companies', value: stats.activeCompanies, color:'var(--admin-nav)' },
                  { label:'Active groups',    value: stats.activeGroups,    color:'var(--admin-nav)' },
                  { label:'Active products',  value: stats.activeProducts,  color:'var(--admin-nav)' },
                ].map(s => (
                  <div key={s.label} style={{ display:'flex', alignItems:'baseline', gap:5 }}>
                    <span style={{ fontSize:15, fontWeight:700, color:s.color }}>{s.value}</span>
                    <span style={{ fontSize:11, color:'var(--text3)' }}>{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
            {stats.pspStages && (
              <div className="card" style={{ marginBottom:16, padding:'10px 16px' }}>
                <div style={{ display:'flex', flexWrap:'wrap', alignItems:'center', gap:'6px 20px' }}>
                  <div style={{ fontWeight:600, fontSize:12, color:'var(--text3)' }}>PSP by stage:</div>
                  {[
                    { key:'sculpting',    label:'Sculpting' },
                    { key:'ready_to_print', label:'Ready to Print' },
                    { key:'printing',     label:'Printing' },
                    { key:'finishing',    label:'Finishing' },
                    { key:'qc',           label:'QC' },
                    { key:'packing',      label:'Packing' },
                  ].map(st => (
                    <div key={st.key} style={{ display:'flex', alignItems:'baseline', gap:5 }}>
                      <span style={{ fontSize:14, fontWeight:700, color:'#D97706' }}>{stats.pspStages[st.key] || 0}</span>
                      <span style={{ fontSize:11, color:'var(--text3)' }}>{st.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Order Outcomes — Shipped to Customer / Cancelled, deliberately
                on their OWN date range, not shared with Revenue by Company's
                picker below. Cancelled is derived server-side from the audit
                trail (latest cancel event per order). ── */}
            {stats.orderOutcomes && (
              <div className="card" style={{ marginBottom:16, padding:'12px 16px' }}>
                <div style={{ fontWeight:600, marginBottom:8 }}>Order Outcomes</div>
                <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:12 }}>
                  {[
                    { id:'all', label:'All time' },
                    { id:'7',   label:'Last 7 days' },
                    { id:'30',  label:'Last 30 days' },
                    { id:'60',  label:'Last 60 days' },
                    { id:'custom', label:'Custom range' },
                  ].map(r => (
                    <button
                      key={r.id}
                      className="btn btn-sm"
                      style={outcomeRange === r.id
                        ? { background:'var(--admin-nav)', color:'#fff', borderColor:'var(--admin-nav)' }
                        : {}}
                      onClick={() => setOutcomeRange(r.id)}
                    >{r.label}</button>
                  ))}
                  {outcomeRange === 'custom' && (
                    <>
                      <input type="date" className="input" style={{ width:150 }} value={outcomeFrom} onChange={e => setOutcomeFrom(e.target.value)} />
                      <span style={{ color:'var(--text3)' }}>to</span>
                      <input type="date" className="input" style={{ width:150 }} value={outcomeTo} onChange={e => setOutcomeTo(e.target.value)} />
                    </>
                  )}
                </div>
                <div style={{ display:'flex', gap:'6px 22px', flexWrap:'wrap' }}>
                  <div style={{ display:'flex', alignItems:'baseline', gap:5 }}>
                    <span style={{ fontSize:20, fontWeight:700, color:'var(--success)' }}>{stats.orderOutcomes.shipped}</span>
                    <span style={{ fontSize:11, color:'var(--text3)' }}>Shipped to customer</span>
                  </div>
                  <div style={{ display:'flex', alignItems:'baseline', gap:5 }}>
                    <span style={{ fontSize:20, fontWeight:700, color:'var(--danger)' }}>{stats.orderOutcomes.cancelled}</span>
                    <span style={{ fontSize:11, color:'var(--text3)' }}>Cancelled</span>
                  </div>
                </div>
                <div style={{ fontSize:11, color:'var(--text3)', marginTop:8 }}>Shipped is by ship date. Cancelled reflects orders placed at any time whose most recent cancellation happened in this range (via audit trail).</div>
              </div>
            )}

            {/* ── Revenue by Company — line chart, then bar chart, then table.
                Always rendered — NEVER gated on data presence, since the
                date-range picker lives inside this card. Gating the whole
                card on revenueByCompany.length was the bug: an empty range
                (e.g. Last 7 days with no orders) made the picker itself
                vanish, with no way to change the range back. ── */}
            {stats && (
              <div className="card" style={{ marginBottom:16 }}>
                <div style={{ fontWeight:600, marginBottom:2 }}>Revenue by Company</div>
                <div style={{ fontSize:11, color:'var(--text3)', marginBottom:12 }}>
                  Sales Revenue = orders placed in the selected range. Gross Revenue = orders shipped in that range. Net Revenue = Gross − COGS. Tax is excluded from all three (collected sales tax isn't real revenue); shipping is included as profit since the flat rate charged exceeds actual carrier cost.
                </div>

                {/* Line chart — stacked area, one band per checked company, independent
                    granularity/time-window from the top-level date range above. */}
                <div style={{ fontWeight:600 }}>Revenue Trend</div>
                <div style={{ marginTop:8, marginBottom:10, display:'flex', gap:12, alignItems:'center', flexWrap:'wrap' }}>
                  <div style={{ display:'flex', gap:4 }}>
                    {[
                      { id:'daily',   label:'Daily' },
                      { id:'weekly',  label:'Weekly' },
                      { id:'monthly', label:'Monthly' },
                      { id:'3month',  label:'3-Month' },
                      { id:'yearly',  label:'Yearly' },
                    ].map(g => (
                      <button
                        key={g.id}
                        className="btn btn-sm"
                        style={trendGranularity === g.id ? { background:'var(--admin-nav)', color:'#fff', borderColor:'var(--admin-nav)' } : {}}
                        onClick={() => setTrendGranularity(g.id)}
                      >{g.label}</button>
                    ))}
                  </div>
                  <select className="input" style={{ width:160 }} value={trendMetric} onChange={e => setTrendMetric(e.target.value)}>
                    {REVENUE_METRICS.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
                  </select>
                </div>
                {!trendData
                  ? <div style={{ color:'var(--text3)', fontSize:13, padding:'20px 0' }}>Loading…</div>
                  : (
                    <>
                      <div style={{ display:'flex', flexWrap:'wrap', gap:'6px 14px', marginBottom:10 }}>
                        {trendData.companies.map((c, i) => (
                          <label key={c} style={{ display:'flex', alignItems:'center', gap:5, fontSize:12, cursor:'pointer' }}>
                            <input
                              type="checkbox"
                              checked={trendCompanies.includes(c)}
                              onChange={() => setTrendCompanies(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c])}
                            />
                            <span style={{ color:PIE_COLORS[i % PIE_COLORS.length] }}>●</span> {c}
                          </label>
                        ))}
                      </div>
                      {trendCompanies.length === 0
                        ? <div style={{ color:'var(--text3)', fontSize:13, padding:'20px 0' }}>Select at least one company above.</div>
                        : (
                          <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={trendData.periods.map(p => ({
                              label: p.label,
                              ...Object.fromEntries(trendCompanies.map(c => [c, p.companies[c]?.[trendMetric] || 0])),
                            }))}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="label" tick={{ fontSize:11 }} />
                              <YAxis tick={{ fontSize:11 }} tickFormatter={v => fmtMoney(v)} />
                              <Tooltip formatter={v => fmtMoney(v)} />
                              <Legend />
                              {trendCompanies.map((c, i) => (
                                <Area key={c} type="monotone" dataKey={c} stackId="1" stroke={PIE_COLORS[i % PIE_COLORS.length]} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                              ))}
                            </AreaChart>
                          </ResponsiveContainer>
                        )}
                    </>
                  )}

                {/* Date range — applies to everything from here down (Revenue by
                    Company chart/table, Products Sold). The Revenue Trend chart
                    above has its own independent granularity/window and ignores
                    this. */}
                <div style={{ marginTop:24, paddingTop:20, borderTop:'1px solid var(--border)' }}>
                  <div style={{ fontSize:11, color:'var(--text3)', marginBottom:8 }}>Date range below — Sales Revenue by order date placed, Gross/Net Revenue and Products Sold by ship date.</div>
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                    {[
                      { id:'all', label:'All time' },
                      { id:'7',   label:'Last 7 days' },
                      { id:'30',  label:'Last 30 days' },
                      { id:'60',  label:'Last 60 days' },
                      { id:'custom', label:'Custom range' },
                    ].map(r => (
                      <button
                        key={r.id}
                        className="btn btn-sm"
                        style={dashRange === r.id
                          ? { background:'var(--admin-nav)', color:'#fff', borderColor:'var(--admin-nav)' }
                          : {}}
                        onClick={() => setDashRange(r.id)}
                      >{r.label}</button>
                    ))}
                    {dashRange === 'custom' && (
                      <>
                        <input type="date" className="input" style={{ width:150 }} value={dashFrom} onChange={e => setDashFrom(e.target.value)} />
                        <span style={{ color:'var(--text3)' }}>to</span>
                        <input type="date" className="input" style={{ width:150 }} value={dashTo} onChange={e => setDashTo(e.target.value)} />
                      </>
                    )}
                  </div>
                </div>

                {/* Bar chart — checkboxes; grouped, not stacked (Sales/Gross/Net aren't additive) */}
                <div style={{ marginTop:24, paddingTop:20, borderTop:'1px solid var(--border)' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10, flexWrap:'wrap', gap:8 }}>
                    <div style={{ fontWeight:600 }}>Revenue by Company — Chart</div>
                    <div style={{ display:'flex', gap:14 }}>
                      {REVENUE_METRICS.map(m => (
                        <label key={m.key} style={{ display:'flex', alignItems:'center', gap:5, fontSize:12, cursor:'pointer' }}>
                          <input
                            type="checkbox"
                            checked={barMetrics.includes(m.key)}
                            onChange={() => setBarMetrics(prev => prev.includes(m.key) ? prev.filter(k => k !== m.key) : [...prev, m.key])}
                          />
                          <span style={{ color:m.color }}>●</span> {m.label}
                        </label>
                      ))}
                    </div>
                  </div>
                  {barMetrics.length === 0
                    ? <div style={{ color:'var(--text3)', fontSize:13, padding:'20px 0' }}>Select at least one metric above.</div>
                    : stats.revenueByCompany.length === 0
                    ? <div style={{ color:'var(--text3)', fontSize:13, padding:'20px 0' }}>No revenue in the selected date range.</div>
                    : (
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={stats.revenueByCompany}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="id" tick={{ fontSize:11 }} />
                          <YAxis tick={{ fontSize:11 }} tickFormatter={v => fmtMoney(v)} />
                          <Tooltip formatter={v => fmtMoney(v)} />
                          <Legend />
                          {barMetrics.map(k => {
                            const m = REVENUE_METRICS.find(x => x.key === k);
                            return <Bar key={k} dataKey={k} name={m.label} fill={m.color} />;
                          })}
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                </div>

                {/* Table, now last */}
                <div style={{ marginTop:24, paddingTop:20, borderTop:'1px solid var(--border)', overflowX:'auto' }}>
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>Company</th>
                        <th style={{ textAlign:'right' }}>Sales Revenue</th>
                        <th style={{ textAlign:'right' }}>Sales Orders</th>
                        <th style={{ textAlign:'right' }}>Gross Revenue</th>
                        <th style={{ textAlign:'right' }}>Net Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.revenueByCompany.map(r => (
                        <tr key={r.id}>
                          <td>{r.id}</td>
                          <td style={{ textAlign:'right' }}>{fmtMoney(r.salesRevenue)}</td>
                          <td style={{ textAlign:'right' }}>{r.salesOrders}</td>
                          <td style={{ textAlign:'right' }}>{fmtMoney(r.grossRevenue)}</td>
                          <td style={{ textAlign:'right', fontWeight:600, color:'var(--success)' }}>{fmtMoney(r.netRevenue)}</td>
                        </tr>
                      ))}
                      {stats.revenueTotals && (
                        <tr style={{ fontWeight:700, borderTop:'2px solid var(--border)' }}>
                          <td>Total</td>
                          <td style={{ textAlign:'right' }}>{fmtMoney(stats.revenueTotals.salesRevenue)}</td>
                          <td style={{ textAlign:'right' }}>{stats.revenueTotals.salesOrders}</td>
                          <td style={{ textAlign:'right' }}>{fmtMoney(stats.revenueTotals.grossRevenue)}</td>
                          <td style={{ textAlign:'right', color:'var(--success)' }}>{fmtMoney(stats.revenueTotals.netRevenue)}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── Products Sold — pie by SKU first, then bar by company. Always
                rendered, same reasoning as the revenue card above — hiding the
                whole card on an empty range is jarring and looks broken. ── */}
            {stats && (
              <div className="card" style={{ marginBottom:16 }}>
                <div style={{ fontWeight:600, marginBottom:2 }}>Products Sold</div>
                <div style={{ fontSize:11, color:'var(--text3)', marginBottom:12 }}>
                  Units on shipped orders in the selected date range.
                </div>

                <div style={{ marginBottom:8, fontSize:13, fontWeight:500 }}>By SKU</div>
                {!stats.productsSoldBySku?.length
                  ? <div style={{ color:'var(--text3)', fontSize:13, padding:'20px 0' }}>No products sold in the selected date range.</div>
                  : (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie data={stats.productsSoldBySku} dataKey="qty" nameKey="id" cx="50%" cy="50%" outerRadius={110} label={e => e.id}>
                          {stats.productsSoldBySku.map((r, i) => <Cell key={r.id} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  )}

                <div style={{ margin:'24px 0 8px', paddingTop:20, borderTop:'1px solid var(--border)', fontSize:13, fontWeight:500 }}>By Company (stacked by product)</div>
                {!stats.productsSoldByCompany?.length
                  ? <div style={{ color:'var(--text3)', fontSize:13, padding:'20px 0' }}>No products sold in the selected date range.</div>
                  : (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={stats.productsSoldByCompanyStacked || stats.productsSoldByCompany}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="id" tick={{ fontSize:11 }} />
                        <YAxis tick={{ fontSize:11 }} allowDecimals={false} />
                        <Tooltip />
                        <Legend />
                        {(stats.productSkuOrder || []).map((sku, i) => (
                          <Bar key={sku} dataKey={sku} name={sku} stackId="products" fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  )}
              </div>
            )}
          </div>
        )}




        {/* ── COMPANIES ── */}
        {tab === 'companies' && (
          <div>
            <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:12 }}>
              <button className="btn btn-primary" onClick={() => { setEditingId(null); setCompanyForm(emptyCompany); setTab('company-form'); }}>+ Add company</button>
            </div>
            <div className="card" style={{ padding:0, overflow:'hidden' }}>
              {companies.length === 0
                ? <div style={{ padding:40, textAlign:'center', color:'var(--text3)' }}>No companies yet.</div>
                : (
                  <table className="tbl">
                    <thead><tr><th>Company</th><th>Contact</th><th>Brand</th><th>OneDrive</th><th>Status</th><th></th></tr></thead>
                    <tbody>
                      {companies.map(c => (
                        <tr key={c.id} onClick={() => setSelCompany(c)}>
                          <td>
                            <div style={{ fontWeight:500 }}>{c.name}</div>
                            <div style={{ fontSize:11, color:'var(--text3)' }}>{c.slug}</div>
                          </td>
                          <td style={{ fontSize:12 }}>
                            {c.contactName && <div>{c.contactName}</div>}
                            {c.contactEmail && <div style={{ color:'var(--text3)' }}>{c.contactEmail}</div>}
                          </td>
                          <td>
                            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                              {c.brandLogo && <img src={c.brandLogo} alt="" style={{ height:24, objectFit:'contain' }} />}
                              <div style={{ width:16, height:16, borderRadius:4, background:c.brandColor||'#0077B6' }} />
                              <span style={{ fontSize:12 }}>{c.brandName||c.name}</span>
                            </div>
                          </td>
                          <td style={{ fontSize:12, color:'var(--text2)' }}>{c.onedriveFolder||'—'}</td>
                          <td><span className={`badge ${c.active?'badge-fulfilled':'badge-cancelled'}`}>{c.active?'Active':'Inactive'}</span></td>
                          <td onClick={e => e.stopPropagation()}>
                            <div style={{ display:'flex', gap:6 }}>
                              <button className="btn btn-sm" onClick={() => editCompany(c)}>Edit</button>
                              <button className="btn btn-danger btn-sm" onClick={async () => { if(window.confirm('Delete?')) { await deleteCompany(c.id); await loadAll(); } }}>Delete</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              }
            </div>
          </div>
        )}

        {/* ── COMPANY FORM ── */}
        {tab === 'company-form' && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
            <div className="card">
              <div style={{ fontWeight:600, marginBottom:16 }}>{editingId ? 'Edit company' : 'Add company'}</div>
              <div className="field"><label className="label">Company name *</label><input className="input" value={companyForm.name} onChange={e => setCompanyForm(p => ({ ...p, name:e.target.value, slug: p.slug||e.target.value.toLowerCase().replace(/\s+/g,'-').replace(/[^a-z0-9-]/g,'') }))} /></div>
              <div className="field"><label className="label">Slug *</label><input className="input" value={companyForm.slug} onChange={e => setCompanyForm(p => ({ ...p, slug:e.target.value.toLowerCase().replace(/\s+/g,'-') }))} /></div>
              <div className="field"><label className="label">Display name</label><input className="input" value={companyForm.brandName} onChange={e => setCompanyForm(p => ({ ...p, brandName:e.target.value }))} /></div>
              <div style={{ fontWeight:500, fontSize:12, color:'var(--text2)', margin:'12px 0 8px', textTransform:'uppercase', letterSpacing:'.04em' }}>Contact info</div>
              <div className="grid2">
                <div className="field"><label className="label">Contact name</label><input className="input" value={companyForm.contactName} onChange={e => setCompanyForm(p => ({ ...p, contactName:e.target.value }))} /></div>
                <div className="field"><label className="label">Job title</label><input className="input" value={companyForm.contactTitle} onChange={e => setCompanyForm(p => ({ ...p, contactTitle:e.target.value }))} placeholder="e.g. Sales Manager" /></div>
              </div>
              <div className="grid2">
                <div className="field"><label className="label">Contact phone</label><input className="input" value={companyForm.contactPhone} onChange={e => setCompanyForm(p => ({ ...p, contactPhone:e.target.value }))} /></div>
                <div className="field"><label className="label">Contact email</label><input className="input" type="email" value={companyForm.contactEmail} onChange={e => setCompanyForm(p => ({ ...p, contactEmail:e.target.value }))} /></div>
              </div>
              <div style={{ fontWeight:500, fontSize:12, color:'var(--text2)', margin:'12px 0 8px', textTransform:'uppercase', letterSpacing:'.04em' }}>Address</div>
              <div className="field"><label className="label">Company name (for mailing address)</label><input className="input" value={companyForm.addressCompanyName} onChange={e => setCompanyForm(p => ({ ...p, addressCompanyName:e.target.value }))} placeholder="e.g. Carnival Corporation (if different from display name)" /></div>
              <div className="field"><label className="label">Address line 1</label><input className="input" value={companyForm.address1} onChange={e => setCompanyForm(p => ({ ...p, address1:e.target.value }))} /></div>
              <div className="field"><label className="label">Address line 2</label><input className="input" value={companyForm.address2} onChange={e => setCompanyForm(p => ({ ...p, address2:e.target.value }))} /></div>
              <div className="grid2">
                <div className="field"><label className="label">City</label><input className="input" value={companyForm.city} onChange={e => setCompanyForm(p => ({ ...p, city:e.target.value }))} /></div>
                <div className="field"><label className="label">State</label><input className="input" value={companyForm.state} onChange={e => setCompanyForm(p => ({ ...p, state:e.target.value }))} /></div>
              </div>
              <div className="grid2">
                <div className="field"><label className="label">ZIP</label><input className="input" value={companyForm.zip} onChange={e => setCompanyForm(p => ({ ...p, zip:e.target.value }))} /></div>
                <div className="field"><label className="label">Country</label><input className="input" value={companyForm.country} onChange={e => setCompanyForm(p => ({ ...p, country:e.target.value }))} /></div>
              </div>

              <div style={{ fontWeight:600, margin:'20px 0 4px' }}>Bill To Address</div>
              <div style={{ fontSize:11, color:'var(--text3)', marginBottom:8 }}>Invoicing/AP address, if different from the company address above. Leave blank to use the company address on invoices.</div>
              <div className="field"><label className="label">Company name</label><input className="input" value={companyForm.billToCompanyName} onChange={e => setCompanyForm(p => ({ ...p, billToCompanyName:e.target.value }))} /></div>
              <div className="field"><label className="label">Address line 1</label><input className="input" value={companyForm.billToAddress1} onChange={e => setCompanyForm(p => ({ ...p, billToAddress1:e.target.value }))} /></div>
              <div className="field"><label className="label">Address line 2</label><input className="input" value={companyForm.billToAddress2} onChange={e => setCompanyForm(p => ({ ...p, billToAddress2:e.target.value }))} /></div>
              <div className="grid2">
                <div className="field"><label className="label">City</label><input className="input" value={companyForm.billToCity} onChange={e => setCompanyForm(p => ({ ...p, billToCity:e.target.value }))} /></div>
                <div className="field"><label className="label">State</label><input className="input" value={companyForm.billToState} onChange={e => setCompanyForm(p => ({ ...p, billToState:e.target.value }))} /></div>
              </div>
              <div className="grid2">
                <div className="field"><label className="label">ZIP</label><input className="input" value={companyForm.billToZip} onChange={e => setCompanyForm(p => ({ ...p, billToZip:e.target.value }))} /></div>
                <div className="field"><label className="label">Country</label><input className="input" value={companyForm.billToCountry} onChange={e => setCompanyForm(p => ({ ...p, billToCountry:e.target.value }))} /></div>
              </div>

              <div style={{ fontWeight:600, margin:'20px 0 4px' }}>Ship To Address</div>
              <div style={{ fontSize:11, color:'var(--text3)', marginBottom:8 }}>Physical delivery destination (e.g. a warehouse or port agent), if different from Bill To. Leave blank to fall back to Bill To, then the company address.</div>
              <div className="field"><label className="label">Company name</label><input className="input" value={companyForm.shipToCompanyName} onChange={e => setCompanyForm(p => ({ ...p, shipToCompanyName:e.target.value }))} /></div>
              <div className="field"><label className="label">Address line 1</label><input className="input" value={companyForm.shipToAddress1} onChange={e => setCompanyForm(p => ({ ...p, shipToAddress1:e.target.value }))} /></div>
              <div className="field"><label className="label">Address line 2</label><input className="input" value={companyForm.shipToAddress2} onChange={e => setCompanyForm(p => ({ ...p, shipToAddress2:e.target.value }))} /></div>
              <div className="grid2">
                <div className="field"><label className="label">City</label><input className="input" value={companyForm.shipToCity} onChange={e => setCompanyForm(p => ({ ...p, shipToCity:e.target.value }))} /></div>
                <div className="field"><label className="label">State</label><input className="input" value={companyForm.shipToState} onChange={e => setCompanyForm(p => ({ ...p, shipToState:e.target.value }))} /></div>
              </div>
              <div className="grid2">
                <div className="field"><label className="label">ZIP</label><input className="input" value={companyForm.shipToZip} onChange={e => setCompanyForm(p => ({ ...p, shipToZip:e.target.value }))} /></div>
                <div className="field"><label className="label">Country</label><input className="input" value={companyForm.shipToCountry} onChange={e => setCompanyForm(p => ({ ...p, shipToCountry:e.target.value }))} /></div>
              </div>
              <div className="field"><label className="label">Invoice email</label><input className="input" type="email" value={companyForm.invoiceEmail} onChange={e => setCompanyForm(p => ({ ...p, invoiceEmail:e.target.value }))} /></div>
              <div className="field">
                <label className="label">Billing model</label>
                <select className="input" value={companyForm.billingModel}
                  onChange={e => setCompanyForm(p => ({ ...p, billingModel:e.target.value }))}>
                  <option value="invoice_retail">Invoice / Retail (hospitality, cruise — invoiced, revenue-shared)</option>
                  <option value="direct_wholesale">Direct / Wholesale (photo studios — card charged at checkout)</option>
                </select>
              </div>
              <div className="field">
                <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}>
                  <input type="checkbox" checked={!!companyForm.freeShippingEnabled}
                    onChange={e => setCompanyForm(p => ({ ...p, freeShippingEnabled:e.target.checked }))} />
                  <span className="label" style={{ margin:0 }}>Free shipping (domestic)</span>
                </label>
                <div style={{ fontSize:11, color:'var(--text2)', marginTop:4 }}>
                  Domestic (US/CA) checkout shows $0.00 shipping — cost is assumed baked into retail pricing.
                  International still charges the reduced free-shipping international rate (set under Settings → Shipping Rates).
                  Default: unchecked.
                </div>
              </div>
              {companyForm.billingModel === 'invoice_retail' && (
                <div className="field">
                  <label className="label">Revenue share % (e.g. 15 for 15%)</label>
                  <input className="input" type="number" step="0.01" min="0" max="100" value={companyForm.revenuePercent}
                    onChange={e => setCompanyForm(p => ({ ...p, revenuePercent:e.target.value }))} placeholder="0" />
                  <div style={{ fontSize:11, color:'var(--text2)', marginTop:4 }}>
                    Partner's share of true product margin, kept by the partner — creates a partner_settlements record once the order ships. Leave blank/0 if this company doesn't get a revenue share.
                  </div>
                </div>
              )}
              {companyForm.billingModel === 'invoice_retail' && (
                <>
                  <div style={{ fontWeight:600, margin:'16px 0 8px' }}>Invoice</div>
                  <div className="field">
                    <label className="label">Invoice format</label>
                    <select className="input" value={companyForm.invoiceFormat}
                      onChange={e => setCompanyForm(p => ({ ...p, invoiceFormat:e.target.value }))}>
                      <option value="revenue_summary">Revenue summary (summary page + order backup)</option>
                      <option value="line_item">Line item, non-PO (qty × unit price + ship spreadsheet)</option>
                    </select>
                  </div>
                  {companyForm.invoiceFormat === 'line_item' && (
                    <>
                      <div className="grid2">
                        <div className="field"><label className="label">P.O. Number</label><input className="input" value={companyForm.invoicePoNumber} onChange={e => setCompanyForm(p => ({ ...p, invoicePoNumber:e.target.value }))} /></div>
                        <div className="field"><label className="label">Terms</label><input className="input" value={companyForm.invoiceTerms} onChange={e => setCompanyForm(p => ({ ...p, invoiceTerms:e.target.value }))} /></div>
                      </div>
                      <div className="grid2">
                        <div className="field"><label className="label">Payment due</label><input className="input" value={companyForm.invoicePaymentDue} onChange={e => setCompanyForm(p => ({ ...p, invoicePaymentDue:e.target.value }))} /></div>
                        <div className="field"><label className="label">Shipping method</label><input className="input" value={companyForm.invoiceVia} onChange={e => setCompanyForm(p => ({ ...p, invoiceVia:e.target.value }))} /></div>
                      </div>
                      <div className="grid2">
                        <div className="field"><label className="label">Customer ID</label><input className="input" value={companyForm.invoiceCustomerId} maxLength={50} placeholder={companyForm.billToCompanyName || companyForm.addressCompanyName || companyForm.name || 'Blank = the Bill To company name'} onChange={e => setCompanyForm(p => ({ ...p, invoiceCustomerId:e.target.value }))} /></div>
                      </div>
                    </>
                  )}
                </>
              )}
              <div className="field"><label className="label">OneDrive folder</label><input className="input" value={companyForm.onedriveFolder} onChange={e => setCompanyForm(p => ({ ...p, onedriveFolder:e.target.value }))} placeholder="e.g. Carnival" /></div>
              <div className="field">
                <label className="label">Order Disclaimer</label>
                <div style={{ fontSize:11, color:'var(--text3)', marginBottom:6 }}>
                  Shown to photographers at checkout next to the agreement checkbox (e.g. "By placing this order you agree to..."). Supports bold, italic, bullet points, headings — paste from Word or Google Docs. The global Terms &amp; Conditions and Privacy Statement are managed under <strong>Settings</strong>.
                </div>
                <RichEditor value={companyForm.orderDisclaimer} onChange={v => setCompanyForm(p => ({ ...p, orderDisclaimer:v }))} />
              </div>
            </div>
            <div className="card">
              <div style={{ fontWeight:600, marginBottom:16 }}>Branding</div>
              <div className="field">
                <label className="label">Brand color</label>
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                  <input type="color" value={companyForm.brandColor} onChange={e => setCompanyForm(p => ({ ...p, brandColor:e.target.value }))} style={{ width:40, height:36, padding:2, border:'1px solid var(--border2)', borderRadius:'var(--radius)', cursor:'pointer' }} />
                  <input className="input" value={companyForm.brandColor} onChange={e => setCompanyForm(p => ({ ...p, brandColor:e.target.value }))} />
                </div>
              </div>
              <div className="field">
                <label className="label">Logo</label>
                {companyForm.brandLogo && (
                  <div style={{ marginBottom:8 }}>
                    <img src={companyForm.brandLogo} alt="Logo" className="logo-preview" />
                    <button className="btn btn-danger btn-sm" onClick={() => setCompanyForm(p => ({ ...p, brandLogo:'' }))}>Remove logo</button>
                  </div>
                )}
                <input
                  id="logo-input"
                  type="file"
                  accept="image/*"
                  style={{ display:'none' }}
                  onChange={handleLogoUpload}
                  onClick={e => { e.target.value = ''; }}
                />
                <button type="button" className="btn btn-sm" onClick={() => document.getElementById('logo-input').click()}>
                  📁 {companyForm.brandLogo ? 'Replace logo' : 'Upload logo'}
                </button>
                <div style={{ fontSize:11, color:'var(--text3)', marginTop:4 }}>PNG, JPG, SVG — max 5MB</div>
              </div>
              {companyForm.brandColor && (
                <div style={{ background:companyForm.brandColor, borderRadius:'var(--radius)', padding:'10px 14px', color:'#fff', fontSize:13, fontWeight:500, marginBottom:16 }}>
                  Preview: {companyForm.brandName||companyForm.name||'Company name'}
                </div>
              )}
              <div style={{ display:'flex', gap:10, marginTop:8 }}>
                <button className="btn btn-primary" disabled={!companyForm.name||!companyForm.slug||saving} onClick={saveCompany}>
                  {saving ? <Spinner size={16} /> : (editingId ? 'Save changes' : 'Create company')}
                </button>
                <button className="btn" onClick={() => { setTab('companies'); setEditingId(null); }}>Cancel</button>
              </div>
            </div>

            {/* Products assigned to this company */}
            <div className="card" style={{ alignSelf:'start' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                <div style={{ fontWeight:600 }}>Assigned Products</div>
                {editingId && (
                  <button className="btn btn-sm btn-primary" onClick={() => {
                    setTab('products');
                    setAssignReturnTo('company-form');
                    setAssigningProduct(null);
                    setCpForm({ ...emptyCpForm, companyId: editingId });
                  }}>+ Assign product</button>
                )}
              </div>
              {!editingId
                ? <div style={{ fontSize:13, color:'var(--text3)', padding:'8px 0' }}>Save the company first, then assign products.</div>
                : (() => {
                    const companyProds = products.filter(p => (p.assignedCompanies||[]).some(c => c.id === editingId));
                    if (!companyProds.length) return <div style={{ fontSize:13, color:'var(--text3)', padding:'8px 0' }}>No products assigned yet.</div>;
                    return (
                      <table className="tbl" style={{ fontSize:12 }}>
                        <thead><tr><th>Product</th><th>SKU</th><th>Retail Price</th><th>Image</th><th></th></tr></thead>
                        <tbody>
                          {companyProds.map(p => {
                            const cp = p.assignedCompanies.find(c => c.id === editingId);
                            return (
                              <tr key={p.id}>
                                <td style={{ fontWeight:500 }}>{p.name}</td>
                                <td>{p.sku}</td>
                                <td>{fmtMoney(cp?.CompanyProduct?.retailPrice)}</td>
                                <td>{cp?.CompanyProduct?.imageUrl ? <img src={cp.CompanyProduct.imageUrl} alt="" style={{ width:30, height:30, objectFit:'contain' }} /> : '—'}</td>
                                <td>
                                  <div style={{ display:'flex', gap:6 }}>
                                    <button className="btn btn-sm" onClick={() => {
                                      setTab('products');
                                      setAssignReturnTo('company-form');
                                      setAssigningProduct(p);
                                      setCpForm({ id: cp?.CompanyProduct?.id, companyId: editingId, productId: p.id, retailPrice: cp?.CompanyProduct?.retailPrice || '', imageUrl: cp?.CompanyProduct?.imageUrl || '', sortOrder: cp?.CompanyProduct?.sortOrder || 0 });
                                    }}>Edit</button>
                                    <button className="btn btn-danger btn-sm" onClick={async () => {
                                      if (window.confirm(`Remove ${p.name} from this company?`)) {
                                        await import('../api').then(a => a.deleteCompanyProduct(cp?.CompanyProduct?.id));
                                        await loadAll();
                                      }
                                    }}>Remove</button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    );
                  })()
              }
            </div>
          </div>
        )}

        {/* ── GROUPS ── */}
        {tab === 'groups' && (
          <div>
            <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:12 }}>
              <button className="btn btn-primary" onClick={() => { setEditingId(null); setGroupForm({ ...emptyGroup, companyId: selCompany?.id||'' }); setTab('group-form'); }}>+ Add group</button>
            </div>
            <div className="card" style={{ padding:0, overflow:'hidden' }}>
              {filteredGroups.length === 0
                ? <div style={{ padding:40, textAlign:'center', color:'var(--text3)' }}>No groups yet.</div>
                : (
                  <table className="tbl">
                    <thead><tr><th>Group</th><th>Company</th><th>Location</th><th>Tax</th><th>Status</th><th></th></tr></thead>
                    <tbody>
                      {filteredGroups.map(g => (
                        <tr key={g.id} style={{ cursor:'default' }}>
                          <td style={{ fontWeight:500 }}>{g.name}</td>
                          <td><span style={{ background:'var(--brand-light)', color:'var(--brand)', padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:500 }}>{g.companyName}</span></td>
                          <td style={{ fontSize:12, color:'var(--text2)' }}>{g.location||'—'}</td>
                          <td>
                            {g.taxExempt ? (
                              g.taxExemptExpiresAt && g.taxExemptExpiresAt < new Date().toISOString().slice(0,10) ? (
                                <span style={{ fontSize:11, fontWeight:600, color:'var(--danger)' }}>⚠️ Exempt expired {g.taxExemptExpiresAt}</span>
                              ) : (
                                <span className="badge" style={{ background:'var(--bg)', color:'var(--text2)', border:'1px solid var(--border)' }}>Tax exempt{g.taxExemptExpiresAt ? ` · to ${g.taxExemptExpiresAt}` : ''}</span>
                              )
                            ) : (
                              <span style={{ fontSize:11 }}>{g.taxEnabled ? `✅ ${(parseFloat(g.taxRate||0)*100).toFixed(1)}%` : '—'}</span>
                            )}
                          </td>
                          <td><span className={`badge ${g.active?'badge-fulfilled':'badge-cancelled'}`}>{g.active?'Active':(g.hasPin?'Inactive':'Inactive · no PIN')}</span></td>
                          <td>
                            <div style={{ display:'flex', gap:6 }}>
                              <button className="btn btn-sm" onClick={() => editGroup(g)}>Edit</button>
                              <button className="btn btn-sm" disabled={!g.active && !g.hasPin} title={!g.active && !g.hasPin ? 'Add a PIN first' : undefined}
                                onClick={async () => { await updateGroup(g.id, { active: !g.active }); await loadAll(); }}>{g.active?'Deactivate':'Activate'}</button>
                              <button className="btn btn-danger btn-sm" onClick={async () => { if(window.confirm('Delete?')) { await deleteGroup(g.id); await loadAll(); } }}>Delete</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              }
            </div>
          </div>
        )}

        {/* ── GROUP FORM ── */}
        {tab === 'group-form' && (
          <div className="card" style={{ maxWidth:600 }}>
            <div style={{ fontWeight:600, marginBottom:16 }}>{editingId ? 'Edit group' : 'Add group'}</div>
            <div className="field"><label className="label">Company *</label>
              <select className="select" value={groupForm.companyId} onChange={e => setGroupForm(p => ({ ...p, companyId:e.target.value }))}>
                <option value="">Select company</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="grid2">
              <div className="field"><label className="label">Group name *</label><input className="input" value={groupForm.name} onChange={e => setGroupForm(p => ({ ...p, name:e.target.value }))} /></div>
              <div className="field"><label className="label">{editingId && groupForm.hasPin ? 'New PIN (blank = keep)' : 'PIN (min 4 digits — activates the group; blank = inactive)'}</label><input className="input" type="password" inputMode="numeric" value={groupForm.pin} onChange={e => setGroupForm(p => ({ ...p, pin:e.target.value }))} /></div>
            </div>
            <div className="field"><label className="label">Location</label><input className="input" placeholder="e.g. New York, NY" value={groupForm.location} onChange={e => setGroupForm(p => ({ ...p, location:e.target.value }))} /></div>
            <div className="field"><label className="label">Description</label><input className="input" value={groupForm.description} onChange={e => setGroupForm(p => ({ ...p, description:e.target.value }))} /></div>
            <div className="field"><label className="label">Invoice email</label><input className="input" type="email" value={groupForm.invoiceEmail} onChange={e => setGroupForm(p => ({ ...p, invoiceEmail:e.target.value }))} /></div>
            {(() => {
              // Entering a PIN for an invoice customer always lists the ship.
              const pinForcesSheet = !!groupForm.pin && companies.find(c => c.id == groupForm.companyId)?.billingModel === 'invoice_retail';
              return (
                <div style={{ marginBottom:14 }}>
                  <label className="toggle-label" style={{ opacity: pinForcesSheet ? 0.6 : 1 }}>
                    <input type="checkbox" checked={pinForcesSheet || groupForm.showOnInvoiceSpreadsheet} disabled={pinForcesSheet}
                      onChange={e => setGroupForm(p => ({ ...p, showOnInvoiceSpreadsheet:e.target.checked }))} />
                    <span>Show on invoice spreadsheet</span>
                  </label>
                </div>
              );
            })()}
            <div style={{ display:'flex', gap:16, alignItems:'flex-start', marginBottom:14 }}>
              <label className="toggle-label" style={{ opacity: groupForm.taxExempt ? 0.5 : 1 }}>
                <input type="checkbox" checked={groupForm.taxEnabled} disabled={groupForm.taxExempt}
                  onChange={e => setGroupForm(p => ({ ...p, taxEnabled:e.target.checked, taxExempt: e.target.checked ? false : p.taxExempt }))} />
                <span>
                  {companies.find(c => c.id == groupForm.companyId)?.billingModel === 'direct_wholesale'
                    ? 'Fallback tax rate (used if Stripe Tax fails)'
                    : 'Enable tax for this group'}
                </span>
              </label>
              {groupForm.taxEnabled && !groupForm.taxExempt && (
                <div className="field" style={{ width:220, marginBottom:0 }}>
                  <label className="label">Fallback rate (e.g. 0.08)</label>
                  <input className="input" type="number" step="0.001" value={groupForm.taxRate} onChange={e => setGroupForm(p => ({ ...p, taxRate:e.target.value }))} />
                  <div style={{ fontSize:11, color:'var(--text2)', marginTop:4 }}>Stripe Tax calculates the real amount; this rate only applies if that call fails.</div>
                </div>
              )}
            </div>
            <div style={{ marginBottom:14 }}>
              <label className="toggle-label" style={{ opacity: groupForm.taxEnabled ? 0.5 : 1 }}>
                <input type="checkbox" checked={groupForm.taxExempt} disabled={groupForm.taxEnabled}
                  onChange={e => setGroupForm(p => ({ ...p, taxExempt:e.target.checked, taxEnabled: e.target.checked ? false : p.taxEnabled }))} />
                <span>Tax exempt</span>
              </label>
              <div style={{ fontSize:11, color:'var(--text2)', marginTop:4, marginBottom:groupForm.taxExempt ? 10 : 0 }}>
                No tax collected for this group, regardless of billing model — overrides both the tax rate above (Invoice/Retail) and Stripe Tax (Direct/Wholesale). Certificates are per-location, so this is set per group rather than for the whole company.
              </div>
              {groupForm.taxExempt && groupForm.taxExemptExpiresAt && groupForm.taxExemptExpiresAt < new Date().toISOString().slice(0,10) && (
                <div style={{ fontSize:12, color:'var(--danger)', fontWeight:600, marginBottom:10, padding:'6px 10px', background:'rgba(220,53,69,0.08)', borderRadius:4 }}>
                  ⚠️ This certificate expired {groupForm.taxExemptExpiresAt} — tax is no longer being waived for this group even though the box is checked. Update the expiry date to renew.
                </div>
              )}
              {groupForm.taxExempt && (
                <div style={{ display:'flex', gap:16, alignItems:'flex-end', flexWrap:'wrap' }}>
                  <div className="field" style={{ width:200, marginBottom:0 }}>
                    <label className="label">Certificate ID</label>
                    <input className="input" value={groupForm.taxExemptCertificateId}
                      onChange={e => setGroupForm(p => ({ ...p, taxExemptCertificateId:e.target.value }))}
                      placeholder="State resale/exemption number" />
                  </div>
                  <div className="field" style={{ width:160, marginBottom:0 }}>
                    <label className="label">Expires *</label>
                    <input className="input" type="date" value={groupForm.taxExemptExpiresAt}
                      onChange={e => setGroupForm(p => ({ ...p, taxExemptExpiresAt:e.target.value }))} />
                    {!groupForm.taxExemptExpiresAt && (
                      <div style={{ fontSize:11, color:'var(--danger)', marginTop:4 }}>Required while tax exempt is checked</div>
                    )}
                  </div>
                  <div className="field" style={{ marginBottom:0 }}>
                    <label className="label">Certificate document</label>
                    <input type="file" accept=".pdf,image/*" onChange={handleCertUpload} />
                    {groupForm.taxExemptCertificateUrl && (
                      <a href={`${process.env.REACT_APP_API_URL || ''}${groupForm.taxExemptCertificateUrl}`} target="_blank" rel="noreferrer" style={{ fontSize:11, marginTop:4, display:'block' }}>
                        View uploaded certificate
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button className="btn btn-primary" disabled={!groupForm.companyId||!groupForm.name||(groupForm.taxExempt&&!groupForm.taxExemptExpiresAt)||saving} onClick={saveGroup}>
                {saving ? <Spinner size={16} /> : (editingId ? 'Save changes' : 'Create group')}
              </button>
              <button className="btn" onClick={() => { setTab('groups'); setEditingId(null); }}>Cancel</button>
            </div>
          </div>
        )}

        {/* ── PRODUCTS ── */}
        {/* ── PRODUCTS ── */}
        {tab === 'products' && assigningProduct === undefined && (
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
              <div>
                <div style={{ fontSize:18, fontWeight:700 }}>Products</div>
                <div style={{ fontSize:13, color:'var(--text2)' }}>Global product catalog — assign to companies from each product row</div>
              </div>
              <button className="btn btn-primary" onClick={() => { setEditingId(null); setProductForm(emptyProduct); setTab('product-form'); }}>+ Add product</button>
            </div>
            <div className="card" style={{ padding:0, overflow:'hidden' }}>
              {products.length === 0
                ? <div style={{ padding:40, textAlign:'center', color:'var(--text3)' }}>No products yet.</div>
                : (
                  <table className="tbl">
                    <thead><tr><th>Product</th><th>SKU</th><th>Wholesale</th><th>Cost</th><th>Companies</th><th>Status</th><th></th></tr></thead>
                    <tbody>
                      {products.map(p => (
                        <tr key={p.id}>
                          <td>
                            <div style={{ fontWeight:500 }}>{p.name}</div>
                            {p.category && <div style={{ fontSize:11, color:'var(--text3)' }}>{p.category}</div>}
                          </td>
                          <td style={{ fontSize:12 }}>{p.sku}</td>
                          <td style={{ color:'var(--text2)' }}>{fmtMoney(p.wholesalePrice)}</td>
                          <td style={{ color:'var(--text2)' }}>{fmtMoney(p.cost)}</td>
                          <td>
                            {!(p.assignedCompanies?.length)
                              ? <span style={{ fontSize:11, color:'var(--text3)' }}>None</span>
                              : <button className="btn btn-sm" style={{ fontSize:11 }}
                                  onClick={() => setCompaniesModal(p)}>
                                  {p.assignedCompanies.length} {p.assignedCompanies.length === 1 ? 'company' : 'companies'}
                                </button>
                            }
                          </td>
                          <td><span className={`badge ${p.active?'badge-fulfilled':'badge-cancelled'}`}>{p.active?'Active':'Inactive'}</span></td>
                          <td>
                            <div style={{ display:'flex', gap:6 }}>
                              <button className="btn btn-sm" onClick={() => editProduct(p)}>Edit</button>
                              <button className="btn btn-primary btn-sm" onClick={() => {
                                const available = companies.filter(c => !(p.assignedCompanies||[]).some(ac => ac.id === c.id));
                                if (!available.length) { alert('This product is already assigned to all companies.'); return; }
                                setAssigningProduct(p); setCpForm(emptyCpForm); setAssignReturnTo('products');
                              }}>Assign</button>
                              <button className="btn btn-sm" onClick={async () => { await updateProduct(p.id, { active: !p.active }); await loadAll(); }}>{p.active?'Disable':'Enable'}</button>
                              <button className="btn btn-danger btn-sm" onClick={async () => { if(window.confirm('Delete product and all company assignments?')) { await deleteProduct(p.id); await loadAll(); } }}>Delete</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              }
            </div>
          </div>
        )}

        {/* ── PRODUCT ASSIGNMENT SCREEN ── */}
        {tab === 'products' && assigningProduct !== undefined && (
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
              <button className="btn" onClick={() => { setAssigningProduct(undefined); setCpForm(emptyCpForm); setTab(assignReturnTo); }}>← Back to {assignReturnTo === 'company-form' ? 'Company' : 'Products'}</button>
              <div>
                {assigningProduct
                  ? <><div style={{ fontSize:18, fontWeight:700 }}>Assign: {assigningProduct.name}</div>
                      <div style={{ fontSize:12, color:'var(--text2)' }}>SKU: {assigningProduct.sku} · Wholesale: {fmtMoney(assigningProduct.wholesalePrice)}</div></>
                  : <div style={{ fontSize:18, fontWeight:700 }}>Assign Product to Company</div>
                }
              </div>
            </div>

            <div className="card" style={{ maxWidth:500 }}>
              <div style={{ fontWeight:600, marginBottom:14 }}>Assignment Details</div>
              {!cpForm.id && (
                <div className="field"><label className="label">Product *</label>
                  <select className="select" value={assigningProduct?.id || ''}
                    onChange={e => {
                      const p = products.find(p => p.id === parseInt(e.target.value));
                      setAssigningProduct(p || null);
                      setCpForm(prev => ({ ...prev, productId: e.target.value, companyId: prev.companyId }));
                    }}>
                    <option value="">Select product</option>
                    {products
                      .filter(p => cpForm.companyId
                        ? !(p.assignedCompanies||[]).some(c => c.id === parseInt(cpForm.companyId))
                        : true
                      )
                      .map(p => <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>)
                    }
                  </select>
                  {cpForm.companyId && <div style={{ fontSize:11, color:'var(--text3)', marginTop:4 }}>
                    Showing unassigned products only
                  </div>}
                </div>
              )}
              <div style={{ fontWeight:600, marginBottom:14 }}>Assign to Company</div>
              {!cpForm.id ? (
                <div className="field"><label className="label">Company *</label>
                  <select className="select" value={cpForm.companyId} onChange={e => setCpForm(p => ({ ...p, companyId: e.target.value }))}>
                    <option value="">Select company</option>
                    {companies
                      .filter(c => !(assigningProduct?.assignedCompanies || []).some(ac => ac.id === c.id))
                      .map(c => <option key={c.id} value={c.id}>{c.name}</option>)
                    }
                  </select>
                  {assigningProduct?.assignedCompanies?.length > 0 && (
                    <div style={{ fontSize:11, color:'var(--text3)', marginTop:4 }}>
                      {assigningProduct.assignedCompanies.length} {assigningProduct.assignedCompanies.length === 1 ? 'company' : 'companies'} already assigned and excluded
                    </div>
                  )}
                </div>
              ) : (
                <div className="field"><label className="label">Company</label>
                  <div style={{ padding:'8px 0', fontWeight:600 }}>{companies.find(c => c.id === parseInt(cpForm.companyId))?.name || '—'}</div>
                  <div style={{ fontSize:11, color:'var(--text3)' }}>Editing an existing assignment — company can't be changed here, only the price/image/order below. Remove and re-assign if you need to move this to a different company.</div>
                </div>
              )}
              <div className="grid2">
                <div className="field"><label className="label">Retail Price ($) *</label>
                  <input className="input" type="number" step="0.01" value={cpForm.retailPrice}
                    onChange={e => setCpForm(p => ({ ...p, retailPrice: e.target.value }))} />
                </div>
                <div className="field"><label className="label">Sort Order</label>
                  <input className="input" type="number" value={cpForm.sortOrder || 0}
                    onChange={e => setCpForm(p => ({ ...p, sortOrder: parseInt(e.target.value)||0 }))} />
                </div>
              </div>
              <div className="field">
                <label className="label">Product Image (company-specific)</label>
                {cpForm.imageUrl && <img src={cpForm.imageUrl} alt="" style={{ width:60, height:60, objectFit:'contain', borderRadius:4, display:'block', marginBottom:6 }} />}
                <input type="file" accept="image/*" key={cpForm.id || 'new'}
                  onChange={async (e) => {
                    const file = e.target.files[0]; if (!file) return;
                    const fd = new FormData(); fd.append('image', file);
                    try { const { data } = await uploadProductImage(fd); setCpForm(p => ({ ...p, imageUrl: data.imageUrl })); }
                    catch (err) { alert('Image upload failed: ' + (err.response?.data?.error || err.message)); }
                    e.target.value = '';
                  }} />
                {cpForm.imageUrl && <div style={{ fontSize:11, color:'var(--success)', marginTop:4 }}>✅ Image ready</div>}
              </div>
              <div style={{ display:'flex', gap:8, marginTop:4, alignItems:'center' }}>
                <button className="btn btn-primary"
                  disabled={!cpForm.companyId || !cpForm.retailPrice || saving}
                  onClick={async () => {
                    setSaving(true);
                    try {
                      const apiModule = await import('../api');
                      const companyName = companies.find(c => c.id === parseInt(cpForm.companyId))?.name || '';
                      const payload = { companyId: cpForm.companyId, productId: assigningProduct.id, retailPrice: cpForm.retailPrice, imageUrl: cpForm.imageUrl || '', sortOrder: cpForm.sortOrder || 0 };
                      if (cpForm.id) await apiModule.updateCompanyProduct(cpForm.id, payload);
                      else           await apiModule.createCompanyProduct(payload);
                      setCpForm(emptyCpForm);
                      await loadAll();
                      const updatedProducts = await getAdminProducts();
                      const updated = updatedProducts.data.products.find(p => p.id === assigningProduct.id);
                      if (updated) setAssigningProduct(updated);
                      toast(`✅ ${assigningProduct.name} assigned to ${companyName}`, 'success');
                    } catch (err) { alert(err.response?.data?.error || 'Error'); }
                    finally { setSaving(false); }
                  }}>
                  {saving ? <Spinner size={14} /> : 'Assign Product'}
                </button>
                <button className="btn" onClick={() => { setAssigningProduct(undefined); setCpForm(emptyCpForm); setTab(assignReturnTo); }}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* ── PRODUCT FORM ── */}
        {tab === 'product-form' && (
          <div style={{ maxWidth:600 }}>
            <div className="card">
              <div style={{ fontWeight:600, marginBottom:16 }}>{editingId ? 'Edit product' : 'Add product'}</div>
              <div className="grid2">
                <div className="field"><label className="label">Product name *</label><input className="input" value={productForm.name} onChange={e => setProductForm(p => ({ ...p, name:e.target.value }))} /></div>
                <div className="field"><label className="label">SKU *</label><input className="input" value={productForm.sku} onChange={e => setProductForm(p => ({ ...p, sku:e.target.value }))} /></div>
              </div>
              <div className="field"><label className="label">Category</label><input className="input" value={productForm.category} onChange={e => setProductForm(p => ({ ...p, category:e.target.value }))} /></div>
              <div className="field"><label className="label">Description</label><textarea className="textarea" value={productForm.description} onChange={e => setProductForm(p => ({ ...p, description:e.target.value }))} style={{ minHeight:60 }} /></div>
              <div className="field"><label className="label">Invoice description</label><input className="input" maxLength={60} value={productForm.invoiceDescription} onChange={e => setProductForm(p => ({ ...p, invoiceDescription:e.target.value }))} placeholder="Short name for invoices — blank uses the product name" /></div>
              <div className="grid2">
                <div className="field"><label className="label">Wholesale price ($)</label><input className="input" type="number" step="0.01" value={productForm.wholesalePrice} onChange={e => setProductForm(p => ({ ...p, wholesalePrice:e.target.value }))} /></div>
                <div className="field"><label className="label">Cost ($)</label><input className="input" type="number" step="0.01" value={productForm.cost} onChange={e => setProductForm(p => ({ ...p, cost:e.target.value }))} /></div>
              </div>
              <div className="grid2">
                <div className="field"><label className="label">Sort order</label><input className="input" type="number" value={productForm.sortOrder} onChange={e => setProductForm(p => ({ ...p, sortOrder:parseInt(e.target.value)||0 }))} /></div>
                <div className="field" style={{ display:'flex', alignItems:'flex-end', paddingBottom:4 }}>
                  <label className="toggle-label">
                    <input type="checkbox" checked={productForm.active} onChange={e => setProductForm(p => ({ ...p, active:e.target.checked }))} />
                    <span>Active</span>
                  </label>
                </div>
              </div>
              <div style={{ display:'flex', gap:10, marginTop:8 }}>
                <button className="btn btn-primary" disabled={!productForm.name||!productForm.sku||saving} onClick={saveProduct}>
                  {saving ? <Spinner size={16} /> : (editingId ? 'Save changes' : 'Create product')}
                </button>
                <button className="btn" onClick={() => { setTab('products'); setEditingId(null); }}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* ── COMPANIES MODAL ── */}
        {companiesModal && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}
            onClick={() => setCompaniesModal(null)}>
            <div className="card" style={{ width:440, padding:24, maxHeight:'70vh', display:'flex', flexDirection:'column' }} onClick={e => e.stopPropagation()}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:16 }}>{companiesModal.name}</div>
                  <div style={{ fontSize:12, color:'var(--text2)' }}>Assigned to {companiesModal.assignedCompanies?.length} {companiesModal.assignedCompanies?.length === 1 ? 'company' : 'companies'}</div>
                </div>
                <button className="btn btn-sm" onClick={() => setCompaniesModal(null)}>✕</button>
              </div>
              <div style={{ overflowY:'auto', flex:1 }}>
                <table className="tbl">
                  <thead><tr><th>Company</th><th>Retail Price</th><th>Image</th><th></th></tr></thead>
                  <tbody>
                    {(companiesModal.assignedCompanies || []).map(c => (
                      <tr key={c.id}>
                        <td style={{ fontWeight:500 }}>{c.name}</td>
                        <td>{fmtMoney(c.CompanyProduct?.retailPrice)}</td>
                        <td>{c.CompanyProduct?.imageUrl ? <img src={c.CompanyProduct.imageUrl} alt="" style={{ width:30, height:30, objectFit:'contain' }} /> : '—'}</td>
                        <td>
                          <div style={{ display:'flex', gap:6 }}>
                            <button className="btn btn-sm" onClick={() => {
                              setAssigningProduct(companiesModal);
                              setCpForm({
                                id: c.CompanyProduct?.id, companyId: c.id, productId: companiesModal.id,
                                retailPrice: c.CompanyProduct?.retailPrice || '', imageUrl: c.CompanyProduct?.imageUrl || '',
                                sortOrder: c.CompanyProduct?.sortOrder || 0,
                              });
                              setAssignReturnTo('products');
                              setCompaniesModal(null);
                            }}>Edit</button>
                            <button className="btn btn-danger btn-sm" onClick={async () => {
                              if (!window.confirm(`Remove ${companiesModal.name} from ${c.name}?`)) return;
                              try {
                                if (!c.CompanyProduct?.id) throw new Error(`CompanyProduct ID not found for ${c.name}`);
                                await import('../api').then(a => a.deleteCompanyProduct(c.CompanyProduct.id));
                                await loadAll();
                                setCompaniesModal(p => ({ ...p, assignedCompanies: p.assignedCompanies.filter(ac => ac.id !== c.id) }));
                                toast(`Removed from ${c.name}`, 'success');
                              } catch(err) { toast(`Failed to remove: ${err.response?.data?.error || err.message}`, 'error'); }
                            }}>Remove</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop:16, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <button className="btn btn-primary btn-sm" onClick={() => { setAssigningProduct(companiesModal); setCpForm(emptyCpForm); setAssignReturnTo('products'); setCompaniesModal(null); }}>Manage assignments</button>
                <button className="btn btn-sm" onClick={() => setCompaniesModal(null)}>Close</button>
              </div>
            </div>
          </div>
        )}

                {/* ── ORDERS ── */}
        {tab === 'orders' && (
          <div>
            {/* Filters */}
            <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
              <input className="input" style={{ flex:1, minWidth:200 }} placeholder="Search order #, customer…" value={orderSearch} onChange={e => setOrderSearch(e.target.value)} />
              <StatusMultiSelect
                options={ALL_STATUSES.map(s => ({ value: s.value, label: s.label }))}
                selected={orderStatus} onChange={setOrderStatus}
                placeholder="All statuses" width={180} />
              <select className="select" style={{ width:160 }} value={orderCompany} onChange={e => { setOrderCompany(e.target.value); setOrderGroup(''); }}>
                <option value="">All companies</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <select className="select" style={{ width:160 }} value={orderGroup} onChange={e => setOrderGroup(e.target.value)}>
                <option value="">All groups</option>
                {(orderCompany ? groups.filter(g => g.companyId === parseInt(orderCompany)) : groups).map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
              <select className="select" style={{ width:140 }} value={orderSort} onChange={e => setOrderSort(e.target.value)}>
                <option value="createdAt">Sort by date</option>
                <option value="total">Sort by total</option>
                <option value="customerLastName">Sort by name</option>
                <option value="status">Sort by status</option>
              </select>
              <button className="btn btn-sm" onClick={() => setOrderSortDir(d => d==='DESC'?'ASC':'DESC')}>
                {orderSortDir === 'DESC' ? '↓ Desc' : '↑ Asc'}
              </button>
              <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, cursor:'pointer' }}>
                <input type="checkbox" checked={orderRushOnly} onChange={e => setOrderRushOnly(e.target.checked)} />
                🔥 Rush only
              </label>
              <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, cursor:'pointer' }}>
                <input type="checkbox" checked={hideShippedCancelled} onChange={e => setHideShippedCancelled(e.target.checked)} disabled={orderStatus.length > 0} />
                Hide shipped &amp; cancelled
              </label>
            </div>

            <div className="card" style={{ padding:0, overflow:'hidden' }}>
              {orders.length === 0
                ? <div style={{ padding:40, textAlign:'center', color:'var(--text3)' }}>No orders found</div>
                : (
                  <table className="tbl">
                    <thead><tr><th>Order</th><th>Customer</th><th>Company / Group</th><th>Room</th><th>Date</th><th style={{ textAlign:'right' }}>Total</th><th>Status</th><th>Code</th><th></th></tr></thead>
                    <tbody>
                      {orders.map(o => (
                        <tr key={o.id} onClick={() => openOrder(o.id)} style={{ cursor:'pointer' }}>
                          <td style={{ fontWeight:500 }}>{o.orderNumber} {o.isRush && <RushBadge />}</td>
                          <td>{o.customerFirstName} {o.customerLastName}</td>
                          <td>
                            <div style={{ fontSize:12 }}>{o.companyName}</div>
                            <div style={{ fontSize:11, color:'var(--text3)' }}>{o.groupName}</div>
                          </td>
                          <td style={{ fontSize:12 }}>{o.roomNumber||'—'}</td>
                          <td style={{ fontSize:12, color:'var(--text2)' }}>{fmtDate(o.createdAt)}</td>
                          <td style={{ textAlign:'right', fontWeight:600 }}>{fmtMoney(o.total)}</td>
                          <td><Badge status={o.status} /></td>
                          <td>
                            <span style={{ fontSize:11, fontWeight:700, color:'var(--text2)', background:'var(--bg)', padding:'2px 7px', borderRadius:4, border:'1px solid var(--border)', letterSpacing:'.03em' }}>
                              {ALL_STATUSES.find(s => s.value === o.status)?.short || ''}
                            </span>
                          </td>
                          <td onClick={e => e.stopPropagation()}>
                            <select className="select" style={{ fontSize:11, padding:'3px 6px', width:150 }}
                              value={o.status}
                              onChange={async (e) => {
                                const newStatus = e.target.value;
                                if (newStatus === 'cancelled') {
                                  setCancelModalOrder(o);
                                  return;
                                }
                                const label = ALL_STATUSES.find(s => s.value === newStatus)?.label || newStatus;
                                const confirmed = window.confirm(
                                  `Change order status to "${label}"?\n\nAll line items will be reset to the equivalent item status.`
                                );
                                if (!confirmed) return;
                                // Phase 12f — reject on receipt needs a note (sent to the PSP)
                                let note;
                                if (newStatus === 'received_rejected') {
                                  note = window.prompt('Reason for rejecting on receipt — this note is sent to the PSP:');
                                  if (!note || !note.trim()) return;
                                }
                                const effective = newStatus === 'received_accepted' ? 'ready_to_ship' : newStatus; // accept → Ready to Ship
                                try {
                                  await updateOrderStatus(o.id, newStatus, note);
                                  setOrders(prev => prev.map(ord => ord.id === o.id ? { ...ord, status: effective } : ord));
                                } catch (err) { alert(err.response?.data?.error || 'Failed to update status: ' + err.message); }
                              }}>
                              {ALL_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              }
            </div>
          </div>
        )}

        {/* ── ORDER DETAIL ── */}
        {tab === 'order-detail' && (
          <div>
            <button className="btn btn-sm" style={{ marginBottom:16 }} onClick={() => { setTab('orders'); setSelOrder(null); }}>← Back to orders</button>
            {loadingOrder ? <Loading label="Loading order…" /> : selOrder ? (
              <div>
                <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:4 }}>
                  <div className="page-title">{selOrder.orderNumber}</div>
                  <Badge status={selOrder.status} />
                  {selOrder.isRush && <RushBadge />}
                  <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, cursor:'pointer', marginLeft:8 }}>
                    <input type="checkbox" checked={!!selOrder.isRush}
                      onChange={async (e) => {
                        const isRush = e.target.checked;
                        try {
                          const { updateOrderRush } = await import('../api');
                          await updateOrderRush(selOrder.id, isRush);
                          setSelOrder(prev => ({ ...prev, isRush }));
                          toast(isRush ? 'Marked as Rush' : 'Rush removed', 'success');
                        } catch (err) { toast('Failed to update rush flag: ' + (err.response?.data?.error || err.message), 'error'); }
                      }} />
                    🔥 Rush
                  </label>
                </div>
                <div style={{ fontSize:13, color:'var(--text2)', marginBottom:20 }}>
                  {selOrder.companyName} · {selOrder.groupName} · {fmtDate(selOrder.createdAt)}
                </div>

                <div style={{ display:'grid', gridTemplateColumns:'1fr 320px', gap:16 }}>
                  <div>
                    {/* Items with photo thumbnails */}
                    <div className="card" style={{ marginBottom:16 }}>
                      <div style={{ fontWeight:600, marginBottom:14 }}>Order items</div>
                      {selOrder.items?.map((item, idx) => (
                        <div key={item.id} style={{ border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:14, marginBottom:12 }}>
                          <div style={{ display:'flex', gap:10, marginBottom:10 }}>
                            {item.photoOneDrivePath && item.photoUrl !== '#demo'
                              ? <LazyImage src={`/api/uploads/image?path=${encodeURIComponent(item.photoOneDrivePath)}`} alt="Front"
                                  onClick={() => setOrderDetailLightbox(`/api/uploads/image?path=${encodeURIComponent(item.photoOneDrivePath)}`)}
                                  style={{ width:48, height:48, objectFit:'cover', borderRadius:4, background:'var(--bg)', flexShrink:0, cursor:'zoom-in', border:'1px solid var(--border)' }} />
                              : item.productImageUrl && <LazyImage src={item.productImageUrl} alt="" style={{ width:48, height:48, objectFit:'contain', borderRadius:4, background:'var(--bg)', flexShrink:0 }} />}
                            <div style={{ flex:1 }}>
                              <div style={{ fontWeight:500 }}>Line {idx+1}: {item.productName} × {item.quantity}</div>
                              <div style={{ fontSize:11, color:'var(--text3)' }}>
                                SKU: {item.sku} · Ref: {item.referenceNumber||'—'}
                                {item.referenceNumber2 && <span> · Back: {item.referenceNumber2}</span>}
                                <span> · Room: {selOrder.roomNumber||'—'}</span>
                              </div>
                              {item.lineItemDescription && <div style={{ fontSize:11, color:'var(--text2)', marginTop:2 }}>ID: {item.lineItemDescription}</div>}
                              <div style={{ fontSize:12, fontWeight:600, marginTop:4 }}>
                                {fmtMoney(item.lineTotal)}
                                {parseFloat(item.volumeDiscountAmount) > 0 && (
                                  <span style={{ fontWeight:400, color:'var(--success)', marginLeft:6 }}>
                                    (-{fmtMoney(item.volumeDiscountAmount)} volume discount, {parseFloat(item.volumeDiscountPercent)}%)
                                  </span>
                                )}
                              </div>
                            </div>
                            <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:6 }}>
                              <Badge status={item.photoStatus==='uploaded'?'uploaded':'pending'} />
                              <select className="select" style={{ fontSize:11, padding:'3px 6px', width:140 }}
                                value={item.itemStatus || 'awaiting_photo'}
                                onChange={async (e) => {
                                  const newStatus = e.target.value;
                                  // Phase 12f — reject on receipt needs a note (sent to the PSP)
                                  let note;
                                  if (newStatus === 'received_rejected') {
                                    note = window.prompt('Reason for rejecting on receipt — this note is sent to the PSP:');
                                    if (!note || !note.trim()) return;
                                  }
                                  try {
                                    const { updateItemStatus, getAdminOrder } = await import('../api');
                                    const { data } = await updateItemStatus(selOrder.id, item.id, newStatus, note);
                                    // Refresh full order to get updated order status
                                    const { data: orderData } = await getAdminOrder(selOrder.id);
                                    setSelOrder(orderData.order);
                                  } catch (err) { alert('Failed to update item status: ' + (err.response?.data?.error || err.message)); }
                                }}>
                                <option value="awaiting_photo">Awaiting Photo</option>
                                <option value="in_process">In Process</option>
                                <option value="ready_to_model">Ready to Model</option>
                                <option value="sculpting">Sculpting</option>
                                <option value="ready_to_print">Ready to Print</option>
                                <option value="printing">Printing</option>
                                <option value="finishing">Finishing</option>
                                <option value="qc">QC</option>
                                <option value="packing">Packing</option>
                                <option value="shipped_to_solidiphy">Shipped to Solidiphy</option>
                                <option value="received_accepted">Received &amp; Accepted</option>
                                <option value="received_rejected">Received &amp; Rejected</option>
                                <option value="ready_to_ship">Ready to Ship</option>
                                <option value="shipped_to_customer">Shipped to Customer</option>
                              </select>
                            </div>
                          </div>
                          {/* Photo section with thumbnail */}
                          {item.photoUrl && item.photoUrl !== '#demo' ? (
                            <div style={{ background:'var(--success-bg)', border:'1px solid var(--success-bd)', borderRadius:'var(--radius)', padding:10 }}>
                              <div style={{ fontSize:11, fontWeight:600, marginBottom:4, color:'var(--success)' }}>✅ Front photo uploaded</div>
                              <div style={{ fontSize:10, color:'var(--text3)', marginBottom:8, wordBreak:'break-all' }}>{item.photoOneDrivePath}</div>
                              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                                <a href={`${process.env.REACT_APP_API_URL || ''}/api/uploads/image?path=${encodeURIComponent(item.photoOneDrivePath)}&t=${Date.now()}`} target="_blank" rel="noreferrer" className="btn btn-sm">View front ↗</a>
                                <label className="btn btn-sm" style={{ cursor:'pointer' }}>
                                  Replace
                                  <input type="file" accept="image/*" style={{ display:'none' }}
                                    onChange={e => e.target.files[0] && handleReplacePhoto(item, e.target.files[0])} />
                                </label>
                              </div>
                              {item.photoUrl2 && (
                                <div style={{ marginTop:8, paddingTop:8, borderTop:'1px solid var(--success-bd)' }}>
                                  <div style={{ fontSize:11, fontWeight:600, marginBottom:4, color:'var(--success)' }}>✅ Back photo uploaded</div>
                                  <div style={{ fontSize:10, color:'var(--text3)', marginBottom:6, wordBreak:'break-all' }}>{item.photoOneDrivePath2}</div>
                                  <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                                    <a href={`${process.env.REACT_APP_API_URL || ''}/api/uploads/image?path=${encodeURIComponent(item.photoOneDrivePath2)}&t=${Date.now()}`} target="_blank" rel="noreferrer" className="btn btn-sm">View back ↗</a>
                                    <label className="btn btn-sm" style={{ cursor:'pointer' }}>
                                      Replace back
                                      <input type="file" accept="image/*" style={{ display:'none' }}
                                        onChange={e => e.target.files[0] && handleReplacePhoto(item, e.target.files[0], true)} />
                                    </label>
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div style={{ background:'var(--warning-bg)', border:'1px solid var(--warning-bd)', borderRadius:'var(--radius)', padding:10, fontSize:12, color:'var(--warning)' }}>
                              ⚠️ No photo uploaded yet
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Audit Trail */}
                    <AuditTrail orderId={selOrder.id} items={selOrder.items} />

                    {/* Editable order info */}
                    <div className="card" style={{ marginBottom:16 }}>
                      <div style={{ fontWeight:600, marginBottom:14 }}>Edit order info</div>
                      <div className="grid2">
                        <div className="field"><label className="label">First name</label>
                          <input className="input" defaultValue={selOrder.customerFirstName}
                            onChange={e => setSelOrder(p => ({ ...p, customerFirstName: e.target.value }))} />
                        </div>
                        <div className="field"><label className="label">Last name</label>
                          <input className="input" defaultValue={selOrder.customerLastName}
                            onChange={e => setSelOrder(p => ({ ...p, customerLastName: e.target.value }))} />
                        </div>
                      </div>
                      <div className="grid2">
                        <div className="field"><label className="label">Email</label>
                          <input className="input" defaultValue={selOrder.customerEmail}
                            onChange={e => setSelOrder(p => ({ ...p, customerEmail: e.target.value }))} />
                        </div>
                        <div className="field"><label className="label">Phone</label>
                          <input className="input" defaultValue={selOrder.customerPhone}
                            onChange={e => setSelOrder(p => ({ ...p, customerPhone: e.target.value }))} />
                        </div>
                      </div>
                      <div className="field"><label className="label">Room #</label>
                        <input className="input" defaultValue={selOrder.roomNumber}
                          onChange={e => setSelOrder(p => ({ ...p, roomNumber: e.target.value }))} />
                      </div>
                      <div className="field"><label className="label">Notes</label>
                        <textarea className="textarea" defaultValue={selOrder.photographerNotes}
                          onChange={e => setSelOrder(p => ({ ...p, photographerNotes: e.target.value }))} />
                      </div>
                    </div>

                    {/* Shipping */}
                    <div className="card">
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10 }}>
                        <div style={{ fontWeight:600 }}>Shipping address</div>
                        {true && (
                          <span style={{ fontSize:11, color:'var(--brand)', fontWeight:500 }}>✏️ Editable</span>
                        )}
                      </div>
                      {true ? (
                        <div>
                          <div className="grid2" style={{ marginBottom:8 }}>
                            <div className="field"><label className="label">First name</label><input className="input" value={selOrder.shippingFirstName||''} onChange={e => setSelOrder(p => ({ ...p, shippingFirstName: e.target.value }))} /></div>
                            <div className="field"><label className="label">Last name</label><input className="input" value={selOrder.shippingLastName||''} onChange={e => setSelOrder(p => ({ ...p, shippingLastName: e.target.value }))} /></div>
                          </div>
                          <div className="field"><label className="label">Address line 1</label><input className="input" value={selOrder.shippingAddress1||''} onChange={e => setSelOrder(p => ({ ...p, shippingAddress1: e.target.value }))} /></div>
                          <div className="field"><label className="label">Address line 2</label><input className="input" value={selOrder.shippingAddress2||''} onChange={e => setSelOrder(p => ({ ...p, shippingAddress2: e.target.value }))} /></div>
                          <div className="grid2">
                            <div className="field"><label className="label">City</label><input className="input" value={selOrder.shippingCity||''} onChange={e => setSelOrder(p => ({ ...p, shippingCity: e.target.value }))} /></div>
                            <div className="field"><label className="label">ZIP / Postal</label><input className="input" value={selOrder.shippingZip||''} onChange={e => setSelOrder(p => ({ ...p, shippingZip: e.target.value }))} /></div>
                          </div>
                          <div className="grid2">
                            <div className="field"><label className="label">State / Province</label><input className="input" value={selOrder.shippingProvince||''} onChange={e => setSelOrder(p => ({ ...p, shippingProvince: e.target.value }))} /></div>
                            <div className="field"><label className="label">Country</label>
                              <CountrySelect value={selOrder.shippingCountry || 'US'} onChange={code => setSelOrder(p => ({ ...p, shippingCountry: code }))} />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div style={{ fontSize:13, color:'var(--text2)', lineHeight:1.8 }}>
                          {selOrder.shippingFirstName} {selOrder.shippingLastName}<br />
                          {selOrder.shippingAddress1}{selOrder.shippingAddress2 ? `, ${selOrder.shippingAddress2}` : ''}<br />
                          {selOrder.shippingCity}, {selOrder.shippingProvince} {selOrder.shippingZip}<br />
                          {selOrder.shippingCountry}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Sidebar */}
                  <div>
                    <div className="card" style={{ marginBottom:14 }}>
                      <div style={{ fontWeight:600, marginBottom:10 }}>Update status</div>
                      <select className="select" value={selOrder.status}
                        onChange={async (e) => {
                          const newStatus = e.target.value;
                          if (newStatus === 'cancelled') {
                            setCancelModalOrder(selOrder);
                            return;
                          }
                          const label = ALL_STATUSES.find(s => s.value === newStatus)?.label || newStatus;
                          const confirmed = window.confirm(
                            `Change order status to "${label}"?\n\nAll line items in this order will be reset to the equivalent item status. This cannot be undone.`
                          );
                          if (!confirmed) return;
                          // Phase 12f — reject on receipt needs a note (sent to the PSP)
                          let note;
                          if (newStatus === 'received_rejected') {
                            note = window.prompt('Reason for rejecting on receipt — this note is sent to the PSP:');
                            if (!note || !note.trim()) return;
                          }
                          const effective = newStatus === 'received_accepted' ? 'ready_to_ship' : newStatus; // accept → Ready to Ship
                          try {
                            await updateOrderStatus(selOrder.id, newStatus, note);
                            setSelOrder(p => ({
                              ...p,
                              status: effective,
                              items: p.items?.map(i => ({ ...i, itemStatus: effective })),
                            }));
                            loadOrders();
                          } catch (err) { alert(err.response?.data?.error || 'Failed to update status: ' + err.message); }
                        }}>
                        {ALL_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </div>

                    <div className="card" style={{ marginBottom:14 }}>
                      <div style={{ fontWeight:600, marginBottom:8 }}>Order total</div>
                      <div className="sum-row"><span>Subtotal</span><span>{fmtMoney(selOrder.subtotal)}</span></div>
                      {(() => {
                        const volumeDiscountTotal = (selOrder.items || []).reduce((s, i) => s + parseFloat(i.volumeDiscountAmount || 0), 0);
                        const couponPercentDiscount = selOrder.couponType === 'percent_discount' ? parseFloat(selOrder.couponDiscount || 0) : 0;
                        return (
                          <>
                            {volumeDiscountTotal > 0 && (
                              <div className="sum-row" style={{ color:'var(--success)' }}>
                                <span>Volume Discount</span><span>-{fmtMoney(volumeDiscountTotal)}</span>
                              </div>
                            )}
                            {couponPercentDiscount > 0 && (
                              <div className="sum-row" style={{ color:'var(--success)' }}>
                                <span>Coupon Discount {selOrder.couponCode ? `(${selOrder.couponCode})` : ''}</span>
                                <span>-{fmtMoney(couponPercentDiscount)}</span>
                              </div>
                            )}
                          </>
                        );
                      })()}
                      <div className="sum-row">
                        <span>Shipping{selOrder.couponType === 'free_shipping' ? ` (waived — ${selOrder.couponCode})` : ''}</span>
                        <span>{fmtMoney(selOrder.shippingCost)}</span>
                      </div>
                      {parseFloat(selOrder.taxAmount) > 0 && (
                        <div className="sum-row">
                          <span>Tax{selOrder.taxFallbackUsed ? ' ⚠️' : ''}</span>
                          <span>{fmtMoney(selOrder.taxAmount)}</span>
                        </div>
                      )}
                      {selOrder.taxFallbackUsed && (
                        <div style={{ fontSize:11, color:'var(--danger)', marginTop:-4, marginBottom:8 }}>
                          Stripe Tax failed for this order — the group's flat rate was used as a fallback. Worth double-checking this amount.
                        </div>
                      )}
                      <hr className="divider" />
                      <div className="sum-total"><span>Total</span><span>{fmtMoney(selOrder.total)}</span></div>
                    </div>

                    {selOrder.billingModel === 'direct_wholesale' && (
                      <div className="card" style={{ marginBottom:14 }}>
                        <div style={{ fontWeight:600, marginBottom:8 }}>Payment</div>
                        <div style={{ fontSize:13, color:'var(--text2)', marginBottom:10, display:'flex', alignItems:'center', gap:8 }}>
                          Status: <PaymentBadge status={selOrder.paymentStatus || 'not_applicable'} />
                          {parseFloat(selOrder.payment?.refundedAmount) > 0 && (
                            <span>· Refunded: <strong>{fmtMoney(selOrder.payment.refundedAmount)}</strong> of {fmtMoney(selOrder.payment.capturedAmount || selOrder.payment.amount)}</span>
                          )}
                        </div>
                        {['captured', 'partially_refunded'].includes(selOrder.paymentStatus) && (
                          <button className="btn btn-danger btn-full" onClick={async () => {
                            const alreadyRefunded = parseFloat(selOrder.payment?.refundedAmount || 0);
                            const capturedTotal   = parseFloat(selOrder.payment?.capturedAmount || selOrder.payment?.amount || selOrder.total);
                            const remaining       = capturedTotal - alreadyRefunded;
                            const amountStr = window.prompt(`Refund amount in dollars (leave blank for the remaining ${fmtMoney(remaining)}):`);
                            if (amountStr === null) return; // cancelled
                            const trimmed = amountStr.trim();
                            const amount = trimmed ? parseFloat(trimmed) : undefined;
                            if (trimmed && (isNaN(amount) || amount <= 0)) { alert('Invalid amount'); return; }
                            const reason = window.prompt('Reason for refund (optional, shown in the audit log):') || '';
                            try {
                              const { data } = await refundOrder(selOrder.id, amount, reason);
                              toast(`Refunded $${data.refundedAmount.toFixed(2)}`, 'success');
                              setSelOrder(p => ({
                                ...p,
                                paymentStatus: data.fullyRefunded ? 'refunded' : 'partially_refunded',
                                payment: { ...p.payment, refundedAmount: (parseFloat(p.payment?.refundedAmount || 0) + data.refundedAmount) },
                              }));
                              loadOrders();
                            } catch (err) { alert('Refund failed: ' + (err.response?.data?.error || err.message)); }
                          }}>Issue Refund</button>
                        )}
                        {selOrder.paymentStatus === 'refunded' && (
                          <div style={{ fontSize:12, color:'var(--text3)' }}>Fully refunded — no further action available.</div>
                        )}
                      </div>
                    )}

                    <div className="card">
                      <div style={{ fontWeight:600, marginBottom:8 }}>Customer</div>
                      <div style={{ fontSize:13, color:'var(--text2)', lineHeight:1.8 }}>
                        {selOrder.customerFirstName} {selOrder.customerLastName}<br />
                        {selOrder.customerEmail || '—'}<br />
                        {selOrder.customerPhone || '—'}
                      </div>
                    </div>

                    <div className="card" style={{ marginTop:14 }}>
                      <button className="btn btn-primary btn-full" onClick={async () => {
                        try {
                          await import('../api').then(a => a.default.put(`/admin/orders/${selOrder.id}`, {
                            customerFirstName:  selOrder.customerFirstName,
                            customerLastName:   selOrder.customerLastName,
                            customerEmail:      selOrder.customerEmail,
                            customerPhone:      selOrder.customerPhone,
                            roomNumber:         selOrder.roomNumber,
                            photographerNotes:  selOrder.photographerNotes,
                            shippingFirstName:  selOrder.shippingFirstName,
                            shippingLastName:   selOrder.shippingLastName,
                            shippingAddress1:   selOrder.shippingAddress1,
                            shippingAddress2:   selOrder.shippingAddress2,
                            shippingCity:       selOrder.shippingCity,
                            shippingProvince:   selOrder.shippingProvince,
                            shippingZip:        selOrder.shippingZip,
                            shippingCountry:    selOrder.shippingCountry,
                          }));
                          alert('Order updated!');
                          await loadOrders();
                        } catch (err) { alert('Failed to save: ' + err.message); }
                      }}>Save changes</button>
                    </div>

                    {/* Cancel reason + Reinstate (only shown when cancelled) */}
                    {selOrder.status === 'cancelled' && (
                      <div className="card" style={{ marginTop:14 }}>
                        {selOrder.cancelReason && (
                          <div style={{ fontSize:12, color:'var(--danger)', marginBottom:10, background:'var(--danger-bg)', padding:'8px 10px', borderRadius:6 }}>
                            <strong>Cancel reason:</strong> {selOrder.cancelReason}
                          </div>
                        )}
                        <button className="btn btn-full" style={{ color:'var(--success)', borderColor:'var(--success)' }}
                          onClick={async () => {
                            if (!window.confirm('Reinstate this order? It will revert to its previous status.')) return;
                            try {
                              await uncancelOrder(selOrder.id);
                              const revertStatus = selOrder.statusBeforeCancel || 'awaiting_photo';
                              setSelOrder(p => ({ ...p, status: revertStatus, cancelReason: '', statusBeforeCancel: '' }));
                              setOrders(prev => prev.map(o => o.id === selOrder.id ? { ...o, status: revertStatus } : o));
                            } catch (err) { alert(err.response?.data?.error || 'Failed'); }
                          }}>Reinstate Order</button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : <p>Order not found</p>}
            {orderDetailLightbox && (
              <div onClick={() => setOrderDetailLightbox(null)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000, cursor:'zoom-out' }}>
                <img src={orderDetailLightbox} alt="Preview" style={{ maxWidth:'90vw', maxHeight:'90vh', objectFit:'contain', borderRadius:8 }} onClick={e => e.stopPropagation()} />
                <button onClick={() => setOrderDetailLightbox(null)} style={{ position:'absolute', top:20, right:24, background:'none', border:'none', color:'#fff', fontSize:28, cursor:'pointer' }}>✕</button>
              </div>
            )}
          </div>
        )}

        {(tab === 'admin-users' || tab === 'line-items' || tab === 'coupons' || tab === 'psp-users' || tab === 'psp-companies' || tab === 'api-integrations' || tab === 'settings' || tab === 'packing-slips' || tab === 'meshy-tasks' || tab === 'volume-discounts' || tab === 'settlements' || tab === 'invoices') && <>
          {tab === 'admin-users'   && <AdminUsersTab />}
          {tab === 'psp-users'     && <PSPUsersTab toast={toast} />}
          {tab === 'psp-companies' && <PSPCompaniesTab toast={toast} />}
          {tab === 'api-integrations' && <APIIntegrationsTab toast={toast} />}
          {tab === 'line-items'    && <LineItemsTab companies={companies} groups={groups} onOrderStatusChange={loadOrders} />}
          {tab === 'coupons'       && <CouponsTab companies={companies} coupons={coupons} onRefresh={loadAll} toast={toast} />}
          {tab === 'settings'      && <SettingsTab toast={toast} />}
          {tab === 'packing-slips' && <PackingSlipsTab toast={toast} />}
          {tab === 'meshy-tasks'   && <MeshyTasksTab toast={toast} />}
          {tab === 'volume-discounts' && <VolumePromotionsTab companies={companies} products={products} promotions={volumePromotions} onRefresh={loadAll} toast={toast} />}
          {tab === 'settlements' && <SettlementsTab companies={companies} toast={toast} />}
          {tab === 'invoices' && <InvoicesTab companies={companies} toast={toast} />}
        </>}
        {tab === 'operations' && (
          <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>
            <OperationsTab />
          </div>
        )}

        {/* ── CANCEL MODAL ── */}
        {cancelModalOrder && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}>
            <div className="card" style={{ width:440, padding:28 }}>
              <div style={{ fontSize:17, fontWeight:700, marginBottom:8 }}>Cancel Order</div>
              <div style={{ fontSize:13, color:'var(--text2)', marginBottom:16 }}>
                Order <strong>{cancelModalOrder.orderNumber}</strong> — {cancelModalOrder.customerFirstName} {cancelModalOrder.customerLastName}
              </div>
              <div className="field">
                <label className="label">Cancel reason *</label>
                <textarea className="textarea" placeholder="Enter reason for cancellation…" value={cancelReason}
                  onChange={e => setCancelReason(e.target.value)} style={{ minHeight:80 }} autoFocus />
              </div>
              <div style={{ display:'flex', gap:10, marginTop:16 }}>
                <button className="btn btn-primary" style={{ background:'var(--danger)', borderColor:'var(--danger)' }}
                  disabled={!cancelReason.trim() || saving}
                  onClick={async () => {
                    setSaving(true);
                    try {
                      await cancelOrderAdmin(cancelModalOrder.id, cancelReason);
                      setOrders(prev => prev.map(o => o.id === cancelModalOrder.id ? { ...o, status: 'cancelled' } : o));
                      if (selOrder?.id === cancelModalOrder.id) {
                        setSelOrder(p => ({ ...p, status: 'cancelled', cancelReason }));
                      }
                      setCancelModalOrder(null); setCancelReason('');
                    } catch (err) { alert(err.response?.data?.error || 'Failed to cancel'); }
                    finally { setSaving(false); }
                  }}>
                  {saving ? <Spinner size={14} /> : 'Cancel Order'}
                </button>
                <button className="btn" onClick={() => { setCancelModalOrder(null); setCancelReason(''); }}>Back</button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}